import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useParams, useNavigate } from "react-router-dom";
import useWorkspaceLifecycle from "../hooks/useWorkspaceLifecycle";
import {
  WorkspaceLockOverlay,
  WorkspaceCloseBanner,
} from "./components/WorkspaceLifecycleUI";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Copy,
  Download,
  GripVertical,
  Image as ImageIcon,
  LayoutTemplate,
  Plus,
  Trash2,
} from "lucide-react";
import Main from "./Main";
import { makeCompanyNav } from "./nav";
import { API_BASE, getCsrfToken } from "../services/server";
import { applyPresentationToIMSections, createIMPresentationLayout, selectFinalIMSections } from "./imSectionSelection";
import "./css/imstudio.css";

const PRELOADED_LOGO = "/logo192.png";
const PAGE_WIDTH = 794;
// A4 height at PAGE_WIDTH px width — keeps preview-canvas page breaks
// aligned with the jsPDF render (which slices the captured image by A4).
const PAGE_HEIGHT_PX = Math.round(PAGE_WIDTH * (297 / 210));

async function imageUrlToPngDataUrl(src) {
  if (!src || typeof window === "undefined") return null;
  return new Promise((resolve) => {
    const image = new window.Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        if (!canvas.width || !canvas.height) return resolve(null);
        canvas.getContext("2d").drawImage(image, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

function toPdfRgb(hex, fallback = [28, 45, 80]) {
  const match = /^#?([0-9a-f]{6})$/i.exec(String(hex || ""));
  if (!match) return fallback;
  return [0, 2, 4].map((i) => parseInt(match[1].slice(i, i + 2), 16));
}

function profilePresentationView(configuration, selectedTemplate) {
  const config = configuration || {};
  const branding = config.branding || {};
  const layout = config.layout || {};
  return {
    ...(selectedTemplate || {}),
    layout: layout.template_layout || selectedTemplate?.layout || [],
    section_order: layout.section_order || [],
    theme: {
      ...(selectedTemplate?.theme || {}),
      primary_color: branding.primary_color,
      accent_color: branding.accent_color,
      text_color: branding.text_color,
      muted_color: branding.muted_color,
      surface_color: branding.background_color,
      font_heading: branding.font_heading,
      font_body: branding.font_body,
    },
  };
}

// Mid-section pagination. When a textSection's content exceeds one A4
// of vertical space, this helper injects visible page-break spacers at
// the boundaries so the broker sees "this section continues on a new
// page" instead of a single tall card.
//
// Algorithm:
//   1. After layout, measure each real child's offsetHeight. Spacers
//      we previously inserted are tagged ``data-page-spacer`` and
//      filtered out, so the computed cumulative height reflects the
//      natural flow and the recompute stays stable across re-renders.
//   2. Walk the children, cumulating height. When adding the next
//      child would cross the next page boundary, record a break *before*
//      that child and reset the cumulative counter to the new boundary.
//   3. Render the children with the spacer divs interleaved at the
//      recorded indices. Each spacer's height is sized so the next
//      child starts exactly at the next page's top.
//
// A minimum spacer height (``MIN_SPACER_PX``) ensures the SIDBRYTNING
// label stays visible even when the natural gap is tiny; the extra
// pixels push the next child slightly past the ideal boundary but keep
// the visual indicator readable.
function PaginatedChildren({ children, innerPageHeight, basePage = 1 }) {
  const containerRef = React.useRef(null);
  const [breaks, setBreaks] = React.useState([]);
  const MIN_SPACER_PX = 48;

  const measure = React.useCallback(() => {
    const container = containerRef.current;
    if (!container || !innerPageHeight || innerPageHeight <= 0) return;
    const kids = Array.from(container.children).filter(
      (el) => !(el instanceof HTMLElement && el.dataset.pageSpacer),
    );
    let cumulative = 0;
    let nextBoundary = innerPageHeight;
    let pageOffset = 0;
    const next = [];
    for (let i = 0; i < kids.length; i++) {
      const h = kids[i].offsetHeight;
      if (cumulative > 0 && cumulative + h > nextBoundary) {
        const spacerHeight = Math.max(
          MIN_SPACER_PX,
          nextBoundary - cumulative,
        );
        pageOffset += 1;
        next.push({ beforeIdx: i, spacerHeight, pageN: basePage + pageOffset });
        cumulative = nextBoundary + Math.max(0, spacerHeight - (nextBoundary - cumulative));
        nextBoundary += innerPageHeight;
      }
      cumulative += h;
    }
    setBreaks((prev) => {
      if (prev.length !== next.length) return next;
      for (let i = 0; i < prev.length; i++) {
        if (
          prev[i].beforeIdx !== next[i].beforeIdx ||
          prev[i].pageN !== next[i].pageN ||
          Math.abs(prev[i].spacerHeight - next[i].spacerHeight) > 1
        ) {
          return next;
        }
      }
      return prev;
    });
  }, [innerPageHeight, basePage]);

  React.useLayoutEffect(() => {
    measure();
  });

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => measure());
    Array.from(container.children).forEach((el) => {
      if (el instanceof HTMLElement && !el.dataset.pageSpacer) ro.observe(el);
    });
    return () => ro.disconnect();
  }, [measure, breaks.length]);

  const childArr = React.Children.toArray(children);
  const breakMap = new Map(breaks.map((b) => [b.beforeIdx, b]));
  const output = [];
  childArr.forEach((child, i) => {
    const brk = breakMap.get(i);
    if (brk) {
      output.push(
        <div
          key={`page-spacer-${i}`}
          data-page-spacer="true"
          data-pdf-hide
          aria-hidden="true"
          style={{
            height: brk.spacerHeight,
            position: "relative",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              width: "100%",
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.6,
              color: "#A8A29E",
              paddingBottom: 2,
            }}
          >
            <span style={{ flex: 1, height: 1, background: "#E7E5E4" }} />
            <span>Sida {brk.pageN}</span>
            <span style={{ flex: 1, height: 1, background: "#E7E5E4" }} />
          </div>
        </div>,
      );
    }
    output.push(child);
  });

  return <div ref={containerRef}>{output}</div>;
}

// Word-style page-break markers: grey horizontal strips with "Sida X"
// label at each A4-height boundary. Gives the broker a visual cue that
// the document IS paginated even though content scrolls as a single flow.
function PageBreakOverlay({ previewRef }) {
  const [pageCount, setPageCount] = useState(1);

  useEffect(() => {
    if (!previewRef?.current) return;
    const update = () => {
      const h = previewRef.current?.offsetHeight || 0;
      setPageCount(Math.max(1, Math.ceil(h / PAGE_HEIGHT_PX)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(previewRef.current);
    return () => ro.disconnect();
  }, [previewRef]);

  // Render N-1 dividers (between pages); first page has no top-band.
  // Style mirrors `.imstudio-editor__page-gap` from imstudio.css — a thick
  // light-grey band that visually slices the document into A4 pages
  // (matches the "SIDA 2" reference design).
  const dividers = [];
  for (let i = 1; i < pageCount; i++) {
    dividers.push(
      <div
        key={i}
        className="imstudio-editor__page-gap"
        style={{
          position: "absolute",
          top: i * PAGE_HEIGHT_PX,
          left: 0,
          right: 0,
          transform: "translateY(-50%)",
          pointerEvents: "none",
          zIndex: 2,
        }}
      >
        Sida {i + 1}
      </div>
    );
  }

  return (
    <div
      data-pdf-hide
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
        zIndex: 2,
      }}
    >
      {dividers}
    </div>
  );
}

const FONT_OPTIONS = [
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Inter", value: "Inter, Arial, sans-serif" },
  { label: "Times New Roman", value: '"Times New Roman", serif' },
  { label: "Verdana", value: "Verdana, sans-serif" },
];

const COLOR_SWATCHES = [
  "#0f172a",
  "#334155",
  "#64748b",
  "#ffffff",
  "#f8fafc",
  "#e2e8f0",
  "#111827",
  "#57534E",
  "#dc2626",
  "#16a34a",
];
const PRESENTATION_SECTION_LABELS = [
  ["executive_summary", "Sammanfattning"], ["investment_highlights", "Investeringsargument"],
  ["summary", "Bolagsöversikt"], ["historik", "Historik"], ["market", "Marknad"],
  ["products_services", "Produkter & tjänster"], ["business_model", "Affärsmodell"],
  ["organisation", "Organisation"], ["financials", "Finansiell utveckling"],
  ["value_creation", "Värdeskapande"], ["risks", "Nyckelrisker"], ["valuation", "Värdering"],
  ["transaction_overview", "Transaktion"], ["strategic_buyer_rationale", "Köparlogik"],
  ["owners", "Ägare"], ["contact_info", "Kontakt"],
];

// Format SEK BigInt → MSEK with 1 decimal; null-safe.
function _toMSEK(n) {
  if (n === null || n === undefined || n === "") return "";
  const v = Number(n);
  if (!Number.isFinite(v)) return "";
  return (v / 1_000_000).toFixed(1).replace(".", ",");
}

function _toPct(n, digits = 1) {
  if (n === null || n === undefined || n === "") return "";
  const v = Number(n);
  if (!Number.isFinite(v)) return "";
  return v.toFixed(digits).replace(".", ",");
}

// Build the {headers, rows, footer} payload for the financialTable block from
// the /im-studio/company/<orgnr>/full/ "financials.years" rows. Pure: no
// fetching. Footer is a single summary row with revenue CAGR over the full
// period — buyers expect that figure on every CF financial page.
function buildFinancialTableData(yearsRows) {
  // 11-kolumners översikt — täcker det en CIM för bolag i 50 MSEK-segmentet
  // brukar ha: omsättning + tillväxt, lönsamhetsraden (RAFI + årets resultat),
  // balansposterna (tillgångar + eget kapital + nettokassa + soliditet) samt
  // personal. AnnualReportYear har inte EBITDA/EBIT separat — de skulle kräva
  // ny kolumn i modellen — så vi använder RAFI som lönsamhetsproxy (samma som
  // den kortare versionen tidigare).
  const headers = [
    "År",
    "Omsättning (MSEK)",
    "Tillväxt (%)",
    "RAFI (MSEK)",
    "Marg. (%)",
    "Årets res. (MSEK)",
    "Tillg. (MSEK)",
    "Eget kap. (MSEK)",
    "Nettokassa (MSEK)",
    "Sol. (%)",
    "Anställda",
  ];

  const list = Array.isArray(yearsRows) ? yearsRows : [];

  const rows = list.map((y, i) => {
    const rev = Number(y.revenue);
    const rafi = y.result_after_financial_items;
    const margin =
      Number.isFinite(rev) && rev > 0 && rafi !== null && rafi !== undefined
        ? (Number(rafi) / rev) * 100
        : null;

    // YoY-tillväxt mot föregående år. Första året får "—".
    let growth = null;
    if (i > 0) {
      const prev = Number(list[i - 1]?.revenue);
      if (Number.isFinite(prev) && prev > 0 && Number.isFinite(rev)) {
        growth = ((rev - prev) / prev) * 100;
      }
    }

    return [
      String(y.fiscal_year_label || ""),
      _toMSEK(y.revenue),
      growth === null ? "" : _toPct(growth),
      _toMSEK(y.result_after_financial_items),
      _toPct(margin),
      _toMSEK(y.profit_for_year),
      _toMSEK(y.total_assets),
      _toMSEK(y.total_equity),
      _toMSEK(y.net_cash),
      _toPct(y.equity_ratio_percent),
      y.employees !== null && y.employees !== undefined ? String(y.employees) : "",
    ];
  });

  // Sammanställning i tfoot — en rad per nyckeltal som hör hemma i PM:et.
  // Stöder en lista av {label, value}-objekt; back-compat med tidigare
  // singel-objekt sker i renderaren.
  const footers = [];
  const revs = list
    .map((y) => Number(y.revenue))
    .filter((v) => Number.isFinite(v) && v > 0);
  if (revs.length >= 2) {
    const first = revs[0];
    const last = revs[revs.length - 1];
    const periods = revs.length - 1;
    const cagrPct = (Math.pow(last / first, 1 / periods) - 1) * 100;
    footers.push({
      label: `Omsättning CAGR (${list[0]?.fiscal_year_label || ""}–${list[list.length - 1]?.fiscal_year_label || ""})`,
      value: `${cagrPct.toFixed(1).replace(".", ",")} %`,
    });
  }

  // Snittmarginal (RAFI / oms) över hela perioden — viktigare nyckeltal
  // än sista årets enskilda marginal eftersom köpare värderar uthållighet.
  const margins = list
    .map((y) => {
      const rev = Number(y.revenue);
      const rafi = Number(y.result_after_financial_items);
      if (!Number.isFinite(rev) || rev <= 0 || !Number.isFinite(rafi)) return null;
      return (rafi / rev) * 100;
    })
    .filter((v) => v !== null);
  if (margins.length) {
    const avg = margins.reduce((a, b) => a + b, 0) / margins.length;
    footers.push({
      label: `Snittmarginal (${margins.length} år)`,
      value: `${avg.toFixed(1).replace(".", ",")} %`,
    });
  }

  // Snitt-antal anställda — vanlig CIM-rad för att signalera bolagets storlek
  // över tid, inte bara senaste år.
  const heads = list
    .map((y) => Number(y.employees))
    .filter((v) => Number.isFinite(v) && v > 0);
  if (heads.length) {
    const avg = heads.reduce((a, b) => a + b, 0) / heads.length;
    footers.push({
      label: `Snitt antal anställda`,
      value: String(Math.round(avg)),
    });
  }

  // Senaste årets omsättning per anställd (kSEK) — proxy för effektivitet.
  const lastYear = list[list.length - 1];
  if (lastYear) {
    const rev = Number(lastYear.revenue);
    const emp = Number(lastYear.employees);
    if (Number.isFinite(rev) && rev > 0 && Number.isFinite(emp) && emp > 0) {
      const perEmployeeKSEK = rev / emp / 1000;
      footers.push({
        label: `Oms./anställd (senaste år, kSEK)`,
        value: new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 })
          .format(Math.round(perEmployeeKSEK)),
      });
    }
  }

  // Back-compat: behåll `footer` (singular) som första entry så äldre
  // renderare som inte uppdaterats fortfarande visar något.
  return {
    headers,
    rows,
    footer: footers[0] || null,
    footers,
  };
}

// {rows: [{label, value}], shareholders: [{name, share_percent, role}]} —
// only fields with a value are forwarded so the UI can skip empty rows
// automatically. shareholders is rendered as its own sub-table in the block
// so each owner gets a row with explicit share percentage.
function buildOwnershipTableData(full) {
  const org = full?.organisation || {};
  const oq = full?.owner_questionnaire || {};

  const rows = [];
  const push = (label, value) => {
    const v = value === null || value === undefined ? "" : String(value).trim();
    if (v) rows.push({ label, value: v });
  };

  push("Juridisk form", org.juridisk_form);
  push("Säte", org.hemvistkommun);
  push("Registreringsdatum", org.registreringsdatum);

  // Aktiekapital displayed with currency when both present.
  if (org.aktiekapital_belopp) {
    const cur = (org.aktiekapital_valuta || "SEK").toUpperCase();
    push("Aktiekapital", `${org.aktiekapital_belopp} ${cur}`);
  }
  // "Antal aktier" intentionally omitted — confidential pre-SPA, belongs in
  // dataroom not the IM ownership page.

  // When a structured shareholder list exists, drop the redundant
  // "Antal ägare", "Ägarbild" and "Ägarnas involvering" rows — the table
  // already conveys them visually. Keep them when shareholders are empty so
  // the section still has substance.
  const hasShareholderTable =
    Array.isArray(oq.shareholders) &&
    oq.shareholders.some(
      (s) =>
        (s?.name || "").trim() ||
        s?.share_percent !== null && s?.share_percent !== undefined,
    );

  if (!hasShareholderTable) {
    if (oq.number_of_shareholders !== null && oq.number_of_shareholders !== undefined) {
      push("Antal ägare", String(oq.number_of_shareholders));
    }
    push("Ägarbild", oq.owner_description);
    push("Ägarnas involvering", oq.owner_involvement);
  }
  push("Ledningsgrupp & nyckelpersoner", oq.management_summary);
  push("Firmateckning", org.firmateckning_klartext);

  // Structured shareholders — kept separate from the label/value rows so the
  // renderer can show them in their own labelled section with explicit
  // share %.
  const rawShareholders = Array.isArray(oq.shareholders) ? oq.shareholders : [];
  const shareholders = rawShareholders
    .map((s) => ({
      name: (s?.name || "").trim(),
      share_percent:
        s?.share_percent === null || s?.share_percent === undefined
          ? null
          : Number(s.share_percent),
      role: (s?.role || "").trim(),
    }))
    .filter((s) => s.name || s.share_percent !== null || s.role);

  return { rows, shareholders };
}

// {periods: [{label, revenue, marginPct}]}
function buildRevenueChartData(full) {
  const years = full?.financials?.years || [];
  const periods = (Array.isArray(years) ? years : [])
    .map((y) => {
      const rev = Number(y.revenue);
      const rafi = Number(y.result_after_financial_items);
      const marginPct = rev > 0 && Number.isFinite(rafi) ? (rafi / rev) * 100 : null;
      return {
        label: String(y.fiscal_year_label || ""),
        revenue: Number.isFinite(rev) ? rev : null,
        marginPct,
      };
    })
    .filter((p) => p.label && p.revenue !== null);
  return { periods };
}

// PREWRITTEN_DEFS mirrors the Phase-12 IMComposer's compose_all order in
// backend/IM_studio/im_composer.py:291. Sections marked ``source:
// "composer"`` are persisted by IMComposer.persist_all and read back
// from ``full.im_comments[<agentKey>].body`` (the generic fallback in
// getPrewrittenBody). Broker-manual and legacy Phase-8 agents (risks,
// prognos) stay in the list — they're not part of the composer output
// but the broker still needs them in the sidebar. Data-only renderers
// (financial_table, ownership_table, revenue_chart) are rendered from
// raw DB via /full/ — no composer/agent involvement.
const PREWRITTEN_DEFS = [
  // ── Composer sections ──
  {
    id: "overview",
    label: "Bolagsöversikt",
    defaultTitle: "Bolagsöversikt",
    source: "composer",
    agentKey: "overview",
  },
  {
    id: "executive_summary",
    label: "Sammanfattning",
    defaultTitle: "Sammanfattning",
    source: "composer",
    agentKey: "executive_summary",
  },
  {
    id: "investment_highlights",
    label: "Investeringsargument",
    defaultTitle: "Investeringsargument",
    source: "composer",
    agentKey: "investment_highlights",
  },
  {
    id: "historik",
    label: "Historik",
    defaultTitle: "Historik",
    source: "composer",
    agentKey: "historik",
  },
  {
    id: "marknad",
    label: "Marknad",
    defaultTitle: "Marknad",
    source: "composer",
    agentKey: "marknad",
  },
  {
    id: "products_services",
    label: "Produkter & tjänster",
    defaultTitle: "Produkter & tjänster",
    source: "composer",
    agentKey: "products_services",
  },
  {
    id: "business_model",
    label: "Affärsmodell",
    defaultTitle: "Affärsmodell",
    source: "composer",
    agentKey: "business_model",
  },
  {
    id: "value_creation",
    label: "Värdeskapande",
    defaultTitle: "Värdeskapande",
    source: "composer",
    agentKey: "value_creation",
    // Composer emits an empty body when there's no meaningful uplift
    // narrative — hide the sidebar row rather than show a permanently
    // grey card.
    optional: true,
  },
  {
    id: "organisation",
    label: "Organisation",
    defaultTitle: "Organisation",
    source: "composer",
    agentKey: "organisation",
  },
  {
    id: "financials",
    label: "Finansiell utveckling",
    defaultTitle: "Finansiell utveckling",
    source: "composer",
    agentKey: "financials",
  },
  // Data-only: pulls AnnualReportYear rows from /full/ and renders a
  // table. No LLM. Placed right after ``financials`` so the numeric
  // backup sits under the prose commentary in the sidebar order.
  {
    id: "financial_table",
    label: "Finansiell tabell",
    defaultTitle: "Finansiell utveckling i siffror",
    source: "data",
  },
  // Data-only SVG chart of revenue & margin trend.
  {
    id: "revenue_chart",
    label: "Omsättningsdiagram",
    defaultTitle: "Omsättning & marginal",
    source: "data",
  },
  {
    id: "saas_metrics",
    label: "SaaS-mätvärden",
    defaultTitle: "SaaS-mätvärden",
    source: "composer",
    agentKey: "saas_metrics",
    // Shape-gated in the backend composer (_compose_saas_metrics):
    // only produces content when the workspace's BusinessShape resolves
    // to a recurring/hybrid revenue model. Non-SaaS companies never
    // get a saas_metrics body, so hide the sidebar row for them.
    optional: true,
  },
  {
    id: "valuation",
    label: "Värdering",
    defaultTitle: "Värdering",
    source: "composer",
    agentKey: "valuation",
  },
  {
    id: "transaction_overview",
    label: "Transaktionsöversikt",
    defaultTitle: "Transaktionsöversikt",
    source: "composer",
    agentKey: "transaction_overview",
  },
  {
    id: "strategic_buyer_rationale",
    label: "Strategisk köparlogik",
    defaultTitle: "Strategisk köparlogik",
    source: "composer",
    agentKey: "strategic_buyer_rationale",
    // Only meaningful when identifiable strategic buyers exist. Hide
    // when the composer emits no rationale rather than show an empty
    // "Kan genereras" card.
    optional: true,
  },
  // ── Legacy Phase-8 sections still supported ──
  {
    id: "risks",
    label: "Nyckelrisker",
    defaultTitle: "Nyckelrisker",
    source: "comment",
    agentKey: "risks",
  },
  {
    id: "prognos",
    label: "Prognos",
    defaultTitle: "Prognos",
    source: "comment",
    agentKey: "prognos",
  },
  // Data-only renderer for owners, board and key corporate facts.
  // Pulls from Organisation + OwnerQuestionnaire — no LLM.
  {
    id: "ownership_table",
    label: "Ägarbild & styrelse",
    defaultTitle: "Ägarbild & styrelse",
    source: "data",
  },
];

function joinUrl(base, path) {
  if (!path.startsWith("/")) path = `/${path}`;
  const b = (base || "").replace(/\/+$/, "");
  return `${b}${path}`;
}

async function apiFetch(path, { method = "GET", body } = {}) {
  const url = joinUrl(API_BASE, path);
  const csrf = getCsrfToken();

  const headers = { Accept: "application/json" };
  const hasBody = body !== undefined && body !== null;
  if (hasBody) headers["Content-Type"] = "application/json";

  const unsafe = !["GET", "HEAD", "OPTIONS", "TRACE"].includes(method.toUpperCase());
  if (unsafe && csrf) headers["X-CSRFToken"] = csrf;

  const res = await fetch(url, {
    method,
    credentials: "include",
    headers,
    body: hasBody ? JSON.stringify(body) : undefined,
  });

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json().catch(() => ({})) : await res.text().catch(() => "");

  if (!res.ok) {
    const msg =
      (typeof data === "object" && (data.detail || data.error)) ||
      (typeof data === "string" && data.slice(0, 250)) ||
      `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }

  if (!isJson && typeof data === "string" && data.trim().startsWith("<!DOCTYPE")) {
    throw new Error(`Expected JSON but got HTML. Wrong host or missing proxy for: ${path}`);
  }

  return typeof data === "object" ? data : { ok: true, body: data };
}

const fetchIMFull = (orgnr) => apiFetch(`/im-studio/company/${encodeURIComponent(orgnr)}/full/`);
const fetchTemplates = (orgnr) => apiFetch(`/im-studio/company/${encodeURIComponent(orgnr)}/templates/`);
const saveTemplate = (orgnr, name, title, layout, theme = {}) =>
  apiFetch(`/im-studio/company/${encodeURIComponent(orgnr)}/templates/`, {
    method: "POST",
    body: { name, title, layout, theme },
  });
const saveIMImages = (orgnr, images) =>
  apiFetch(`/im-studio/context/${encodeURIComponent(orgnr)}/images/`, {
    method: "POST",
    body: { images },
  });
const saveIMPresentation = (orgnr, selection) =>
  apiFetch(`/im-studio/context/${encodeURIComponent(orgnr)}/presentation/`, {
    method: "POST",
    body: selection,
  });
const fetchPresentationProfile = (orgnr) =>
  apiFetch(`/im-studio/company/${encodeURIComponent(orgnr)}/presentation-profile/`);
const savePresentationProfile = (orgnr, configuration) =>
  apiFetch(`/im-studio/company/${encodeURIComponent(orgnr)}/presentation-profile/`, {
    method: "PATCH", body: { configuration },
  });
async function uploadPresentationAsset(orgnr, role, file) {
  const form = new FormData();
  form.append("role", role);
  form.append("image", file);
  const csrf = getCsrfToken();
  const response = await fetch(joinUrl(API_BASE,
    `/im-studio/company/${encodeURIComponent(orgnr)}/presentation-profile/`), {
    method: "POST", credentials: "include",
    headers: csrf ? { "X-CSRFToken": csrf } : {}, body: form,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`);
  return data;
}
const fetchAgentStatus = (orgnr) =>
  apiFetch(`/im-studio/agents/status/${encodeURIComponent(orgnr)}/`);
const runValuationAgent = (orgnr) =>
  apiFetch(`/im-studio/agents/run/valuation/${encodeURIComponent(orgnr)}/`, { method: "POST" });
const runFinancialAgent = (orgnr) =>
  apiFetch(`/im-studio/agents/run/financials/${encodeURIComponent(orgnr)}/`, { method: "POST" });
const runRiskAgent = (orgnr) =>
  apiFetch(`/im-studio/agents/run/risks/${encodeURIComponent(orgnr)}/`, { method: "POST" });
const runWebAgent = (orgnr, base_url) =>
  apiFetch(`/im-studio/agents/run/web/${encodeURIComponent(orgnr)}/`, {
    method: "POST",
    body: base_url ? { base_url } : {},
  });
const runOverviewAgent = (orgnr) =>
  apiFetch(`/im-studio/agents/run/overview/${encodeURIComponent(orgnr)}/`, { method: "POST" });

// "Ladda in allt": runs all 13 agents server-side, returns per-agent
// success/fail in `results`. We always treat a 200 as "look at results";
// individual agent failures are not HTTP errors.
const runAllAgents = (orgnr, force = true) =>
  apiFetch(`/im-studio/agents/run/all/${encodeURIComponent(orgnr)}/`, {
    method: "POST",
    body: { force_refresh: force },
  });

// SSE variant of runAllAgents — reads the endpoint's text/event-stream
// response and invokes callbacks per event so the caller can insert
// sections into the canvas as each future in the parallel prose layer
// resolves (first section around ~10s, all done by ~30-45s instead of
// a single ~40s blocking response).
//
// Contract: onEvent receives {type: "started"} | {type: "section", ...}
//           | {type: "section_updated", ...} | {type: "error", ...}
//           | {type: "done", ...}. Fires as each event arrives.
// Returns  the "done" payload once the stream closes.
async function streamRunAllAgents(orgnr, onEvent) {
  const url = joinUrl(
    API_BASE,
    `/im-studio/agents/run/all/${encodeURIComponent(orgnr)}/`,
  );
  const csrf = getCsrfToken();
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Accept": "text/event-stream",
      "Content-Type": "application/json",
      ...(csrf ? { "X-CSRFToken": csrf } : {}),
    },
    body: JSON.stringify({ force_refresh: true }),
  });
  if (!res.ok || !res.body) {
    throw new Error(`Streaming request failed (HTTP ${res.status}).`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let doneEvent = null;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE frames are delimited by "\n\n". Parse whatever's complete
    // so far and keep the tail in the buffer.
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const dataLines = frame
        .split("\n")
        .filter((l) => l.startsWith("data:"))
        .map((l) => l.slice(5).trim())
        .filter(Boolean);
      if (!dataLines.length) continue;
      let event;
      try {
        event = JSON.parse(dataLines.join("\n"));
      } catch {
        continue;
      }
      if (event?.type === "done") {
        doneEvent = event;
      }
      try {
        onEvent(event);
      } catch (err) {
        // Callback errors shouldn't abort the stream — surface via
        // an error event on the same channel so the UI can react.
        onEvent({ type: "error", error: String(err?.message || err) });
      }
    }
  }
  return doneEvent;
}

// Faktagranskning — toggle the broker's "reviewed" state on a single
// consistency issue. Returns the full updated payload so the caller can
// re-render from the response without a separate GET.
const patchConsistencyIssue = (orgnr, issueId, checked) =>
  apiFetch(
    `/im-studio/agents/consistency/${encodeURIComponent(orgnr)}/issue/${encodeURIComponent(issueId)}/`,
    { method: "PATCH", body: { checked: !!checked } },
  );

// HITL endpoints. `comment_type` is one of: "financials" | "valuation" | "risks" | "web_profile".
const approveAgentComment = (orgnr, comment_type) =>
  apiFetch(`/im-studio/agents/approve/${encodeURIComponent(orgnr)}/${encodeURIComponent(comment_type)}/`, {
    method: "POST",
  });
const reopenAgentComment = (orgnr, comment_type) =>
  apiFetch(`/im-studio/agents/reopen/${encodeURIComponent(orgnr)}/${encodeURIComponent(comment_type)}/`, {
    method: "POST",
  });
const refineAgentComment = (orgnr, comment_type, feedback) =>
  apiFetch(`/im-studio/agents/refine/${encodeURIComponent(orgnr)}/${encodeURIComponent(comment_type)}/`, {
    method: "POST",
    body: { feedback },
  });
const editAgentComment = (orgnr, comment_type, body) =>
  apiFetch(`/im-studio/agents/edit/${encodeURIComponent(orgnr)}/${encodeURIComponent(comment_type)}/`, {
    method: "PATCH",
    body: { body },
  });
const fetchAgentRevisions = (orgnr, comment_type) =>
  apiFetch(`/im-studio/agents/revisions/${encodeURIComponent(orgnr)}/${encodeURIComponent(comment_type)}/`);
const fetchAgentContext = (orgnr, comment_type) =>
  apiFetch(`/im-studio/agents/context/${encodeURIComponent(orgnr)}/${encodeURIComponent(comment_type)}/`);
const restoreAgentRevision = (orgnr, comment_type, revisionId) =>
  apiFetch(
    `/im-studio/agents/revisions/${encodeURIComponent(orgnr)}/${encodeURIComponent(comment_type)}/${encodeURIComponent(revisionId)}/restore/`,
    { method: "POST" }
  );

// Map IMStudio's section.agentKey ("financials" | "valuation" | "risks" | "web")
// to the backend IMCompanyComment.comment_type values. The web one differs.
function commentTypeFor(agentKey) {
  if (agentKey === "web") return "web_profile";
  if (agentKey === "overview") return "overview";
  return agentKey || null;
}

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function sectionStyleDefaults() {
  return {
    fontFamily: "Arial, Helvetica, sans-serif",
    fontSize: 16,
    fontWeight: 400,
    textColor: "#0f172a",
    backgroundColor: "transparent",
    textAlign: "left",
    paddingY: 32,
    paddingX: 32,
  };
}

function createContentBlock(kind, overrides = {}) {
  const baseByKind = {
    title: {
      id: uid(),
      kind: "title",
      text: "Section title",
      level: "h2",
    },
    text: {
      id: uid(),
      kind: "text",
      text: "Write a short paragraph here.",
    },
    image: {
      id: uid(),
      kind: "image",
      imageUrl:
        "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=1200&q=80",
      caption: "",
    },
    imageCard: {
      id: uid(),
      kind: "imageCard",
      imageUrl:
        "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80",
      title: "Kort-titel",
      text: "Kort beskrivande text under bilden — gör om till vad du vill.",
      buttonText: "Läs mer",
      buttonUrl: "#",
    },
    divider: {
      id: uid(),
      kind: "divider",
      color: "#e7e5e4",
      thickness: 1,
    },
    spacer: {
      id: uid(),
      kind: "spacer",
      height: 32,
    },
    button: {
      id: uid(),
      kind: "button",
      text: "Learn more",
      url: "#",
      style: "dark",
    },
    twoColText: {
      id: uid(),
      kind: "twoColText",
      leftTitle: "Left title",
      leftText: "Left text",
      rightTitle: "Right title",
      rightText: "Right text",
    },
    // Financial table — data-only, no agent. Headers + rows arrays are passed
    // in as overrides when the prewritten card is dragged in; default is an
    // empty placeholder.
    financialTable: {
      id: uid(),
      kind: "financialTable",
      headers: ["År", "Omsättning (MSEK)", "RAFI (MSEK)", "Marginal (%)", "Nettokassa (MSEK)", "Soliditet (%)", "Anställda"],
      rows: [],
      footer: null,  // optional {label, value} summary row
    },
    // Ownership & board — pure data-renderer (no agent). Two columns of
    // label/value pairs sourced from Organisation + OwnerQuestionnaire, plus
    // a structured shareholders list with explicit share percentages.
    ownershipTable: {
      id: uid(),
      kind: "ownershipTable",
      rows: [],
      shareholders: [],
    },
    // Revenue chart — inline SVG, no chart library. periods is an array of
    // {label, revenue, marginPct}.
    revenueChart: {
      id: uid(),
      kind: "revenueChart",
      periods: [],
    },
  };

  return {
    ...baseByKind[kind],
    ...overrides,
    id: overrides.id || uid(),
  };
}

function createSectionBlock(type, overrides = {}) {
  const baseByType = {
    hero: {
      id: uid(),
      type: "hero",
      label: "Hero section",
      bg: "#f8fafc",
      ...sectionStyleDefaults(),
      children: [
        createContentBlock("title", {
          text: "Sell your company with a sharper process",
          level: "h1",
        }),
        createContentBlock("text", {
          text: "A clean, structured overview for buyers with the right story, metrics, and next steps.",
        }),
        createContentBlock("button", {
          text: "Book a meeting",
          url: "#",
          style: "dark",
        }),
        createContentBlock("image", {
          imageUrl:
            "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80",
          caption: "",
        }),
      ],
    },
    textSection: {
      id: uid(),
      type: "textSection",
      label: "Text section",
      ...sectionStyleDefaults(),
      children: [
        createContentBlock("title", {
          text: "Section heading",
          level: "h2",
        }),
        createContentBlock("text", {
          text: "Write a short paragraph here. This is intended for prewritten content blocks that can be adjusted quickly.",
        }),
      ],
    },
    twoColSection: {
      id: uid(),
      type: "twoColSection",
      label: "Two columns section",
      ...sectionStyleDefaults(),
      children: [
        createContentBlock("twoColText", {
          leftTitle: "What buyers care about",
          leftText:
            "Clear growth drivers, stable margins, recurring customers, and a credible expansion story.",
          rightTitle: "What we prepare",
          rightText:
            "Financial overview, risk mapping, company story, and a cleaner buyer-facing presentation package.",
        }),
      ],
    },
    imageSection: {
      id: uid(),
      type: "imageSection",
      label: "Image section",
      ...sectionStyleDefaults(),
      children: [
        createContentBlock("image", {
          imageUrl:
            "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=1200&q=80",
          caption: "Optional image caption",
        }),
      ],
    },
    ctaSection: {
      id: uid(),
      type: "ctaSection",
      label: "CTA section",
      bg: "#111827",
      textColor: "#ffffff",
      ...sectionStyleDefaults(),
      children: [
        createContentBlock("title", {
          text: "Ready to move forward?",
          level: "h2",
        }),
        createContentBlock("text", {
          text: "Book a review and get a clean first version of your material.",
        }),
        createContentBlock("button", {
          text: "Get started",
          url: "#",
          style: "light",
        }),
      ],
    },
    dividerSection: {
      id: uid(),
      type: "dividerSection",
      label: "Divider section",
      backgroundColor: "transparent",
      paddingY: 24,
      paddingX: 32,
      children: [createContentBlock("divider")],
    },
    spacerSection: {
      id: uid(),
      type: "spacerSection",
      label: "Spacer",
      backgroundColor: "transparent",
      paddingY: 0,
      paddingX: 0,
      children: [createContentBlock("spacer")],
    },
    imageCardSection: {
      id: uid(),
      type: "imageCardSection",
      label: "Image card",
      ...sectionStyleDefaults(),
      children: [createContentBlock("imageCard")],
    },
    columnsSection: {
      id: uid(),
      type: "columnsSection",
      label: "Kolumner",
      ...sectionStyleDefaults(),
      // Two cells, each holding its own children — Mailchimp-style.
      // Cells are independent drop zones (handled by the renderer below).
      cells: [
        { id: uid(), children: [createContentBlock("title", { text: "Vänster", level: "h3" }),
                                createContentBlock("text", { text: "Text i vänster kolumn." })] },
        { id: uid(), children: [createContentBlock("title", { text: "Höger", level: "h3" }),
                                createContentBlock("text", { text: "Text i höger kolumn." })] },
      ],
      children: [],
    },
  };

  const base = baseByType[type];
  return {
    ...base,
    ...overrides,
    id: overrides.id || uid(),
    children: overrides.children || base.children.map((child) => ({ ...child, id: uid() })),
  };
}

const sectionLibrary = {
  hero: { type: "hero", label: "Hero" },
  textSection: { type: "textSection", label: "Text section" },
  twoColSection: { type: "twoColSection", label: "Two columns" },
  imageSection: { type: "imageSection", label: "Image section" },
  imageCardSection: { type: "imageCardSection", label: "Image card" },
  columnsSection: { type: "columnsSection", label: "Kolumner" },
  ctaSection: { type: "ctaSection", label: "CTA" },
  dividerSection: { type: "dividerSection", label: "Divider" },
  spacerSection: { type: "spacerSection", label: "Spacer" },
};

const starterTemplates = [
  {
    id: "deal-room",
    name: "Deal teaser",
    description: "Hero, intro, two columns, CTA",
    sections: [
      createSectionBlock("hero", {
        children: [
          createContentBlock("title", {
            text: "Confidential investment opportunity",
            level: "h1",
          }),
          createContentBlock("text", {
            text: "A modern one-page teaser with your logo, short story, and clear next action.",
          }),
          createContentBlock("button", {
            text: "Book a meeting",
            url: "#",
            style: "dark",
          }),
          createContentBlock("image", {
            imageUrl:
              "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80",
            caption: "",
          }),
        ],
      }),
      createSectionBlock("textSection", {
        children: [
          createContentBlock("title", {
            text: "Company overview",
            level: "h2",
          }),
          createContentBlock("text", {
            text: "The company operates in a structurally attractive niche with repeat demand, strong customer relationships, and a scalable operating model.",
          }),
        ],
      }),
      createSectionBlock("twoColSection"),
      createSectionBlock("ctaSection", {
        children: [
          createContentBlock("title", {
            text: "Request more information",
            level: "h2",
          }),
          createContentBlock("text", {
            text: "Contact us to receive a fuller presentation and process overview.",
          }),
          createContentBlock("button", {
            text: "Get started",
            url: "#",
            style: "light",
          }),
        ],
      }),
    ],
  },
  {
    id: "newsletter",
    name: "Simple newsletter",
    description: "Header, text, image, CTA",
    sections: [
      createSectionBlock("textSection", {
        children: [
          createContentBlock("title", {
            text: "Monthly update",
            level: "h2",
          }),
          createContentBlock("text", {
            text: "Use this for a Mailchimp-style email or PDF layout with reusable sections and branded visuals.",
          }),
        ],
      }),
      createSectionBlock("imageSection"),
      createSectionBlock("ctaSection", {
        children: [
          createContentBlock("title", {
            text: "Read more",
            level: "h2",
          }),
          createContentBlock("text", {
            text: "Link readers to a landing page or booking flow.",
          }),
          createContentBlock("button", {
            text: "Read more",
            url: "#",
            style: "light",
          }),
        ],
      }),
    ],
  },
];

function getNestedString(obj, paths = []) {
  for (const path of paths) {
    const value = path.split(".").reduce((acc, key) => acc?.[key], obj);
    if (typeof value === "string" && value.trim()) return value;
  }
  return "";
}

function buildWhyDisabled(status, agentKey) {
  if (!status || typeof status !== "object") return [];

  if (agentKey === "financials") {
    const lines = [];
    if (!status?.financials?.sie_exists) lines.push("SIE saknas.");
    if ((status?.financials?.bokslut_count || 0) < 5) {
      lines.push(`Bokslut: ${status?.financials?.bokslut_count || 0}/5.`);
    }
    return lines;
  }

  if (agentKey === "valuation") {
    if (!status?.valuation?.finalized_exists) {
      return ["Ingen färdig värdering hittades."];
    }
    return [];
  }

  if (agentKey === "risks") {
    if (status?.risks?.inputs_done) return [];

    const missing = status?.risks?.inputs_missing || {};
    const lines = ["Risk-underlag är inte komplett."];

    const rp = missing?.risk_profile;
    if (Array.isArray(rp) && rp.length) {
      if (rp[0] === "missing") {
        lines.push("Riskprofil saknas helt.");
      } else {
        lines.push(`Riskprofil saknar: ${rp.join(", ")}.`);
      }
    }

    const ddq = missing?.ddq;
    if (ddq) {
      lines.push("DDQ saknas eller är inte konfigurerad.");
    }

    const oq = missing?.owner_questionnaire;
    if (Array.isArray(oq) && oq.length) {
      if (oq[0] === "missing") {
        lines.push("Ägarfrågeformulär saknas helt.");
      } else {
        lines.push(`Ägarfrågeformulär saknar: ${oq.join(", ")}.`);
      }
    }

    return lines;
  }

  if (agentKey === "web") {
    if (!status?.web?.has_website) {
      return ["Ingen webbplats kopplad till workspacet."];
    }
    return [];
  }

  if (agentKey === "overview") {
    const missing = status?.overview?.missing || [];
    if (!missing.length) return [];
    const labelMap = {
      financials_comment_missing: "Finansiell utveckling",
      risks_comment_missing: "Nyckelrisker",
      valuation_comment_missing: "Värdering",
      web_comment_missing: "Erbjudande & tjänster",
    };
    return [
      "Följande avsnitt behöver vara klara först:",
      ...missing.map((k) => `• ${labelMap[k] || k}`),
    ];
  }

  return [];
}

function getDisabledTooltip(item) {
  if (!item?.whyDisabled?.length) return "";
  return item.whyDisabled.join("\n");
}

const RISK_FIELD_LABELS = {
  key_person_risk: "Key person risk",
  customer_concentration: "Customer concentration",
  supplier_dependency: "Supplier dependency",
  contract_quality: "Contract quality",
  financial_health: "Financial health",
  compliance_risk: "Compliance risk",
};

const OQ_FIELD_LABELS = {
  deal_type: "Deal type",
  reason_for_sale: "Anledning till försäljning",
  seller_role_after: "Säljarens roll efter",
  number_of_customers: "Antal kunder",
  headcount_fte: "Antal anställda (FTE)",
};

function buildGateChecklist(agentKey, agentStatus) {
  if (!agentKey || !agentStatus) return null;

  if (agentKey === "financials") {
    const fin = agentStatus.financials || {};
    const fields = [
      { label: "SIE-fil", done: !!fin.sie_exists },
      { label: `Bokslut (${fin.bokslut_count || 0}/5)`, done: (fin.bokslut_count || 0) >= 5 },
    ];
    return fields;
  }

  if (agentKey === "valuation") {
    const v = agentStatus.valuation || {};
    return [{ label: "Färdig värdering", done: !!v.finalized_exists }];
  }

  if (agentKey === "risks") {
    const r = agentStatus.risks || {};
    const missing = r.inputs_missing || {};

    const rpMissing = Array.isArray(missing.risk_profile) ? missing.risk_profile : [];
    const rpDone = rpMissing.length === 0;

    const ddqRow = missing.ddq;
    const ddqDone = !ddqRow || (typeof ddqRow === "object" && ddqRow.completed === ddqRow.total);
    const ddqLabel =
      typeof ddqRow === "object" && ddqRow.completed != null
        ? `Due diligence-quiz (${ddqRow.completed}/${ddqRow.total})`
        : "Due diligence-quiz";

    const oqMissing = Array.isArray(missing.owner_questionnaire) ? missing.owner_questionnaire : [];
    const oqDone = oqMissing.length === 0;

    return [
      { label: "Riskprofil",       done: rpDone },
      { label: ddqLabel,           done: ddqDone },
      { label: "Ägarformulär",     done: oqDone },
    ];
  }

  if (agentKey === "web") {
    const w = agentStatus.web || {};
    return [{ label: "Webbplats kopplad", done: !!w.has_website }];
  }

  if (agentKey === "overview") {
    // Bolagsöversikt requires all four sibling comments to exist first.
    const o = agentStatus.overview || {};
    const missing = new Set(Array.isArray(o.missing) ? o.missing : []);
    return [
      { label: "Erbjudande & tjänster", done: !missing.has("web_comment_missing") },
      { label: "Finansiell utveckling", done: !missing.has("financials_comment_missing") },
      { label: "Värdering",             done: !missing.has("valuation_comment_missing") },
      { label: "Nyckelrisker",          done: !missing.has("risks_comment_missing") },
    ];
  }

  return null;
}

function gateCtaFor(agentKey, orgnr) {
  if (!orgnr) return null;
  switch (agentKey) {
    case "financials":
      return { label: "Ladda upp finansdata →", to: `/check?orgnr=${orgnr}` };
    case "valuation":
      return { label: "Slutför värdering →", to: `/valuation/${orgnr}/` };
    case "risks":
      return { label: "Fyll i riskprofil →", to: `/duediligence/${orgnr}/` };
    case "web":
      return { label: "Lägg till webbplats →", to: `/dashboard/${orgnr}/` };
    default:
      return null;
  }
}

function getPrewrittenBody(item, full, agentStatus) {
  const pm = full?.pm || full?.im_document || {};
  const storedSections = pm.sections && typeof pm.sections === "object" ? pm.sections : {};

  if (typeof storedSections[item.id] === "string" && storedSections[item.id].trim()) {
    return storedSections[item.id];
  }

  // financial_table is data-only — render a short preview string for the
  // prewritten card so the broker sees "5 år (2020–2024)" instead of empty.
  // The actual table rows live on item.tableData (set by
  // buildPrewrittenItemsFromBackend) and are inserted by buildPrewrittenChildren.
  if (item.id === "financial_table") {
    const years = full?.financials?.years || [];
    if (!Array.isArray(years) || years.length === 0) return "";
    const labels = years
      .map((y) => String(y.fiscal_year_label || "").trim())
      .filter(Boolean);
    const first = labels[0];
    const last = labels[labels.length - 1];
    return first && last ? `${years.length} år (${first}–${last})` : `${years.length} år`;
  }

  if (item.id === "ownership_table") {
    const data = buildOwnershipTableData(full);
    const fieldCount = data.rows.length;
    const ownerCount = data.shareholders.length;
    if (!fieldCount && !ownerCount) return "";
    const parts = [];
    if (fieldCount) parts.push(`${fieldCount} fält`);
    if (ownerCount) parts.push(`${ownerCount} ägare`);
    return parts.join(", ");
  }

  if (item.id === "revenue_chart") {
    const data = buildRevenueChartData(full);
    if (!data.periods.length) return "";
    return `${data.periods.length} år`;
  }

  if (item.agentKey === "financials" && agentStatus?.financials?.comment_exists) {
    return getNestedString(full, [
      "im_comments.financials.body",
      "im_comments.financials.payload.body",
      "financial_comment.body",
      "financial_comment.payload.body",
      "comments.financials.body",
      "comments.financials.payload.body",
      "agents.financials.body",
      "agents.financials.payload.body",
    ]);
  }

  if (item.agentKey === "valuation" && agentStatus?.valuation?.comment_exists) {
    return getNestedString(full, [
      "im_comments.valuation.body",
      "im_comments.valuation.payload.body",
      "valuation_comment.body",
      "valuation_comment.payload.body",
      "comments.valuation.body",
      "comments.valuation.payload.body",
      "agents.valuation.body",
      "agents.valuation.payload.body",
    ]);
  }

  if (item.agentKey === "risks" && agentStatus?.risks?.comment_exists) {
    return getNestedString(full, [
      "im_comments.risks.body",
      "im_comments.risks.payload.body",
      "risk_comment.body",
      "risk_comment.payload.body",
      "comments.risks.body",
      "comments.risks.payload.body",
      "agents.risks.body",
      "agents.risks.payload.body",
    ]);
  }

  if (item.agentKey === "overview" && agentStatus?.overview?.comment_exists) {
    return getNestedString(full, [
      "im_comments.overview.body",
      "im_comments.overview.payload.body",
      "comments.overview.body",
      "comments.overview.payload.body",
      "agents.overview.body",
      "agents.overview.payload.body",
    ]);
  }

  // Composer sections (source: "composer") — read directly from
  // full.im_comments[<agentKey>].body. IMComposer.persist_all writes
  // one IMCompanyComment per section keyed by section_type, and the
  // /full/ endpoint exposes them as full.im_comments[<comment_type>].
  // This one branch covers every Phase-12 composer section, so we
  // don't need per-section paths for historik / marknad / etc.
  if (item.source === "composer" && item.agentKey) {
    const body =
      getNestedString(full, [
        `im_comments.${item.agentKey}.body`,
        `im_comments.${item.agentKey}.payload.body`,
      ]);
    if (body) return body;
  }

  if (item.agentKey === "web" && agentStatus?.web?.profile_exists) {
    // The web agent emits a structured im_business_story object — different
    // fields drive different prewritten cards. Pull the right one for each.
    const story =
      (full?.web_profile?.payload?.im_business_story) ||
      (full?.im_comments?.web_profile?.payload?.im_business_story) ||
      {};

    if (item.id === "summary") {
      // 3-paragraph IM-ready company description.
      const oneLiner = (story.one_liner || "").trim();
      if (oneLiner) return oneLiner;
    }

    if (item.id === "offering") {
      // Each array field typically carries one long prose paragraph from
      // the web agent (covering what/who/why respectively). Join with a
      // blank line between so the rendered text reads as 3 separate
      // paragraphs — the same shape as the broker-validated agent output.
      const lines = [
        ...(Array.isArray(story.what_they_sell) ? story.what_they_sell : []),
        ...(Array.isArray(story.who_buys) ? story.who_buys : []),
        ...(Array.isArray(story.why_they_buy) ? story.why_they_buy : []),
      ].map((s) => String(s || "").trim()).filter(Boolean);
      if (lines.length) return lines.join("\n\n");
    }

    // Legacy fallback paths from older payload shapes.
    return getNestedString(full, [
      "im_comments.web_profile.body",
      "im_comments.web_profile.payload.body",
      "web_profile.body",
      "web_profile.payload.body",
      "web_profile.profile.body",
      "web_profile.summary",
      "web_profile.profile.summary",
      "comments.web.body",
      "comments.web.payload.body",
      "agents.web.body",
      "agents.web.payload.body",
    ]);
  }

  return "";
}

function buildPrewrittenItemsFromBackend(full, agentStatus) {
  const pm = full?.pm || full?.im_document || {};
  const storedTitles = pm.titles && typeof pm.titles === "object" ? pm.titles : {};

  // Filter out optional composer sections that the composer chose not
  // to produce for this workspace. saas_metrics is shape-gated (only
  // recurring / hybrid revenue), strategic_buyer_rationale needs
  // identifiable strategic buyers, value_creation needs meaningful
  // uplift narrative. When their backing IMCompanyComment is absent
  // AND no persisted body exists, hide the sidebar row rather than
  // show a permanently-grey card the broker can't do anything with.
  const visibleDefs = PREWRITTEN_DEFS.filter((item) => {
    if (!item.optional || item.source !== "composer" || !item.agentKey) {
      return true;
    }
    const imComments = (full && full.im_comments) || {};
    const entry = imComments[item.agentKey];
    if (!entry) return false;
    const body =
      (entry && typeof entry.body === "string" && entry.body) ||
      (entry && entry.payload && typeof entry.payload.body === "string" && entry.payload.body) ||
      "";
    return !!body.trim();
  });

  return visibleDefs.map((item) => {
    const title =
      typeof storedTitles[item.id] === "string" ? storedTitles[item.id] : item.defaultTitle;

    const body = getPrewrittenBody(item, full, agentStatus);
    const hasBody = !!body.trim();

    let exists = hasBody;
    let canGenerate = false;
    let action = "check";

    if (item.agentKey === "financials") {
      canGenerate = !!agentStatus?.financials?.can_run;
      exists = !!agentStatus?.financials?.comment_exists || hasBody;
      action = exists ? "refresh" : canGenerate ? "generate" : "check";
    } else if (item.agentKey === "valuation") {
      canGenerate = !!agentStatus?.valuation?.can_run;
      exists = !!agentStatus?.valuation?.comment_exists || hasBody;
      action = exists ? "refresh" : canGenerate ? "generate" : "check";
    } else if (item.agentKey === "risks") {
      canGenerate = !!agentStatus?.risks?.can_run;
      exists = !!agentStatus?.risks?.comment_exists || hasBody;
      action = exists ? "refresh" : canGenerate ? "generate" : "check";
    } else if (item.agentKey === "web") {
      canGenerate = !!agentStatus?.web?.can_run;
      exists = !!agentStatus?.web?.profile_exists || hasBody;
      action = exists ? "refresh" : canGenerate ? "generate" : "check";
    } else if (item.agentKey === "overview") {
      canGenerate = !!agentStatus?.overview?.can_run;
      exists = !!agentStatus?.overview?.comment_exists || hasBody;
      action = exists ? "refresh" : canGenerate ? "generate" : "check";
    } else if (item.source === "composer") {
      // Composer sections aren't tracked by the legacy status endpoint;
      // they're written by IMComposer.persist_all when "Ladda in allt"
      // runs. When we have no body yet the section IS generatable — the
      // composer will produce one on the next run. Once persisted the
      // /full/ endpoint returns the body and hasBody flips true.
      canGenerate = !hasBody;
      exists = hasBody;
      action = exists ? "refresh" : "generate";
    } else {
      action = hasBody ? "refresh" : "check";
    }

    const whyDisabled = item.agentKey ? buildWhyDisabled(agentStatus, item.agentKey) : [];
    const gateFields = buildGateChecklist(item.agentKey, agentStatus);

    // HITL state — pulled from /im-studio/company/<orgnr>/full/'s
    // im_comments_meta block. The card uses this to render the Godkänn/
    // Skriv om/Versionshistorik affordances.
    const ct = commentTypeFor(item.agentKey);
    const meta = (ct && full?.im_comments_meta && full.im_comments_meta[ct]) || null;
    const status = meta?.status || (hasBody ? "draft" : null);
    const approvedAt = meta?.approved_at || null;
    const approvedByEmail = meta?.approved_by_email || null;
    const revisionCount = meta?.revision_count || 0;

    // financial_table carries structured table data on the item itself —
    // buildPrewrittenChildren reads it to insert a financialTable block.
    let tableData = null;
    let ownershipData = null;
    let chartData = null;
    if (item.id === "financial_table") {
      const years = full?.financials?.years || [];
      tableData = buildFinancialTableData(years);
      if (tableData.rows.length > 0) {
        exists = true;
        action = "refresh";
      }
    } else if (item.id === "ownership_table") {
      ownershipData = buildOwnershipTableData(full);
      if (ownershipData.rows.length > 0) {
        exists = true;
        action = "refresh";
      }
    } else if (item.id === "revenue_chart") {
      chartData = buildRevenueChartData(full);
      if (chartData.periods.length > 0) {
        exists = true;
        action = "refresh";
      }
    } else if (item.agentKey === "prognos") {
      canGenerate = !!agentStatus?.prognos?.can_run;
      exists = !!agentStatus?.prognos?.comment_exists || hasBody;
      action = exists ? "refresh" : canGenerate ? "generate" : "check";
    }

    return {
      id: `prewritten-${item.id}`,
      sectionId: item.id,
      label: item.label,
      heading: title,
      body,
      tableData,
      ownershipData,
      chartData,
      hasContent:
        hasBody ||
        (tableData && tableData.rows.length > 0) ||
        (ownershipData && ownershipData.rows.length > 0) ||
        (chartData && chartData.periods.length > 0),
      exists,
      canGenerate,
      action,
      agentKey: item.agentKey,
      source: item.source,
      whyDisabled,
      gateFields,
      stateClass: hasBody
        ? "is-ready"
        : canGenerate
        ? "is-generatable"
        : whyDisabled.length
        ? "is-blocked"
        : "is-empty",
      // HITL
      commentType: ct,
      hitlStatus: status,
      approvedAt,
      approvedByEmail,
      revisionCount,
      refinable: ct && ct !== "web_profile",
    };
  });
}

function buildPrewrittenSection(item) {
  // Every section produced from a composer/data source is tagged
  // ``origin: "composer"`` so ``handleLoadAll`` can strip and
  // regenerate them freely without touching broker-authored blocks.
  return createSectionBlock("textSection", {
    label: item.label,
    documentSectionId: item.sectionId || item.id,
    origin: "composer",
    children: buildPrewrittenChildren(item),
  });
}

// Parse the composer's HTML body into an ordered sequence of block
// specs. IMComposer emits ``<h3>Heading</h3>`` for subheadings and
// ``<p>…</p>`` for paragraphs (plus the occasional ``<table>`` which
// we currently flatten to plain-text rows). PM Studio's text block
// treats its ``text`` prop as literal characters, so any remaining
// HTML would render as visible tags. Splitting into typed blocks
// per element gives the canvas a proper structural rendering that
// matches the bash-generated PDF output.
function _parseComposerHtml(html) {
  if (!html || typeof html !== "string") return [];

  // Decode entities FIRST. If transit middleware double-encodes the body
  // (`&lt;p&gt;…&lt;/p&gt;`), the block-level regex below misses it, the
  // fallback decodes `&lt;` into `<` AFTER its tag-strip pass, and the
  // literal "<p>" survives into the text block the broker sees. Decoding
  // up-front lets both the regex and stripInline operate on real tags.
  const normalized = String(html)
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");

  const stripInline = (s) =>
    String(s || "")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/?(strong|em|b|i|u|span)[^>]*>/gi, "")
      .replace(/<[^>]+>/g, "")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

  const blocks = [];
  const re = /<(h1|h2|h3|h4|p|li|ul|ol|table)([^>]*)>([\s\S]*?)<\/\1>/gi;
  let m;
  let matched = false;
  while ((m = re.exec(normalized)) !== null) {
    matched = true;
    const tag = m[1].toLowerCase();
    const inner = m[3];
    if (tag === "h1" || tag === "h2" || tag === "h3" || tag === "h4") {
      const text = stripInline(inner);
      if (text) blocks.push({ kind: "subheading", text });
    } else if (tag === "p") {
      const text = stripInline(inner);
      if (text) blocks.push({ kind: "paragraph", text });
    } else if (tag === "ul" || tag === "ol") {
      // Best-effort: pull <li> items out and render each on its own
      // line prefixed with a bullet. Preserves list structure without
      // needing a dedicated list block-type on the canvas.
      const items = [];
      const liRe = /<li[^>]*>([\s\S]*?)<\/li>/gi;
      let li;
      while ((li = liRe.exec(inner)) !== null) {
        const t = stripInline(li[1]);
        if (t) items.push(`• ${t}`);
      }
      if (items.length) blocks.push({ kind: "paragraph", text: items.join("\n") });
    } else if (tag === "table") {
      // Tables in composer output are rare — flatten cells to
      // whitespace-separated rows so nothing is lost. A real table
      // block-type could replace this later.
      const rowRe = /<tr[^>]*>([\s\S]*?)<\/tr>/gi;
      const cellRe = /<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi;
      const rows = [];
      let tr;
      while ((tr = rowRe.exec(inner)) !== null) {
        const cells = [];
        let td;
        while ((td = cellRe.exec(tr[1])) !== null) cells.push(stripInline(td[1]));
        if (cells.length) rows.push(cells.join("  ·  "));
      }
      if (rows.length) blocks.push({ kind: "paragraph", text: rows.join("\n") });
    }
  }
  // Fallback for bodies that don't wrap content in block-level tags.
  if (!matched) {
    const text = stripInline(normalized);
    if (text) blocks.push({ kind: "paragraph", text });
  }
  return blocks;
}

// Round-trip helpers for the broker-edit modal. The IM comment body is
// stored as light HTML (<p>, <h3>, etc.) — the modal exposes a plain
// textarea, so we flatten on open and re-wrap on save. "## " is used as
// the sentinel for subheadings so the broker can keep structure without
// learning HTML.
function _bodyToPlainText(html) {
  const blocks = _parseComposerHtml(html || "");
  if (!blocks.length) return "";
  return blocks
    .map((b) => (b.kind === "subheading" ? `## ${b.text}` : b.text))
    .join("\n\n");
}

function _plainTextToBody(text) {
  const esc = (s) =>
    String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  const trimmed = (text || "").trim();
  if (!trimmed) return "";
  return trimmed
    .split(/\n{2,}/)
    .map((para) => {
      const t = para.trim();
      if (!t) return "";
      if (t.startsWith("## ")) return `<h3>${esc(t.slice(3))}</h3>`;
      return `<p>${esc(t).replace(/\n/g, "<br />")}</p>`;
    })
    .filter(Boolean)
    .join("");
}

function buildPrewrittenChildren(item) {
  const blocks = [];

  if (item.heading?.trim()) {
    blocks.push(
      createContentBlock("title", {
        text: item.heading,
        level: "h2",
      })
    );
  }

  // Data-only items ship a structured payload on the item instead of a
  // text body — each maps to its own dedicated content-block kind.
  if (item.sectionId === "financial_table" && item.tableData) {
    blocks.push(
      createContentBlock("financialTable", {
        headers: item.tableData.headers,
        rows: item.tableData.rows,
        footer: item.tableData.footer || null,
      })
    );
    return blocks;
  }

  if (item.sectionId === "ownership_table" && item.ownershipData) {
    blocks.push(
      createContentBlock("ownershipTable", {
        rows: item.ownershipData.rows,
        shareholders: item.ownershipData.shareholders || [],
      })
    );
    return blocks;
  }

  if (item.sectionId === "revenue_chart" && item.chartData) {
    blocks.push(
      createContentBlock("revenueChart", {
        periods: item.chartData.periods,
      })
    );
    return blocks;
  }

  if (item.body?.trim()) {
    const parsed = _parseComposerHtml(item.body);
    if (parsed.length) {
      for (const p of parsed) {
        if (p.kind === "subheading") {
          blocks.push(
            createContentBlock("title", {
              text: p.text,
              level: "h3",
            })
          );
        } else {
          blocks.push(
            createContentBlock("text", {
              text: p.text,
            })
          );
        }
      }
    } else {
      // Empty parse — fall back to raw body so we never silently
      // drop content the parser didn't recognise.
      blocks.push(
        createContentBlock("text", {
          text: item.body,
        })
      );
    }
  }

  return blocks;
}


function canAcceptPrewrittenChildren(section) {
  if (!section) return false;
  return (
    section.type === "textSection" ||
    section.type === "hero" ||
    section.type === "ctaSection"
  );
}

// ── Teaser canvas ──────────────────────────────────────────────────────
// Renders the anonymised teaser body inside the same preview-page shell
// the IM canvas uses so the switch between modes doesn't flash a
// differently-sized canvas. The body is parsed via the existing
// _parseComposerHtml helper so paragraphs and subheadings map to real
// React elements — no dangerouslySetInnerHTML.
// Serialize the composer's HTML (or a contentEditable div's DOM
// output) down to a clean subset the teaser body actually uses:
// ``<p>`` for paragraphs, ``<h3>`` for subheadings. Any other block
// tag is coerced to ``<p>``. Text content is HTML-escaped. Empty
// blocks are dropped. Keeps the round-trip through user edits
// bounded — browsers otherwise sprinkle ``<div>``, ``<br>``, inline
// styles etc. into contentEditable output.
function _escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
function _serializeTeaserHtml(rawHtml) {
  if (typeof document === "undefined") return String(rawHtml || "");
  const div = document.createElement("div");
  div.innerHTML = String(rawHtml || "");
  const out = [];
  // Top-level block walk. If someone edits and produces bare text
  // nodes (no wrapping <p>), we normalize each newline-delimited run
  // into its own paragraph.
  const push = (tag, text) => {
    const clean = String(text || "").trim();
    if (!clean) return;
    if (tag === "h3" || tag === "h4") {
      out.push(`<h3>${_escapeHtml(clean)}</h3>`);
    } else {
      // Preserve intra-paragraph newlines as <br> so pressing enter
      // inside a paragraph doesn't silently collapse.
      const html = _escapeHtml(clean).replace(/\n/g, "<br>");
      out.push(`<p>${html}</p>`);
    }
  };
  const walk = (node) => {
    for (const child of node.childNodes) {
      if (child.nodeType === 3) {
        // Text node at the top level → wrap as paragraph.
        push("p", child.textContent);
        continue;
      }
      if (child.nodeType !== 1) continue;
      const tag = child.tagName.toLowerCase();
      if (tag === "h1" || tag === "h2" || tag === "h3" || tag === "h4") {
        // Broker edits collapse every header level into <h3>. The
        // sanitizer keeps <h1> for buyer-facing render, but the
        // editor's own model has one heading level; preserving a
        // distinct h1 through contentEditable is not worth the
        // round-trip complexity.
        push("h3", child.textContent);
      } else if (tag === "p" || tag === "div") {
        push("p", child.textContent);
      } else if (tag === "br") {
        // Standalone <br> between blocks — ignore.
      } else {
        // Unknown block: recurse to catch nested paragraphs.
        walk(child);
      }
    }
  };
  walk(div);
  return out.join("");
}

// Convert composer HTML to a clean initial-innerHTML string for the
// contentEditable div — same subset as _serializeTeaserHtml so the
// broker sees exactly what will be re-serialized on save.
function _teaserInitialHtml(teaserBody) {
  const blocks = _parseComposerHtml(teaserBody || "");
  if (!blocks.length) return "";
  return blocks
    .map((b) => {
      const text = _escapeHtml(String(b.text || "")).replace(/\n/g, "<br>");
      if (b.kind === "subheading") return `<h3>${text}</h3>`;
      return `<p>${text}</p>`;
    })
    .join("");
}

function TeaserCanvas({
  teaserBody,
  teaserDraftId,
  editable,
  onBodyChange,
  label,
  headerAction,
  emptyMessage = 'Klicka "Generera teaser" för att skapa ett anonymiserat utkast.',
  alwaysEditable = false,
  pageWidth = PAGE_WIDTH,
  placeholderText = "",
}) {
  const editorRef = React.useRef(null);
  const initialHtml = React.useMemo(
    () => _teaserInitialHtml(teaserBody),
    // Reset on regenerate (new draftId) or when the incoming body
    // string changes because the server sent an updated one. Local
    // edits between saves don't blow away the DOM because React
    // never re-runs dangerouslySetInnerHTML for an unchanged key.
    [teaserBody, teaserDraftId],
  );

  const handleBlur = React.useCallback(() => {
    if (!editable || !onBodyChange || !editorRef.current) return;
    const raw = editorRef.current.innerHTML || "";
    const clean = _serializeTeaserHtml(raw);
    if (clean !== teaserBody) {
      onBodyChange(clean);
    }
  }, [editable, onBodyChange, teaserBody]);

  // When `alwaysEditable` is set (user scratchpad), render the editor
  // even on an empty body so the broker can start typing immediately.
  // The AI side keeps its "click Generate" placeholder so the empty
  // state tells the broker what to do, not an empty rectangle.
  const showEditor = alwaysEditable || initialHtml !== "";
  const placeholderAttr = placeholderText ? { "data-placeholder": placeholderText } : {};

  return (
    <div
      className="imstudio-editor__preview-page"
      style={{
        maxWidth: pageWidth,
        width: "100%",
        position: "relative",
        margin: "0 auto",
        background: "#fff",
        minHeight: PAGE_HEIGHT_PX,
        padding: "48px 40px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {(label || headerAction) ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
            gap: 8,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#57534E",
              textTransform: "uppercase",
              letterSpacing: 0.6,
            }}
          >
            {label}
          </div>
          {headerAction}
        </div>
      ) : null}

      {showEditor ? (
        <>
          <style>{`
            .imstudio-teaser-editor[data-placeholder]:empty::before {
              content: attr(data-placeholder);
              color: #A8A29E;
              font-style: italic;
              pointer-events: none;
            }
          `}</style>
          <div
            key={teaserDraftId || "no-draft"}
            ref={editorRef}
            className="imstudio-teaser-editor"
            contentEditable={!!editable}
            suppressContentEditableWarning
            spellCheck={editable ? "true" : "false"}
            onBlur={handleBlur}
            dangerouslySetInnerHTML={{ __html: initialHtml }}
            {...placeholderAttr}
            style={{
              fontSize: 14,
              lineHeight: 1.6,
              color: "#292524",
              outline: "none",
              minHeight: 200,
              flex: 1,
            }}
          />
        </>
      ) : (
        <p
          style={{
            color: "#78716C",
            fontStyle: "italic",
            fontSize: 14,
            textAlign: "center",
            marginTop: 48,
          }}
        >
          {emptyMessage}
        </p>
      )}
    </div>
  );
}

// ── Teaser sidebar ────────────────────────────────────────────────────
// Single-card panel that replaces the Prewritten/Blocks/Brand tab-stack
// when PM Studio is in teaser mode. Shows a generate button, then a
// review/publish button once the backend returns a draft body, then the
// public URL block once the draft has been approved.
function TeaserSidebar({
  slug,
  teaserDraftId,
  teaserTemplateId,
  onSelectTemplate,
  teaserBody,
  teaserAccessToken,
  teaserLoading,
  teaserError,
  onGenerate,
  onApprove,
  onDownloadPdf,
}) {
  const [copied, setCopied] = React.useState(false);
  const [templates, setTemplates] = React.useState([]);
  const [templateName, setTemplateName] = React.useState("");
  const [templateFont, setTemplateFont] = React.useState("Helvetica");
  const [templateAccent, setTemplateAccent] = React.useState("#24415b");
  const [savingTemplate, setSavingTemplate] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    if (!slug) return undefined;
    apiFetch(`/ai/teasers/templates/${encodeURIComponent(slug)}/`)
      .then((res) => { if (alive) setTemplates(res?.templates || []); })
      .catch(() => { if (alive) setTemplates([]); });
    return () => { alive = false; };
  }, [slug]);

  async function createPresentationTemplate() {
    const name = templateName.trim();
    if (!name || savingTemplate) return;
    setSavingTemplate(true);
    try {
      const created = await apiFetch(`/ai/teasers/templates/${encodeURIComponent(slug)}/`, {
        method: "POST",
        body: { name, presentation: { font_family: templateFont, accent_color: templateAccent } },
      });
      setTemplates((current) => [...current.filter((row) => row.id !== created.id), created]);
      setTemplateName("");
      await onSelectTemplate?.(String(created.id));
    } catch (error) {
      window.alert(error?.message || "Kunde inte skapa presentation.");
    } finally {
      setSavingTemplate(false);
    }
  }

  // Build the public teaser URL against the current window origin so this
  // component works in both local dev and prod without hard-coding hosts.
  const publicUrl = teaserAccessToken
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/teasers/${teaserAccessToken}/`
    : "";

  async function handleCopy() {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // clipboard access may be blocked (insecure origin) — no UI beyond
      // the button, silently no-op so we don't spam alerts.
    }
  }

  return (
    <div className="imstudio-editor__tab-pane">
      <div
        style={{
          padding: 16,
          border: "1px solid #E7E5E4",
          borderRadius: 12,
          background: "#FFFFFF",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: "#1C1917", marginBottom: 4 }}>
          Teaser
        </div>
        <div style={{ fontSize: 12, color: "#78716C", marginBottom: 16, lineHeight: 1.5 }}>
          Anonymiserat utkast för köparkontakt före NDA.
        </div>

        <label style={{ display: "block", fontSize: 12, color: "#57534E", marginBottom: 12 }}>
          Presentation
          <select
            value={teaserTemplateId || ""}
            onChange={(event) => onSelectTemplate?.(event.target.value || null)}
            disabled={!teaserDraftId || !!teaserAccessToken || teaserLoading}
            style={{ display: "block", width: "100%", marginTop: 5, padding: 7, border: "1px solid #E7E5E4", borderRadius: 6, background: "#fff" }}
          >
            <option value="">Standard</option>
            {templates.map((template) => <option key={template.id} value={String(template.id)}>{template.name}</option>)}
          </select>
        </label>
        {!teaserAccessToken && (
          <div style={{ marginBottom: 16, padding: 10, background: "#F9FAFB", border: "1px solid #E7E5E4", borderRadius: 7 }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Ny mäklarpresentation</div>
            <input
              value={templateName}
              onChange={(event) => setTemplateName(event.target.value)}
              placeholder="Mallnamn"
              style={{ width: "100%", boxSizing: "border-box", padding: 6, border: "1px solid #E7E5E4", borderRadius: 5, marginBottom: 6 }}
            />
            <div style={{ display: "flex", gap: 6, marginBottom: 7 }}>
              <select value={templateFont} onChange={(event) => setTemplateFont(event.target.value)} style={{ flex: 1, padding: 5 }}>
                {["Helvetica", "Arial", "Georgia", "Times New Roman"].map((font) => <option key={font}>{font}</option>)}
              </select>
              <input type="color" value={templateAccent} onChange={(event) => setTemplateAccent(event.target.value)} aria-label="Accentfärg" />
            </div>
            <button type="button" disabled={!templateName.trim() || savingTemplate} onClick={createPresentationTemplate} style={{ padding: "5px 9px" }}>
              {savingTemplate ? "Sparar…" : "Skapa och använd"}
            </button>
          </div>
        )}

        {/* Generate button — primary, dark. Spinner + "Genererar…" while
            teaserLoading so brokers get feedback during the ~15-30s LLM
            round-trip. */}
        <button
          type="button"
          onClick={onGenerate}
          disabled={teaserLoading}
          style={{
            width: "100%",
            padding: "10px 16px",
            borderRadius: 8,
            border: "none",
            background: "#1C1917",
            color: "#F5F5F4",
            fontSize: 13,
            fontWeight: 600,
            cursor: teaserLoading ? "progress" : "pointer",
            opacity: teaserLoading ? 0.75 : 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {teaserLoading ? (
            <>
              <span
                aria-hidden="true"
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  border: "2px solid currentColor",
                  borderTopColor: "transparent",
                  borderRadius: "50%",
                  animation: "imstudio-spin 0.9s linear infinite",
                }}
              />
              <span>Genererar…</span>
            </>
          ) : (
            teaserBody ? "Regenerera teaser" : "Generera teaser"
          )}
        </button>

        {/* Approve button — secondary style. Only surfaced once a body
            exists so the sidebar doesn't offer to publish nothing. */}
        {teaserBody ? (
          <button
            type="button"
            onClick={onApprove}
            disabled={teaserLoading}
            style={{
              width: "100%",
              marginTop: 10,
              padding: "10px 16px",
              borderRadius: 8,
              border: "1px solid #E7E5E4",
              background: "#F5F5F4",
              color: "#1C1917",
              fontSize: 13,
              fontWeight: 600,
              cursor: teaserLoading ? "progress" : "pointer",
              opacity: teaserLoading ? 0.6 : 1,
            }}
          >
            Skicka till granskning
          </button>
        ) : null}

        {/* Public URL block — visible once the draft has an access_token,
            which happens after approval. Small inline block, no modal. */}
        {teaserAccessToken ? (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              background: "#F5F5F4",
              border: "1px solid #E7E5E4",
              borderRadius: 8,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: "#57534E", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
              Publik URL
            </div>
            <div
              style={{
                fontSize: 12,
                color: "#292524",
                wordBreak: "break-all",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                marginBottom: 8,
              }}
            >
              {publicUrl}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={handleCopy}
                style={{
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "1px solid #E7E5E4",
                  background: "#FFFFFF",
                  color: "#1C1917",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {copied ? "Kopierad ✓" : "Kopiera länk"}
              </button>
              {onDownloadPdf ? (
                <button
                  type="button"
                  onClick={onDownloadPdf}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 6,
                    border: "1px solid #E7E5E4",
                    background: "#FFFFFF",
                    color: "#1C1917",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Ladda ner PDF
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {teaserError ? (
          <div
            style={{
              marginTop: 12,
              fontSize: 12,
              color: "#b91c1c",
              lineHeight: 1.4,
            }}
            role="alert"
          >
            {teaserError}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function PdfEditorPage() {
  const { company: slugFromParams, slug: slugAlt } = useParams();
  const slug = slugFromParams || slugAlt || "";

  // Persist editor state per workspace so navigating away and back doesn't
  // force the broker to redo the wizard. We hydrate synchronously from
  // localStorage before the first render so there's no flash of the
  // wizard for users who've already passed it.
  const storageKey = slug ? `im-studio:${slug}:state` : null;
  const initialPersisted = React.useMemo(() => {
    if (!storageKey) return null;
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [storageKey]);

  const [logoUrl, setLogoUrl] = useState(() => initialPersisted?.logoUrl || PRELOADED_LOGO);
  const [title, setTitle] = useState(() => initialPersisted?.title || "One-page editor");
  const [sections, setSections] = useState(() => Array.isArray(initialPersisted?.sections) ? initialPersisted.sections : []);
  const [selectedId, setSelectedId] = useState(null);
  const [exporting, setExporting] = useState(false);
  // "Ladda in allt" — batch state. loadAllProgress carries typing-mode
  // status ({current, total}) so the button label can show "Skapar N av
  // M…" during the progressive insertion. loadAllError is shown inline
  // next to
  // the button so partial failures are visible without a modal.
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadAllError, setLoadAllError] = useState("");
  const [loadAllProgress, setLoadAllProgress] = useState(null);
  // Bottom-right status pill shown while "Ladda in allt" streams
  // sections in. ``phase`` drives which message renders:
  //   working  → cycles through "Skriver…", "Funderar…" etc.
  //   done     → static "Klar" for a moment, then fades
  //   null     → hidden
  const [loadAllStatus, setLoadAllStatus] = useState(null); // {phase, message}
  const loadAllStatusTimer = useRef(null);
  const [activeTab, setActiveTab] = useState("prewritten");
  const [prewrittenItems, setPrewrittenItems] = useState([]);
  // Faktagranskning report — null = not yet loaded / no report. Hidden
  // entirely when issues is empty. Hydrated from /full/ on page load and
  // refreshed from runAllAgents' response after generation.
  const [consistency, setConsistency] = useState(null);

  // Which prewritten card is expanded — null = all collapsed. Default
  // "overview" (Bolagsöversikt) so the broker lands on the most-used
  // section pre-opened. Click a header to swap which one is open.
  const [expandedPrewrittenId, setExpandedPrewrittenId] = useState("overview");
  const [prewrittenLoading, setPrewrittenLoading] = useState(false);
  const [prewrittenError, setPrewrittenError] = useState("");
  const [activeDrag, setActiveDrag] = useState(null);
  const [agentStatus, setAgentStatus] = useState(null);

  // ── Teaser mode (Shape B) ──
  // pmMode toggles the PM Studio between the full IM editor ("im") and the
  // lightweight anonymised Teaser workflow ("teaser"). Switching modes is
  // purely a render-branch — the IM sections state stays intact so brokers
  // can flip back to the IM canvas without losing work. Mode is persisted
  // per-workspace in sessionStorage so intra-session navigation preserves it.
  const pmModeStorageKey = slug ? `pmstudio-mode-${slug}` : null;
  const [pmMode, setPmMode] = useState(() => {
    if (!pmModeStorageKey) return "im";
    try {
      const stored = window.sessionStorage.getItem(pmModeStorageKey);
      return stored === "teaser" ? "teaser" : "im";
    } catch {
      return "im";
    }
  }); // "im" | "teaser"
  const [teaserDraftId, setTeaserDraftId] = useState(null);
  const [teaserTemplateId, setTeaserTemplateId] = useState(null);
  const [teaserBody, setTeaserBody] = useState("");  // HTML string from backend
  const [teaserAccessToken, setTeaserAccessToken] = useState(null);
  const [teaserLoading, setTeaserLoading] = useState(false);
  const [teaserError, setTeaserError] = useState("");

  // ── First-run wizard ──
  // If we already have persisted sections for this workspace the broker has
  // been here before — skip straight into the editor.
  const hadPriorState = Array.isArray(initialPersisted?.sections) && initialPersisted.sections.length > 0;
  const [showWizard, setShowWizard] = useState(() => !hadPriorState);
  const [wizardStep, setWizardStep] = useState(1); // 1=logo, 2=template, 3=done
  const [wizardLogo, setWizardLogo] = useState(() => initialPersisted?.wizardLogo || null);
  const [wizardSelectedTemplate, setWizardSelectedTemplate] = useState(() => initialPersisted?.wizardSelectedTemplate || null);
  const [presentationTemplate, setPresentationTemplate] = useState(() => initialPersisted?.presentationTemplate || null);
  const [presentationProfile, setPresentationProfile] = useState(null);
  const [presentationProfileDraft, setPresentationProfileDraft] = useState(null);
  const [presentationProfileSaving, setPresentationProfileSaving] = useState(false);
  const [presentationProfileError, setPresentationProfileError] = useState("");
  const wizardLogoInputRef = useRef(null);
  const [prewrittenProgress, setPrewrittenProgress] = useState({});
  const [workingPrewrittenId, setWorkingPrewrittenId] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState("");
  const [saving, setSaving] = useState(false);

  const fileInputRef = useRef(null);
  const previewRef = useRef(null);
  const presentationExportVersionRef = useRef(null);
  // Ref med senast hämtade bolagsnamnet — används som fallback i
  // handleWizardComplete så cover-titeln blir rätt även när `title`-
  // state-uppdateringen från fetchIMFull inte hunnit committas än.
  const companyNameRef = useRef("");

  // ── Word-style pagination ─────────────────────────────────────────
  // Sections render in a single SortableContext for dnd-kit but are
  // visually grouped into A4-sized "pages". After each render we measure
  // each section's height and compute which section indices start a new
  // page (`pageBreaks`). Sections never split across pages — if a section
  // wouldn't fit on remaining page-budget, it starts on the next page,
  // leaving whitespace at the bottom of the prior page like a real PDF.
  const sectionDomRefs = useRef({});
  const [pageBreaks, setPageBreaks] = useState([0]);

  // Cumulative page numbering. Each section's measured height is divided
  // by PAGE_HEIGHT_PX to compute how many A4 pages that section occupies.
  // basePages[i] = total pages used by all sections BEFORE i, plus 1 — so
  // section i's content starts on page basePages[i]. The between-section
  // divider labels and PaginatedChildren's internal "Sida N" labels both
  // key off this array, so overflowing sections no longer cause the
  // numbering to drift from reality.
  const [sectionPageCounts, setSectionPageCounts] = useState({});
  const sectionIdsKey = React.useMemo(
    () => sections.map((s) => s.id).join(","),
    [sections],
  );
  useEffect(() => {
    const observers = [];
    const ids = sectionIdsKey ? sectionIdsKey.split(",") : [];
    for (const sectionId of ids) {
      const el = sectionDomRefs.current[sectionId];
      if (!el) continue;
      const update = () => {
        const h = el.offsetHeight;
        const pages = Math.max(1, Math.ceil(h / PAGE_HEIGHT_PX));
        setSectionPageCounts((prev) => {
          if (prev[sectionId] === pages) return prev;
          return { ...prev, [sectionId]: pages };
        });
      };
      update();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      observers.push(ro);
    }
    return () => observers.forEach((ro) => ro.disconnect());
  }, [sectionIdsKey]);
  const basePages = React.useMemo(() => {
    const arr = [];
    let page = 1;
    for (const s of sections) {
      arr.push(page);
      page += sectionPageCounts[s.id] || 1;
    }
    arr.push(page);
    return arr;
  }, [sections, sectionPageCounts]);

  const recomputePageBreaks = useCallback(() => {
    if (sections.length === 0) {
      setPageBreaks((prev) => (prev.length === 1 && prev[0] === 0 ? prev : [0]));
      return;
    }
    // One section per A4 page. Decorative sections (divider/spacer) don't
    // warrant their own page — they share with the section above them.
    // Cover-section is always section 0 and starts page 1 implicitly.
    const NON_PAGINATING_TYPES = new Set(["dividerSection", "spacerSection"]);
    const breaks = [0];
    sections.forEach((s, idx) => {
      if (idx === 0) return;
      if (NON_PAGINATING_TYPES.has(s.type)) return;
      breaks.push(idx);
    });
    setPageBreaks((prev) => {
      if (prev.length === breaks.length && prev.every((v, i) => v === breaks[i])) {
        return prev;
      }
      return breaks;
    });
  }, [sections]);

  // Observe section size changes — section heights change when the user
  // edits text inline, drops a new block, etc. ResizeObserver triggers a
  // pagination recompute so page wrappers stay accurate.
  useEffect(() => {
    recomputePageBreaks();
    // Andra mätningen efter ett par animation-frames — fångar fonter/
    // bilder som laddar lite efter första paint och som annars lämnade
    // pageBreaks=[0] för mallar där cover-sektionen mättes innan dess
    // minHeight hade applicerats.
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => recomputePageBreaks());
    });
    const observer = new ResizeObserver(() => recomputePageBreaks());
    Object.values(sectionDomRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => {
      cancelAnimationFrame(raf1);
      observer.disconnect();
    };
  }, [sections, recomputePageBreaks]);

  // Toggle that hides all editor chrome (hover toolbars, +-zones, drag
  // handles, selection borders) so the broker can preview the PM exactly
  // as it will look when exported. Driven by a single CSS class on the
  // preview wrapper.
  const [previewMode, setPreviewMode] = useState(false);

  // PointerSensor handles mouse+touch+pen with one set of handlers, BUT it
  // depends on the dragged element having `touch-action: none` so the
  // browser doesn't claim the gesture as scroll. A larger activation
  // distance also makes drags feel sluggish — 4px feels right.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const selectedSection = useMemo(
    () => sections.find((section) => section.id === selectedId) || null,
    [sections, selectedId]
  );
  const profilePresentation = useMemo(
    () => profilePresentationView(presentationProfileDraft, presentationTemplate),
    [presentationProfileDraft, presentationTemplate],
  );

  useEffect(() => {
    let alive = true;

    async function loadPrewritten() {
      if (!slug) return;

      try {
        setPrewrittenLoading(true);
        setPrewrittenError("");

        const [full, status, brokerProfile] = await Promise.all([
          fetchIMFull(slug),
          fetchAgentStatus(slug).catch(() => null),
          fetchPresentationProfile(slug).catch(() => null),
        ]);

        if (!alive) return;

        const companyData = full.company || full.organisation || {};
        const companyName = companyData?.name || companyData?.namn || "Investment memorandum";
        const pm = full?.pm || full?.im_document || {};
        if (brokerProfile?.configuration) {
          setPresentationProfile(brokerProfile);
          setPresentationProfileDraft(JSON.parse(JSON.stringify(brokerProfile.configuration)));
          if (brokerProfile.assets?.logo) setLogoUrl(brokerProfile.assets.logo);
          // The default or existing broker profile carries forward. Template
          // selection remains available as an explicit profile change, but
          // is not required for each new engagement.
          setShowWizard(false);
        }
        const storedImages = pm.images && typeof pm.images === "object" ? pm.images : {};
        const selectedPresentation = pm.selected_presentation || {};
        if (selectedPresentation.source && selectedPresentation.id != null) {
          setWizardSelectedTemplate(`${selectedPresentation.source}:${selectedPresentation.id}`);
          setPresentationTemplate(selectedPresentation);
        }

        if (companyName && title === "One-page editor") {
          setTitle(companyName);
        }
        // Spara även i ref så handleWizardComplete kan plocka rätt namn
        // även om setTitle inte hunnit committas innan användaren klickar
        // klar i mall-wizarden.
        if (companyName && companyName !== "Investment memorandum") {
          companyNameRef.current = companyName;
        }

        // Logo can come from two sources:
        //  - pm.images.logo: previously uploaded through the IM wizard
        //  - full.broker_logo_url: workspace's owner-company logo (set via
        //    workspace settings / earlier IM uploads that persisted upward)
        // If either exists we skip the wizard's logo step — the broker has
        // already answered the question.
        const existingLogo =
          (storedImages.logo && typeof storedImages.logo === "string" && storedImages.logo) ||
          (typeof full?.broker_logo_url === "string" && full.broker_logo_url) ||
          "";
        if (existingLogo) {
          setLogoUrl(existingLogo);
          setWizardLogo(existingLogo);
          setWizardStep((prev) => (prev < 2 ? 2 : prev));
        }

        setAgentStatus(status);
        setPrewrittenItems(buildPrewrittenItemsFromBackend(full, status));
      } catch (error) {
        if (!alive) return;
        setPrewrittenError(error?.message || "Could not load prewritten text.");
      } finally {
        if (alive) setPrewrittenLoading(false);
      }
    }

    loadPrewritten();

    return () => {
      alive = false;
    };
  }, [slug, title]);

  // Load saved templates from backend — newest first so brokers see their
  // most-recently-saved layout at the top of the picker.
  useEffect(() => {
    if (!slug) return;
    fetchTemplates(slug)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        list.sort((a, b) => {
          const ta = a?.created_at || a?.updated_at || "";
          const tb = b?.created_at || b?.updated_at || "";
          return tb.localeCompare(ta);
        });
        setSavedTemplates(list);
        setPresentationTemplate((current) => {
          const ref = current?.source && current?.id != null ? current : null;
          if (!ref) return current;
          return list.find((tpl) => (tpl.is_global ? "global" : "broker") === ref.source && String(tpl.id) === String(ref.id)) || current;
        });
      })
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!presentationTemplate?.source || presentationTemplate?.id == null) return;
    const match = savedTemplates.find((tpl) =>
      (tpl.is_global ? "global" : "broker") === presentationTemplate.source &&
      String(tpl.id) === String(presentationTemplate.id));
    if (match && match !== presentationTemplate) setPresentationTemplate(match);
  }, [presentationTemplate, savedTemplates]);

  // ── Teaser hydration ──
  // Whenever the workspace slug changes, ask the backend for the latest
  // non-rejected TeaserDraft. 404 means "no draft yet" — leave state empty
  // so the broker starts in button-only mode. Any other error surfaces in
  // teaserError so the sidebar can render it inline.
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    setTeaserError("");
    apiFetch(`/ai/teasers/current/${encodeURIComponent(slug)}/`)
      .then((res) => {
        if (!alive) return;
        setTeaserDraftId(res?.id || null);
        setTeaserTemplateId(res?.presentation_template_id || null);
        setTeaserBody(res?.body || "");
        setTeaserAccessToken(res?.access_token || null);
      })
      .catch((err) => {
        if (!alive) return;
        // 404 = "no draft yet" — swallow silently so the sidebar stays
        // in button-only mode. apiFetch attaches .status to the Error so
        // we don't have to parse the Swedish `detail` string.
        if (err?.status === 404) {
          setTeaserDraftId(null);
          setTeaserTemplateId(null);
          setTeaserBody("");
          setTeaserAccessToken(null);
          return;
        }
        setTeaserError(err?.message || "Kunde inte ladda teaser.");
      });
    return () => { alive = false; };
  }, [slug]);

  // Persist pmMode per workspace so switching pages within a session keeps
  // whichever mode (IM or Teaser) the broker last opened. Cleared implicitly
  // by sessionStorage lifetime — new browser session starts back on IM.
  useEffect(() => {
    if (!pmModeStorageKey) return;
    try {
      window.sessionStorage.setItem(pmModeStorageKey, pmMode);
    } catch {
      // sessionStorage may be unavailable (privacy modes) — silently skip.
    }
  }, [pmModeStorageKey, pmMode]);

  // ── Teaser action handlers ──
  async function handleGenerateTeaser() {
    if (teaserLoading) return;
    setTeaserLoading(true);
    setTeaserError("");
    try {
      const res = await apiFetch(`/ai/teasers/compose/${encodeURIComponent(slug)}/`, {
        method: "POST",
        body: {},
      });
      setTeaserDraftId(res.id);
      setTeaserTemplateId(res.presentation_template_id || null);
      setTeaserBody(res.body || "");
      setTeaserAccessToken(res.access_token || null);
    } catch (err) {
      setTeaserError(err?.message || "Kunde inte generera teaser.");
    } finally {
      setTeaserLoading(false);
    }
  }

  async function handleSelectTeaserTemplate(templateId) {
    if (!teaserDraftId) return;
    setTeaserLoading(true);
    setTeaserError("");
    try {
      const result = await apiFetch(`/ai/teasers/${teaserDraftId}/`, {
        method: "PATCH",
        body: { action: "select_template", template_id: templateId },
      });
      setTeaserTemplateId(result.presentation_template_id || null);
    } catch (err) {
      setTeaserError(err?.message || "Kunde inte byta presentation.");
    } finally {
      setTeaserLoading(false);
    }
  }

  // Broker canvas edits — save through a short debounce so we don't
  // fire one PATCH per keystroke. The contentEditable in TeaserCanvas
  // calls onBodyChange on blur, and this handler also debounces to
  // survive rapid focus-out/focus-in on the same paragraph.
  //
  // ``teaserPendingBodyRef`` mirrors the most recent body the user
  // wrote and gets flushed synchronously by ``flushTeaserSave``
  // before ``handleApproveTeaser`` runs — that closes the "type →
  // approve within 600 ms → stale body gets frozen" race the audit
  // flagged.
  const teaserSaveTimerRef = useRef(null);
  const teaserPendingBodyRef = useRef(null);

  const flushTeaserSave = useCallback(async () => {
    if (teaserSaveTimerRef.current) {
      clearTimeout(teaserSaveTimerRef.current);
      teaserSaveTimerRef.current = null;
    }
    const pending = teaserPendingBodyRef.current;
    teaserPendingBodyRef.current = null;
    if (!pending || !teaserDraftId) return;
    try {
      await apiFetch(`/ai/teasers/${teaserDraftId}/`, {
        method: "PATCH",
        body: { body: pending },
      });
    } catch (err) {
      // Re-queue the body so we don't silently lose it. Caller can
      // catch and decide (approve aborts, edits keep trying).
      teaserPendingBodyRef.current = pending;
      throw err;
    }
  }, [teaserDraftId]);

  const handleTeaserBodyChange = useCallback((newBody) => {
    setTeaserBody(newBody);
    if (!teaserDraftId) return;
    teaserPendingBodyRef.current = newBody;
    if (teaserSaveTimerRef.current) {
      clearTimeout(teaserSaveTimerRef.current);
    }
    teaserSaveTimerRef.current = setTimeout(async () => {
      teaserSaveTimerRef.current = null;
      const pending = teaserPendingBodyRef.current;
      if (!pending) return;
      teaserPendingBodyRef.current = null;
      try {
        await apiFetch(`/ai/teasers/${teaserDraftId}/`, {
          method: "PATCH",
          body: { body: pending },
        });
      } catch (err) {
        // Save failed — put the body back so the next edit flushes it.
        teaserPendingBodyRef.current = pending;
        setTeaserError(err?.message || "Kunde inte spara ändringar.");
      }
    }, 600);
  }, [teaserDraftId]);

  async function handleApproveTeaser() {
    if (!teaserDraftId || teaserLoading) return;
    setTeaserLoading(true);
    setTeaserError("");
    try {
      // Flush any pending debounced save FIRST so the approve pass
      // acts on the body the broker actually sees. Without this a
      // broker who edits → clicks Approve within 600 ms gets a PDF
      // rendered from the pre-edit body.
      await flushTeaserSave();
      const res = await apiFetch(`/ai/teasers/${teaserDraftId}/`, {
        method: "PATCH",
        body: { action: "approve" },
      });
      setTeaserAccessToken(res.access_token || null);
    } catch (err) {
      setTeaserError(err?.message || "Kunde inte publicera teaser.");
    } finally {
      setTeaserLoading(false);
    }
  }

  // Broker PDF download — routes to the authenticated broker endpoint
  // (skips the buyer NDA gate). Opens in a new tab so the current
  // PM Studio state isn't disturbed.
  const handleDownloadTeaserPdf = useCallback(() => {
    if (!teaserDraftId) return;
    const url = joinUrl(
      API_BASE,
      `/ai/teasers/${teaserDraftId}/pdf/`,
    );
    window.open(url, "_blank", "noopener");
  }, [teaserDraftId]);

  // Persist editor state on changes so navigating away + back resumes
  // where the broker left off. Debounced via a microtask-free useEffect.
  useEffect(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          showWizard,
          wizardLogo,
          wizardSelectedTemplate,
          presentationTemplate,
          sections,
          logoUrl,
          title,
        }),
      );
    } catch {
      // localStorage may be unavailable (private mode, quota) — silently skip.
    }
  }, [storageKey, showWizard, wizardLogo, wizardSelectedTemplate, presentationTemplate, sections, logoUrl, title]);

  async function handleSaveTemplate() {
    const name = saveTemplateName.trim();
    if (!name || !slug) return;
    setSaving(true);
    try {
      const saved = await saveTemplate(
        slug, name, title, createIMPresentationLayout(sections),
        presentationTemplate?.theme || {},
      );
      setSavedTemplates((prev) => {
        const idx = prev.findIndex((t) => t.id === saved.id);
        // New saves go to the top so they show first in the picker; updates
        // also bubble to the top since the broker just touched them.
        if (idx >= 0) {
          const next = prev.slice();
          next.splice(idx, 1);
          return [saved, ...next];
        }
        return [saved, ...prev];
      });
      setSaveModalOpen(false);
      setSaveTemplateName("");
    } catch {
      // ignore — user can retry
    } finally {
      setSaving(false);
    }
  }

  function updateSection(id, patch) {
    setSections((prev) =>
      prev.map((section) => (section.id === id ? { ...section, ...patch } : section))
    );
  }

  function updateChild(sectionId, childId, patch) {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          ...(section.origin === "template" ? { origin: "broker" } : {}),
          children: section.children.map((child) =>
            child.id === childId ? { ...child, ...patch } : child
          ),
        };
      })
    );
  }

  function removeSection(id) {
    setSections((prev) => {
      const next = prev.filter((section) => section.id !== id);
      if (selectedId === id) {
        setSelectedId(next[0]?.id || null);
      }
      return next;
    });
  }

  // Duplicate a section in-place. Fresh ids on every nested node so
  // reordering/edits don't accidentally mutate the original. Covers the
  // columnsSection cells variant as well as the standard children array.
  function duplicateSection(id) {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      if (idx < 0) return prev;
      const src = prev[idx];
      const clone = {
        ...src,
        id: uid(),
        children: (src.children || []).map((c) => ({ ...c, id: uid() })),
        cells: Array.isArray(src.cells)
          ? src.cells.map((cell) => ({
              ...cell,
              id: uid(),
              children: (cell.children || []).map((c) => ({ ...c, id: uid() })),
            }))
          : undefined,
      };
      const next = [...prev];
      next.splice(idx + 1, 0, clone);
      return next;
    });
  }

  function moveSectionDir(id, dir) {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      if (idx < 0) return prev;
      const target = dir === "up" ? idx - 1 : idx + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }

  function handleLogoUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    // Size guard — backend rejects > 5 MB
    if (file.size > 5 * 1024 * 1024) {
      alert("Logotypen är för stor (max 5 MB).");
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      setLogoUrl(dataUrl);
      if (!slug) return;
      try {
        await saveIMImages(slug, { logo: dataUrl });
        const savedProfile = await uploadPresentationAsset(slug, "logo", file);
        setPresentationProfile(savedProfile);
        setPresentationProfileDraft(savedProfile.configuration);
      } catch (err) {
        console.error("Failed to save logo:", err);
        alert(`Kunde inte spara logotypen i brokerprofilen: ${err.message}`);
      }
    };
    reader.readAsDataURL(file);
  }

  function updatePresentationDraft(group, key, value) {
    setPresentationProfileDraft((current) => ({
      ...(current || presentationProfile?.configuration || {}),
      [group]: { ...((current || presentationProfile?.configuration || {})[group] || {}), [key]: value },
    }));
  }

  async function persistPresentationProfile() {
    if (!slug || !presentationProfileDraft) return;
    setPresentationProfileSaving(true);
    setPresentationProfileError("");
    try {
      const saved = await savePresentationProfile(slug, presentationProfileDraft);
      setPresentationProfile(saved);
      setPresentationProfileDraft(saved.configuration);
    } catch (error) {
      setPresentationProfileError(error.message || "Kunde inte spara presentationsprofilen.");
    } finally {
      setPresentationProfileSaving(false);
    }
  }

  async function handlePresentationAssetUpload(event, role) {
    const file = event.target.files?.[0];
    if (!file || !slug) return;
    try {
      const saved = await uploadPresentationAsset(slug, role, file);
      setPresentationProfile(saved);
      setPresentationProfileDraft(saved.configuration);
      if (role === "logo" && saved.assets?.logo) setLogoUrl(saved.assets.logo);
    } catch (error) {
      setPresentationProfileError(error.message || "Kunde inte ladda upp bilden.");
    } finally {
      event.target.value = "";
    }
  }

  function insertSectionAt(section, overId) {
    setSections((prev) => {
      const next = [...prev];
      const index =
        overId && overId !== "canvas-dropzone"
          ? next.findIndex((item) => item.id === overId)
          : -1;

      if (index >= 0) {
        next.splice(index, 0, section);
      } else {
        next.push(section);
      }
      return next;
    });
    setSelectedId(section.id);
  }

  // Insert a new section at an exact array index. Used by the "+"-zones
  // between rows so the broker can drop a new block at any point without
  // dragging from the dock.
  function insertSectionAtIndex(section, idx) {
    setSections((prev) => {
      const next = [...prev];
      const clamped = Math.max(0, Math.min(idx, next.length));
      next.splice(clamped, 0, section);
      return next;
    });
    setSelectedId(section.id);
  }

  function insertPrewrittenIntoSection(targetSectionId, item) {
    const newChildren = buildPrewrittenChildren(item);
    if (!newChildren.length) return false;

    let inserted = false;

    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== targetSectionId) return section;
        if (!canAcceptPrewrittenChildren(section)) return section;

        inserted = true;

        return {
          ...section,
          children: [...section.children, ...newChildren],
        };
      })
    );

    if (inserted) {
      setSelectedId(targetSectionId);
    }

    return inserted;
  }

  /**
   * Click-to-insert with typewriter animation.
   *
   * Lands the prewritten text into the document the same way drag-drop does
   * (into the currently selected section if it can accept children, else as
   * a new section appended), but renders the body progressively so the
   * broker sees the AI "type" the text in. Speed: ~60 chars/100ms which is
   * fast enough not to feel sluggish but slow enough to register.
   *
   * Bypasses the drag-and-drop path entirely — useful when the snippet
   * button doesn't drag for some reason and as a more discoverable affordance
   * (a real "Insert" button is clearer than a small draggable card).
   */
  function insertPrewrittenWithAnimation(item) {
    if (!item) return;

    // Data-only items have no body to typewriter — insert the prebuilt
    // children straight into a textSection and stop here. Payload is already
    // populated on item.{tableData,ownershipData,chartData}.
    const isDataOnly =
      (item.sectionId === "financial_table" && item.tableData) ||
      (item.sectionId === "ownership_table" && item.ownershipData) ||
      (item.sectionId === "revenue_chart" && item.chartData);
    if (isDataOnly) {
      const target = sections.find(
        (s) => s.id === selectedId && canAcceptPrewrittenChildren(s),
      );
      if (target) {
        insertPrewrittenIntoSection(target.id, item);
        setSelectedId(target.id);
      } else {
        const section = buildPrewrittenSection(item);
        setSections((prev) => [...prev, section]);
        setSelectedId(section.id);
      }
      return;
    }

    if (!item.body) return;
    const fullBody = item.body;

    // Pick a target child id we can mutate after the section exists.
    const newChildren = buildPrewrittenChildren({ ...item, body: "" });
    const textChild = newChildren.find((c) => c.type === "text");
    if (!textChild) return;
    const childId = textChild.id;

    // Find a section that can accept the children, OR build a new one.
    const target = sections.find(
      (s) => s.id === selectedId && canAcceptPrewrittenChildren(s),
    );

    let targetSectionId;
    if (target) {
      targetSectionId = target.id;
      setSections((prev) =>
        prev.map((s) =>
          s.id === target.id ? { ...s, children: [...s.children, ...newChildren] } : s,
        ),
      );
    } else {
      const empty = buildPrewrittenSection({ ...item, body: "" });
      targetSectionId = empty.id;
      setSections((prev) => [...prev, empty]);
    }
    setSelectedId(targetSectionId);

    // Typewriter loop — chunk size + interval are tuned so a 200-word
    // paragraph fully reveals in ~3 seconds.
    let pos = 0;
    const step = Math.max(2, Math.ceil(fullBody.length / 90));
    const tick = () => {
      pos = Math.min(pos + step, fullBody.length);
      setSections((prev) =>
        prev.map((s) => {
          if (s.id !== targetSectionId) return s;
          return {
            ...s,
            children: s.children.map((c) =>
              c.id === childId ? { ...c, text: fullBody.slice(0, pos) } : c,
            ),
          };
        }),
      );
      if (pos < fullBody.length) {
        timer = setTimeout(tick, 30);
      }
    };
    let timer = setTimeout(tick, 30);
  }

  function handleDragStart(event) {
    setActiveDrag(event.active?.data?.current || null);
    // Diagnostic — strip after the drag-drop UX is verified working in prod.
    // eslint-disable-next-line no-console
    console.log("[IMStudio drag] start", {
      activeId: event.active?.id,
      source: event.active?.data?.current?.source,
    });
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    setActiveDrag(null);

    // Diagnostic — strip after drag-drop UX is verified.
    // eslint-disable-next-line no-console
    console.log("[IMStudio drag] end", {
      activeId: active?.id,
      overId: over?.id,
      source: active?.data?.current?.source,
    });

    if (!over) return;

    const activeData = active?.data?.current;
    const overId = String(over.id);

    if (!activeData) return;

    if (activeData.source === "library") {
      // Library drags are broker-initiated custom sections; tag
      // origin so ``handleLoadAll`` preserves them across "Ladda in
      // allt" regenerations.
      insertSectionAt(
        createSectionBlock(activeData.sectionType, { origin: "broker" }),
        overId,
      );
      return;
    }

    if (activeData.source === "prewritten") {
      const targetSection = sections.find((section) => section.id === overId);

      if (targetSection) {
        const inserted = insertPrewrittenIntoSection(targetSection.id, activeData.item);
        if (inserted) return;
      }

      insertSectionAt(buildPrewrittenSection(activeData.item), overId);
      return;
    }

    if (activeData.source === "canvas") {
      const oldIndex = sections.findIndex((section) => section.id === active.id);
      const newIndex =
        overId === "canvas-dropzone"
          ? sections.length - 1
          : sections.findIndex((section) => section.id === over.id);

      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

      setSections((prev) => arrayMove(prev, oldIndex, newIndex));
    }
  }

  function setProgress(sectionId, type, text) {
    setPrewrittenProgress((prev) => ({
      ...prev,
      [sectionId]: { type, text },
    }));
  }

  async function refreshPrewrittenData() {
    const [full, status] = await Promise.all([
      fetchIMFull(slug),
      fetchAgentStatus(slug).catch(() => agentStatus),
    ]);

    const companyData = full.company || full.organisation || {};
    const companyName = companyData?.name || companyData?.namn || "Investment memorandum";
    const pm = full?.pm || full?.im_document || {};
    const storedImages = pm.images && typeof pm.images === "object" ? pm.images : {};

    if (companyName && title === "One-page editor") {
      setTitle(companyName);
    }
    if (companyName && companyName !== "Investment memorandum") {
      companyNameRef.current = companyName;
    }
    if (storedImages.logo && typeof storedImages.logo === "string") {
      setLogoUrl(storedImages.logo);
    }

    setAgentStatus(status);
    const nextItems = buildPrewrittenItemsFromBackend(full, status);
    setPrewrittenItems(nextItems);

    // Hydrate faktagranskning from the persisted IMCompanyComment so the
    // card survives reloads without requiring a fresh runAllAgents.
    const cr = full?.im_comments?.consistency_report || null;
    setConsistency(cr && Array.isArray(cr.issues) ? cr : null);

    return { full, status, items: nextItems };
  }

  async function handlePreparePrewritten(item) {
    if (!slug || workingPrewrittenId) return;

    const sectionId = item.sectionId;
    setWorkingPrewrittenId(sectionId);

    try {
      setProgress(sectionId, "loading", "Kontrollerar status…");
      const latestStatus = await fetchAgentStatus(slug).catch(() => agentStatus);
      if (latestStatus) setAgentStatus(latestStatus);

      if (!item.agentKey) {
        setProgress(sectionId, "loading", "Hämtar sektion från backend…");
        await refreshPrewrittenData();
        setProgress(sectionId, "success", "Klar att dra in i dokumentet.");
        return;
      }

      if (item.agentKey === "financials") {
        if (!latestStatus?.financials?.comment_exists) {
          if (!latestStatus?.financials?.can_run) {
            throw new Error(
              buildWhyDisabled(latestStatus, "financials")[0] ||
                "Finansiell kommentar är inte redo."
            );
          }
          setProgress(sectionId, "loading", "Genererar finansiell kommentar…");
          await runFinancialAgent(slug);
        } else {
          setProgress(sectionId, "loading", "Hämtar finansiell kommentar…");
        }
      }

      if (item.agentKey === "valuation") {
        if (!latestStatus?.valuation?.comment_exists) {
          if (!latestStatus?.valuation?.can_run) {
            throw new Error(
              buildWhyDisabled(latestStatus, "valuation")[0] ||
                "Värderingskommentar är inte redo."
            );
          }
          setProgress(sectionId, "loading", "Genererar värderingskommentar…");
          await runValuationAgent(slug);
        } else {
          setProgress(sectionId, "loading", "Hämtar värderingskommentar…");
        }
      }

      if (item.agentKey === "risks") {
        if (!latestStatus?.risks?.comment_exists) {
          if (!latestStatus?.risks?.can_run) {
            throw new Error(
              buildWhyDisabled(latestStatus, "risks")[0] || "Nyckelrisker är inte redo."
            );
          }
          setProgress(sectionId, "loading", "Genererar riskkommentar…");
          await runRiskAgent(slug);
        } else {
          setProgress(sectionId, "loading", "Hämtar riskkommentar…");
        }
      }

      if (item.agentKey === "web") {
        if (!latestStatus?.web?.profile_exists) {
          if (!latestStatus?.web?.can_run) {
            throw new Error(
              buildWhyDisabled(latestStatus, "web")[0] || "Webbprofil är inte redo."
            );
          }
          setProgress(sectionId, "loading", "Genererar webbprofil…");
          const baseUrl = latestStatus?.web?.base_url || null;
          await runWebAgent(slug, baseUrl);
        } else {
          setProgress(sectionId, "loading", "Hämtar webbprofil…");
        }
      }

      if (item.agentKey === "overview") {
        if (!latestStatus?.overview?.comment_exists) {
          if (!latestStatus?.overview?.can_run) {
            // List of missing prerequisite comments comes back as
            // overview.missing[] from the status endpoint.
            const missing = latestStatus?.overview?.missing || [];
            const labelMap = {
              financials_comment_missing: "Finansiell utveckling",
              risks_comment_missing: "Nyckelrisker",
              valuation_comment_missing: "Värdering",
              web_comment_missing: "Erbjudande & tjänster",
            };
            const labels = missing.map((k) => labelMap[k] || k).join(", ");
            throw new Error(
              labels
                ? `Bolagsöversikt kräver att följande avsnitt är klara först: ${labels}.`
                : "Bolagsöversikt är inte redo."
            );
          }
          setProgress(sectionId, "loading", "Genererar bolagsöversikt…");
          await runOverviewAgent(slug);
        } else {
          setProgress(sectionId, "loading", "Hämtar bolagsöversikt…");
        }
      }

      setProgress(sectionId, "loading", "Hämtar senaste text från backend…");
      await refreshPrewrittenData();
      setProgress(sectionId, "success", "Klar att dra in i dokumentet.");

      window.setTimeout(() => {
        setPrewrittenProgress((prev) => {
          const next = { ...prev };
          delete next[sectionId];
          return next;
        });
      }, 1800);
    } catch (error) {
      setProgress(sectionId, "error", error?.message || "Kunde inte hämta sektionen.");
    } finally {
      setWorkingPrewrittenId(null);
    }
  }

  // ── "Ladda in allt" ────────────────────────────────────────────────────
  //
  // Enable the button whenever the workspace has loaded. The backend
  // now runs `IMComposer.persist_all` (Phase-12 composer) which handles
  // missing inputs per-section by emitting an empty body — a missing
  // CompanyWebProfile for the web section does NOT stop financials /
  // valuation / risks from composing successfully. The old "every
  // legacy base-agent must be able to run" gate blocked the button on
  // any single missing input, which is stricter than the composer
  // actually needs. Sections with missing inputs will simply come back
  // empty in the response and the broker can drag in whichever ones
  // succeeded.
  const canLoadAll = useMemo(() => {
    return !!agentStatus;
  }, [agentStatus]);

  async function handleLoadAll() {
    if (!slug || loadingAll || !canLoadAll) return;

    setLoadingAll(true);
    setLoadAllError("");
    setLoadAllProgress(null);

    // ── Status pill: cycle through "thinking / writing" phrases so
    // the broker feels the AI is working. Cleared on done + fades
    // 5s later via the done handler below.
    if (loadAllStatusTimer.current) {
      clearInterval(loadAllStatusTimer.current);
      loadAllStatusTimer.current = null;
    }
    const CYCLE_MESSAGES = [
      "Funderar…",
      "Skriver…",
      "Formulerar…",
      "Väger fakta…",
      "Redigerar…",
      "Läser branschdata…",
      "Renderar…",
    ];
    let cycleIdx = 0;
    setLoadAllStatus({ phase: "working", message: CYCLE_MESSAGES[0] });
    loadAllStatusTimer.current = setInterval(() => {
      cycleIdx = (cycleIdx + 1) % CYCLE_MESSAGES.length;
      setLoadAllStatus({ phase: "working", message: CYCLE_MESSAGES[cycleIdx] });
    }, 1800);

    // Auto-scroll: after every section insertion, nudge the window so
    // the bottom of the growing preview stays in view. Debounced via
    // requestAnimationFrame so multiple rapid arrivals only scroll
    // once per paint. Only scrolls DOWN — if the broker manually
    // scrolls up while streaming, we respect that until the next
    // arrival.
    const scrollPreviewToBottom = () => {
      requestAnimationFrame(() => {
        const el = previewRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const targetY = window.scrollY + rect.bottom - window.innerHeight + 120;
        if (targetY > window.scrollY) {
          window.scrollTo({ top: targetY, behavior: "smooth" });
        }
      });
    };

    // Canonical label ordering + lookup-by-section_type (agentKey).
    // Sections arrive out-of-order (parallel prose completes at
    // different times) — labelRank keeps them sorted into the
    // canonical PREWRITTEN_DEFS order regardless of arrival order.
    const labelRank = new Map();
    const defByAgentKey = new Map();
    PREWRITTEN_DEFS.forEach((d, i) => {
      labelRank.set(d.label, i);
      if (d.agentKey) defByAgentKey.set(d.agentKey, d);
    });

    // Strip composer-origin sections; preserve broker-authored ones.
    //
    // Sections carry an ``origin`` tag set at creation time:
    //   - ``composer``: built from IMComposer output; safe to strip
    //     and regenerate from the incoming stream.
    //   - ``broker``: created via a library drag or the add-section
    //     UI; preserved across "Ladda in allt" runs.
    //   - unset (legacy): from an earlier PREWRITTEN_DEFS revision or
    //     from before origin-tagging existed. Treated case-by-case
    //     via the content heuristic below — blank ones get dropped
    //     as leftover template scaffolding, non-blank ones are
    //     tagged ``broker`` on the fly and kept.
    const _hasContent = (section) => {
      if (!Array.isArray(section?.children) || !section.children.length) return false;
      return section.children.some((c) => {
        if (!c) return false;
        if (c.kind === "text" && typeof c.text === "string" && c.text.trim().length > 20) return true;
        if (c.kind === "title" && typeof c.text === "string" && c.text.trim().length > 0) return true;
        if (c.kind === "financialTable" && Array.isArray(c.rows) && c.rows.length) return true;
        if (c.kind === "ownershipTable" && Array.isArray(c.rows) && c.rows.length) return true;
        if (c.kind === "revenueChart" && Array.isArray(c.periods) && c.periods.length) return true;
        if (c.kind === "image" && (c.imageUrl || c.url)) return true;
        if (c.kind === "button" && typeof c.text === "string" && c.text.trim()) return true;
        return false;
      });
    };

    // Labels that originate from BACKEND_SECTION_LABELS are template
    // scaffolding — they were seeded by the Wizard template loader before
    // we started tagging ``origin: "template"``. Drop them on load-all so
    // existing broker workspaces (whose sections live in localStorage
    // untagged) also get a clean regen instead of a stale tail.
    const templateLabelSet = new Set([
      "Bolags?versikt", "?gare & bakgrund", "Marknad & kunder", "Verksamhet",
      "Aff?rsmodell & int?kter", "Organisation & personal", "Finansiell utveckling",
      "V?rdering", "Nyckelrisker", "Value uplift & m?jligheter", "Process & n?sta steg",
    ]);
    setSections((prev) => {
      const cover = prev[0] && (prev[0].type === "coverSection" || prev[0].label === "Omslag")
        ? prev[0]
        : null;
      const kept = prev
        .slice(cover ? 1 : 0)
        .filter((s) => {
          if (s.origin === "composer") return false;
          // Canonical template labels identify old imported template
          // scaffolding even when a legacy migration marked a block as
          // broker-authored. They must not return as custom tail sections.
          if (templateLabelSet.has(s.label)) return false;
          if (s.origin === "template") return false;
          if (s.origin === "broker") return true;
          // Legacy / untagged: drop template-named scaffolding, keep only
          // broker work. Any section whose label matches a canonical
          // template label is treated as pre-tag template placeholder.
          return _hasContent(s);
        })
        .map((s) => (s.origin ? s : { ...s, origin: "broker" }));
      return [
        ...(cover ? [cover] : []),
        ...kept,
      ];
    });

    // Build a section block from a streamed event and insert / replace
    // in canonical position. Called from the SSE event handler.
    const insertStreamedSection = (event, replace = false) => {
      const def = defByAgentKey.get(event.section_type);
      if (!def) return;
      const item = {
        id: `prewritten-${def.id}`,
        sectionId: def.id,
        label: def.label,
        heading: def.defaultTitle,
        body: event.body || "",
        hasContent: !!(event.body && event.body.trim()),
      };
      const section = buildPrewrittenSection(item);

      setSections((prev) => {
        const cover = prev[0] && (prev[0].type === "coverSection" || prev[0].label === "Omslag")
          ? prev[0]
          : null;
        const rest = prev.slice(cover ? 1 : 0);
        // Preserve custom (non-prewritten) sections at the tail.
        const customExtras = rest.filter((s) => !labelRank.has(s.label));
        // Existing prewritten sections, minus this label if we're
        // replacing (update event).
        const otherPrewritten = rest
          .filter((s) => labelRank.has(s.label) && s.label !== def.label);
        // Re-insert this section in canonical order.
        const merged = [...otherPrewritten, section].sort(
          (a, b) => (labelRank.get(a.label) ?? 999) - (labelRank.get(b.label) ?? 999),
        );
        return [
          ...(cover ? [cover] : []),
          ...merged,
          ...customExtras,
        ];
      });
    };

    let arrived = 0;
    let doneEvent = null;
    const failed = [];

    try {
      doneEvent = await streamRunAllAgents(slug, (event) => {
        if (!event || !event.type) return;
        switch (event.type) {
          case "started":
            setLoadAllProgress({ current: 0, total: 0 });
            break;
          case "section":
            if (event.ok) {
              insertStreamedSection(event, /* replace */ false);
              arrived += 1;
              setLoadAllProgress({ current: arrived, total: arrived });
              scrollPreviewToBottom();
            } else {
              failed.push({ agent: event.section_type, error: event.error });
            }
            break;
          case "section_updated":
            // Post-prose passes mutated a section already on the
            // canvas — replace the body in place.
            insertStreamedSection(event, /* replace */ true);
            break;
          case "error":
            failed.push({ agent: "composer", error: event.error });
            break;
          case "done":
            // Handled after the loop below.
            break;
          default:
            break;
        }
      });

      // After streaming, refresh /full/ once to pull structured
      // payloads (financial-table rows, ownership shareholders,
      // chart periods) — the streaming events carry only bodies.
      try {
        const refresh = await refreshPrewrittenData();
        const nextItems = refresh?.items || [];
        for (const it of nextItems) {
          if (it.source === "data" && it.hasContent) {
            const dataSection = buildPrewrittenSection(it);
            setSections((prev) => {
              const cover = prev[0] && (prev[0].type === "coverSection" || prev[0].label === "Omslag")
                ? prev[0]
                : null;
              const rest = prev.slice(cover ? 1 : 0);
              const customExtras = rest.filter((s) => !labelRank.has(s.label));
              const otherPrewritten = rest
                .filter((s) => labelRank.has(s.label) && s.label !== it.label);
              const merged = [...otherPrewritten, dataSection].sort(
                (a, b) => (labelRank.get(a.label) ?? 999) - (labelRank.get(b.label) ?? 999),
              );
              return [
                ...(cover ? [cover] : []),
                ...merged,
                ...customExtras,
              ];
            });
          }
        }
      } catch {
        // Structured-data refresh is best-effort; the streamed
        // bodies are already on the canvas.
      }

      if (failed.length) {
        const names = failed.map((f) => f.agent).join(", ");
        setLoadAllError(`${failed.length} sektioner misslyckades: ${names}.`);
      }
    } catch (err) {
      setLoadAllError(err?.message || "Kunde inte ladda in alla agenter.");
    } finally {
      setLoadingAll(false);
      setLoadAllProgress(null);
      // Stop the cycling; hold "Klar" for ~4.6s, then fade out
      // over 400ms via the phase="fading" style branch below.
      if (loadAllStatusTimer.current) {
        clearInterval(loadAllStatusTimer.current);
        loadAllStatusTimer.current = null;
      }
      setLoadAllStatus({ phase: "done", message: "Klar" });
      window.setTimeout(() => {
        setLoadAllStatus((prev) =>
          prev && prev.phase === "done"
            ? { ...prev, phase: "fading" }
            : prev,
        );
      }, 4600);
      window.setTimeout(() => setLoadAllStatus(null), 5000);
    }
  }

  // Render the current PM preview into a jsPDF instance. Returned blob +
  // filename are reused by both finalize (server-save) and download (local).
  async function _renderPdf() {
    if (!previewRef.current) return null;
    let exportProfile = presentationProfile;
    if (slug && presentationProfileDraft && JSON.stringify(presentationProfileDraft) !==
        JSON.stringify(presentationProfile?.configuration || {})) {
      exportProfile = await savePresentationProfile(slug, presentationProfileDraft);
      setPresentationProfile(exportProfile);
      setPresentationProfileDraft(exportProfile.configuration);
    }
    presentationExportVersionRef.current = exportProfile?.id || null;

    // Select the final document before rendering. Suppressed/template
    // blocks, placeholders, decorative page breaks, and duplicate
    // section ids never become document elements.
    const finalSections = applyPresentationToIMSections(
      selectFinalIMSections(sections), profilePresentation,
    );
    if (finalSections.length !== sections.length
        || finalSections.some((section, index) => section !== sections[index])) {
      setSections(finalSections);
      if (!finalSections.some((section) => section.id === selectedId)) {
        setSelectedId(finalSections[0]?.id || null);
      }
      await new Promise((resolve) => requestAnimationFrame(() =>
        requestAnimationFrame(resolve)));
    }

    const ignoreElements = (el) => {
      const cls = (el.className && el.className.baseVal) || el.className || "";
      const classStr = typeof cls === "string" ? cls : "";
      return (
        el.hasAttribute?.("data-pdf-hide") ||
        /imstudio-block-flybar|imstudio-drag-handle|pmstudio-block-flybar|pmstudio-drag-handle|imstudio-editor__preview-block--selected/.test(classStr)
      );
    };

    // Browsers cap canvas height (~16384px in Chrome). At scale 2, a long PM
    // overflows and toDataURL silently returns "data:," — jsPDF then fails
    // with "wrong PNG signature". Clamp scale for tall previews and retry at
    // scale 1 if the first pass still produced an invalid data URL.
    const previewHeight = previewRef.current.scrollHeight || 0;
    const initialScale = previewHeight > 3500 ? 1 : 2;

    const configuration = exportProfile?.configuration || presentationProfileDraft || {};
    const branding = configuration.branding || {};
    const chartStyle = configuration.chart || {};
    const profileLayout = configuration.layout || {};
    async function renderAt(scale) {
      const c = await html2canvas(previewRef.current, {
        scale,
        useCORS: true,
        backgroundColor: "#ffffff",
        ignoreElements,
        onclone: (clonedDocument) => {
          const style = clonedDocument.createElement("style");
          style.textContent = `
            .imstudio-editor__preview-page .imstudio-editor__text-heading,
            .imstudio-editor__preview-page .imstudio-editor__two-col-heading,
            .imstudio-editor__preview-page .imstudio-editor__cta-heading,
            .imstudio-editor__preview-page .imstudio-editor__hero-title {
              color: ${branding.primary_color || "#1C2D50"} !important;
              font-family: '${branding.font_heading || "Arial"}', sans-serif !important;
            }
            .imstudio-editor__preview-page .imstudio-editor__text-body,
            .imstudio-editor__preview-page .imstudio-editor__two-col-text,
            .imstudio-editor__preview-page .imstudio-editor__cta-text {
              color: ${branding.text_color || "#172033"} !important;
              font-family: '${branding.font_body || "Arial"}', sans-serif !important;
            }
            .imstudio-editor__preview-page .imstudio-editor__text-section,
            .imstudio-editor__preview-page .imstudio-editor__two-col {
              padding-left: ${profileLayout.margin_x || 48}px !important;
              padding-right: ${profileLayout.margin_x || 48}px !important;
            }
            .imstudio-editor__preview-page .imstudio-editor__financial-table th,
            .imstudio-editor__preview-page .imstudio-editor__ownership-table th {
              background: ${branding.primary_color || "#1C2D50"} !important;
              color: #ffffff !important;
            }
            .imstudio-editor__preview-page .imstudio-editor__financial-table td,
            .imstudio-editor__preview-page .imstudio-editor__ownership-table td {
              border-color: ${chartStyle.grid_color || "#D8DEE8"} !important;
            }
            .imstudio-editor__preview-page .imstudio-editor__content-image {
              object-fit: ${profileLayout.image_placement === "cover" ? "cover" : "contain"} !important;
            }
            .imstudio-editor__preview-page .imstudio-editor__dark-button,
            .imstudio-editor__preview-page .imstudio-editor__cta-button {
              background: ${branding.accent_color || "#B28A45"} !important;
            }
          `;
          clonedDocument.head.appendChild(style);
          clonedDocument.querySelectorAll(".imstudio-editor__revenue-chart svg").forEach((svg) => {
            svg.querySelectorAll('rect[fill="#1c1917"]').forEach((node) => node.setAttribute("fill", chartStyle.revenue_color || branding.primary_color || "#1C2D50"));
            svg.querySelectorAll('[stroke="#ea580c"], [fill="#ea580c"]').forEach((node) => {
              if (node.tagName.toLowerCase() === "line" || node.tagName.toLowerCase() === "path") node.setAttribute("stroke", chartStyle.margin_color || branding.accent_color || "#B28A45");
              if (node.tagName.toLowerCase() === "circle") node.setAttribute("fill", chartStyle.margin_color || branding.accent_color || "#B28A45");
            });
            svg.querySelectorAll('line[stroke="#e7e5e4"]').forEach((node) => node.setAttribute("stroke", chartStyle.grid_color || "#D8DEE8"));
          });
        },
      });
      return { canvas: c, dataUrl: c.toDataURL("image/png") };
    }

    let { canvas, dataUrl: imgData } = await renderAt(initialScale);
    if (!imgData.startsWith("data:image/png") && initialScale > 1) {
      ({ canvas, dataUrl: imgData } = await renderAt(1));
    }
    if (!imgData.startsWith("data:image/png")) {
      throw new Error("Preview too large to rasterize for PDF export");
    }
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // ── Cover page ─────────────────────────────────────────────────
    // Branded IM cover. The logo is uploaded by the broker or inherited
    // from workspace branding; a failed remote image load leaves a clean
    // wordmark cover with the company name intact.
    const cover = configuration.cover || {};
    const primaryRgb = toPdfRgb(branding.primary_color);
    const accentRgb = toPdfRgb(branding.accent_color, [178, 138, 69]);
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
    const coverImage = cover.style === "image"
      ? await imageUrlToPngDataUrl(exportProfile?.assets?.cover)
      : null;
    if (coverImage) {
      pdf.addImage(coverImage, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.setFillColor(24, 36, 54);
      pdf.setGState(new pdf.GState({ opacity: 0.72 }));
      pdf.rect(0, pdfHeight * 0.52, pdfWidth, pdfHeight * 0.48, "F");
      pdf.setGState(new pdf.GState({ opacity: 1 }));
    }
    pdf.setFillColor(...(coverImage ? accentRgb : primaryRgb));
    pdf.rect(0, 0, pdfWidth, 4, "F");

    const coverLogo = await imageUrlToPngDataUrl(exportProfile?.assets?.logo || logoUrl);
    if (coverLogo) {
      const logoWidth = 42;
      const logoHeight = 24;
      const logoX = (cover.alignment || "left") === "left" ? 24
        : cover.alignment === "right" ? pdfWidth - logoWidth - 24 : (pdfWidth - logoWidth) / 2;
      pdf.addImage(coverLogo, "PNG", logoX, 52, logoWidth, logoHeight);
    }

    const align = cover.alignment || "center";
    const textX = align === "left" ? 24 : align === "right" ? pdfWidth - 24 : pdfWidth / 2;
    const textAlign = align;
    pdf.setTextColor(...(coverImage ? [255, 255, 255] : primaryRgb));
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.setCharSpace(1.5);
    pdf.text(cover.confidentiality_label || "INFORMATION MEMORANDUM · KONFIDENTIELLT", textX, pdfHeight / 2 - 28, { align: textAlign });
    pdf.setCharSpace(0);
    pdf.setFontSize(27);
    const coverTitle = pdf.splitTextToSize(title || "", pdfWidth - 44);
    pdf.text(coverTitle, textX, pdfHeight / 2 + 2, { align: textAlign, maxWidth: pdfWidth - 44 });
    if (cover.subtitle) {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(12);
      pdf.text(cover.subtitle, textX, pdfHeight / 2 + (coverTitle.length > 1 ? 33 : 28), { align: textAlign, maxWidth: pdfWidth - 48 });
    }
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(...(coverImage ? [255, 255, 255] : toPdfRgb(branding.muted_color, [120, 113, 108])));
    if (cover.show_date !== false) {
      pdf.text(new Date().toLocaleDateString("sv-SE"), textX,
        pdfHeight / 2 + (coverTitle.length > 1 ? 42 : 34), { align: textAlign });
    }

    // ── Content pages ──────────────────────────────────────────────
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addPage();
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    const layout = configuration.layout || {};
    const pageCount = pdf.internal.getNumberOfPages();
    for (let pageNo = 2; pageNo <= pageCount; pageNo += 1) {
      pdf.setPage(pageNo);
      if (layout.header_enabled) {
        pdf.setDrawColor(...primaryRgb);
        pdf.setLineWidth(0.4);
        pdf.line(18, 16, pdfWidth - 18, 16);
        pdf.setTextColor(...toPdfRgb(branding.muted_color, [102, 112, 133]));
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.text(title || "Information Memorandum", 18, 12);
      }
      if (layout.footer_enabled !== false) {
        const footerY = pdfHeight - 10;
        pdf.setDrawColor(...toPdfRgb(branding.accent_color, [178, 138, 69]));
        pdf.setLineWidth(0.35);
        pdf.line(18, footerY - 7, pdfWidth - 18, footerY - 7);
        pdf.setTextColor(...toPdfRgb(branding.footer_color || branding.muted_color, [102, 112, 133]));
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.text(title || "Information Memorandum", 18, footerY);
        const position = layout.page_number_position || "right";
        const numberX = position === "left" ? 18 : position === "center" ? pdfWidth / 2 : pdfWidth - 18;
        pdf.text(`${pageNo - 1} / ${pageCount - 1}`, numberX, footerY,
          { align: position === "left" ? "left" : position === "center" ? "center" : "right" });
      }
    }

    const filename = `${title || "document"}.pdf`;
    return { pdf, blob: pdf.output("blob"), filename };
  }

  // Save the PDF to the backend so pipeline_send_pm can attach it.
  // Returns true on success, false otherwise — caller decides what to
  // show the broker.
  async function _uploadPdfBlob(blob, filename) {
    if (!slug || !blob) return false;
    try {
      const form = new FormData();
      form.append("file", blob, filename);
      if (presentationExportVersionRef.current) form.append("presentation_profile_version", String(presentationExportVersionRef.current));
      const csrf = getCsrfToken();
      const res = await fetch(`${API_BASE}/im-studio/company/${encodeURIComponent(slug)}/pdf/`, {
        method: "POST",
        credentials: "include",
        headers: csrf ? { "X-CSRFToken": csrf } : {},
        body: form,
      });
      return res.ok;
    } catch (uploadErr) {
      console.error("PDF upload to backend failed", uploadErr);
      return false;
    }
  }

  // "Färdigställ PM" — render PDF, upload to backend, mark finalized.
  // No local download is triggered here. Returns true on full success.
  async function exportPdf() {
    if (!previewRef.current) return false;
    try {
      setExporting(true);
      const rendered = await _renderPdf();
      if (!rendered) return false;
      const ok = await _uploadPdfBlob(rendered.blob, rendered.filename);
      if (!ok) {
        alert(
          "Kunde inte spara PM på servern. Pipeline-utskick kommer inte " +
          "kunna bifoga PDF förrän det här är fixat. Klicka Färdigställ igen " +
          "eller kontakta admin om problemet kvarstår."
        );
        return false;
      }
      return true;
    } catch (error) {
      console.error("PDF export failed", error);
      alert("Kunde inte färdigställa PM — försök igen.");
      return false;
    } finally {
      setExporting(false);
    }
  }

  // "Ladda ner PM" — render PDF and trigger a local browser download.
  // No backend interaction.
  async function downloadPdfLocal() {
    if (!previewRef.current) return;
    try {
      setExporting(true);
      const rendered = await _renderPdf();
      if (!rendered) return;
      rendered.pdf.save(rendered.filename);
    } catch (error) {
      console.error("PDF download failed", error);
      alert("Kunde inte skapa PDF för nedladdning.");
    } finally {
      setExporting(false);
    }
  }

  // ── Wizard handlers ──
  function handleWizardLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setWizardLogo(reader.result);
      setLogoUrl(reader.result);
    };
    reader.readAsDataURL(file);
  }

  async function handleWizardComplete() {
    // Find selected template from all sources
    const allTemplates = [...savedTemplates, ...starterTemplates];
    const tpl = wizardSelectedTemplate
      ? allTemplates.find((t) => `${t.is_global ? "global" : "broker"}:${t.id}` === wizardSelectedTemplate || t.id === wizardSelectedTemplate)
      : allTemplates.find((t) => t.is_default) || allTemplates[0];

    if (tpl) {
      setPresentationTemplate(tpl);
      if (tpl.is_global || tpl.id && Number.isFinite(Number(tpl.id))) {
        try {
          const selected = await saveIMPresentation(slug, {
            source: tpl.is_global ? "global" : "broker",
            id: tpl.id,
          });
          if (selected.presentation_profile) {
            setPresentationProfile(selected.presentation_profile);
            setPresentationProfileDraft(selected.presentation_profile.configuration);
          }
        } catch (err) {
          console.error("Failed to save presentation template selection:", err);
        }
      }
    }

    if (wizardLogo) {
      setLogoUrl(wizardLogo);
      if (slug) {
        try {
          await saveIMImages(slug, { logo: wizardLogo });
          const logoBlob = await (await fetch(wizardLogo)).blob();
          const ext = logoBlob.type.includes("jpeg") ? "jpg" : "png";
          const savedProfile = await uploadPresentationAsset(
            slug, "logo", new File([logoBlob], `broker-logo.${ext}`, { type: logoBlob.type || "image/png" }),
          );
          setPresentationProfile(savedProfile);
          setPresentationProfileDraft(savedProfile.configuration);
        } catch (err) {
          console.error("Failed to persist wizard logo:", err);
        }
      }
    }
    setShowWizard(false);
  }

  // ── Wizard UI ──
  if (showWizard) {
    return (
      <Main navItems={makeCompanyNav(slug)}>
        <div style={{
          maxWidth: 680, margin: "60px auto", padding: "0 24px",
        }}>
          {/* Progress */}
          <div style={{ display: "flex", gap: 8, marginBottom: 40 }}>
            {[1, 2].map((s) => (
              <div key={s} style={{
                flex: 1, height: 4, borderRadius: 2,
                background: wizardStep >= s ? "#57534E" : "#E7E5E4",
                transition: "background 0.3s",
              }} />
            ))}
          </div>

          {/* Step 1: Logo */}
          {wizardStep === 1 && (
            <div>
              <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1C1917", marginBottom: 8 }}>
                Ladda upp er logotyp
              </h1>
              <p style={{ fontSize: 15, color: "#78716C", marginBottom: 32, lineHeight: 1.6 }}>
                Logotypen visas på framsidan av informationsmemorandumet. Du kan ändra den senare.
              </p>

              <div
                onClick={() => wizardLogoInputRef.current?.click()}
                style={{
                  width: "100%", height: 200, border: "2px dashed #E7E5E4", borderRadius: 12,
                  display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                  background: wizardLogo ? "#FAFAF9" : "#F5F5F4",
                  transition: "border-color 0.2s",
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = "#57534E"}
                onMouseOut={(e) => e.currentTarget.style.borderColor = "#E7E5E4"}
              >
                {wizardLogo ? (
                  <img src={wizardLogo} alt="Logo" style={{ maxHeight: 160, maxWidth: "80%", objectFit: "contain" }} />
                ) : (
                  <div style={{ textAlign: "center", color: "#A8A29E" }}>
                    <svg width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{ margin: "0 auto 8px" }}>
                      <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/>
                    </svg>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>Klicka för att ladda upp</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>PNG, JPG eller SVG</div>
                  </div>
                )}
              </div>
              <input ref={wizardLogoInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleWizardLogoUpload} />

              <div style={{ display: "flex", gap: 12, marginTop: 32, justifyContent: "flex-end" }}>
                <button
                  onClick={() => setWizardStep(2)}
                  style={{
                    padding: "10px 24px", fontSize: 14, fontWeight: 600, borderRadius: 8,
                    border: "none", background: "#57534E", color: "#fff", cursor: "pointer",
                  }}
                >
                  {wizardLogo ? "Nästa" : "Hoppa över"}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Template picker */}
          {wizardStep === 2 && (() => {
            // Two-section layout: broker's own templates on top, built-in
            // (global) templates below. savedTemplates from the API already
            // contains both with an is_global flag; starterTemplates is a
            // fallback if the API didn't return any built-ins.
            const ownTemplates = savedTemplates.filter((t) => !t.is_global);
            const builtInFromApi = savedTemplates.filter((t) => t.is_global);
            const builtInTemplates = builtInFromApi.length ? builtInFromApi : starterTemplates;

            const renderTemplateCard = (tpl) => {
              const id = tpl.id;
              const choiceKey = `${tpl.is_global ? "global" : "broker"}:${id}`;
              const selected = wizardSelectedTemplate === choiceKey || wizardSelectedTemplate === id;
              const previewImg = tpl.preview_image;
              const desc = tpl.description || tpl.title || "";
              const sectionCount = (tpl.layout || tpl.sections || []).length;

              return (
                <div
                  key={id}
                  onClick={() => setWizardSelectedTemplate(choiceKey)}
                  style={{
                    borderRadius: 12, cursor: "pointer", overflow: "hidden",
                    border: selected ? "2px solid #57534E" : "1px solid #E7E5E4",
                    background: selected ? "#F5F5F4" : "#fff",
                    transition: "all 0.15s",
                  }}
                >
                  {previewImg ? (
                    <div style={{ height: 160, overflow: "hidden", background: "#F5F5F4" }}>
                      <img src={previewImg} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  ) : (
                    <TemplateThumbnail name={tpl.name} />
                  )}

                  <div style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: "#1C1917" }}>{tpl.name}</div>
                      {tpl.is_default && (
                        <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "#57534E", color: "#fff", fontWeight: 600 }}>
                          Standard
                        </span>
                      )}
                    </div>
                    {desc && <div style={{ fontSize: 13, color: "#78716C", marginTop: 4 }}>{desc}</div>}
                    <div style={{ fontSize: 11, color: "#A8A29E", marginTop: 8 }}>
                      {sectionCount} sektion{sectionCount !== 1 ? "er" : ""}
                      {tpl.is_global && <span> &middot; Global mall</span>}
                    </div>
                  </div>
                </div>
              );
            };

            const sectionHeading = {
              fontSize: 14,
              fontWeight: 600,
              color: "#57534E",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 12,
              marginTop: 8,
            };

            return (
            <div>
              <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1C1917", marginBottom: 8 }}>
                Välj en mall
              </h1>
              <p style={{ fontSize: 15, color: "#78716C", marginBottom: 32, lineHeight: 1.6 }}>
                Börja med en färdig mall och anpassa den efter era behov. Du kan spara er version som standardmall.
              </p>

              {ownTemplates.length > 0 && (
                <div style={{ marginBottom: 28 }}>
                  <div style={sectionHeading}>Egna mallar</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    {ownTemplates.map(renderTemplateCard)}
                  </div>
                </div>
              )}

              <div>
                <div style={sectionHeading}>Byggda mallar</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  {builtInTemplates.map(renderTemplateCard)}
                </div>
              </div>

              <div style={{ display: "flex", gap: 12, marginTop: 32, justifyContent: "space-between" }}>
                <button
                  onClick={() => setWizardStep(1)}
                  style={{
                    padding: "10px 24px", fontSize: 14, fontWeight: 600, borderRadius: 8,
                    border: "1px solid #E7E5E4", background: "#fff", color: "#57534E", cursor: "pointer",
                  }}
                >
                  Tillbaka
                </button>
                <button
                  onClick={handleWizardComplete}
                  style={{
                    padding: "10px 24px", fontSize: 14, fontWeight: 600, borderRadius: 8,
                    border: "none", background: "#57534E", color: "#fff", cursor: "pointer",
                    opacity: wizardSelectedTemplate ? 1 : 0.5,
                  }}
                  disabled={!wizardSelectedTemplate}
                >
                  Börja redigera
                </button>
              </div>
            </div>
            );
          })()}
        </div>
      </Main>
    );
  }

  return (
    <Main navItems={makeCompanyNav(slug)}>
      <IMStudioLifecycleWrap slug={slug}>
      {/* Status pill — bottom-right toast rendered outside the
          editor tree so it's positioned relative to the viewport.
          Fades in on mount, fades out on unmount via CSS
          transitions. Hidden entirely when loadAllStatus is null. */}
      {loadAllStatus ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1000,
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 16px",
            borderRadius: 999,
            background: loadAllStatus.phase === "working"
              ? "#1C1917"
              : "#EFF6FF",
            color: loadAllStatus.phase === "working"
              ? "#F5F5F4"
              : "#1D4ED8",
            fontSize: 13,
            fontWeight: 600,
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            letterSpacing: 0.1,
            animation: "imstudio-status-in 200ms ease-out",
            transition: "opacity 400ms ease-out",
            opacity: loadAllStatus.phase === "fading" ? 0 : 1,
            pointerEvents: loadAllStatus.phase === "fading" ? "none" : "auto",
          }}
        >
          {loadAllStatus.phase === "working" ? (
            <span
              aria-hidden="true"
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                border: "2px solid currentColor",
                borderTopColor: "transparent",
                borderRadius: "50%",
                animation: "imstudio-spin 0.9s linear infinite",
              }}
            />
          ) : (
            <span aria-hidden="true" style={{ fontSize: 14 }}>✓</span>
          )}
          <span>{loadAllStatus.message}</span>
        </div>
      ) : null}
      <div className="imstudio-editor">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {/* ── PM Studio mode switcher ──
              Two pills sitting above the two-pane. Toggles between the
              full IM editor and the anonymised Teaser workflow. Switching
              never mutates IM state — the IM branch just hides so the
              sections array is preserved untouched. */}
          <div
            className="imstudio-editor__mode-switch"
            style={{
              maxWidth: 1680,
              margin: "0 auto 12px auto",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <button
              type="button"
              onClick={() => setPmMode("im")}
              style={{
                padding: "8px 16px",
                borderRadius: 999,
                border: "1px solid #E7E5E4",
                background: pmMode === "im" ? "#1C1917" : "#F5F5F4",
                color: pmMode === "im" ? "#F5F5F4" : "#57534E",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                letterSpacing: 0.1,
              }}
              aria-pressed={pmMode === "im"}
              title="Fullständigt informationsmemorandum"
            >
              Informationsmemorandum
            </button>
            <button
              type="button"
              onClick={() => setPmMode("teaser")}
              style={{
                padding: "8px 16px",
                borderRadius: 999,
                border: "1px solid #E7E5E4",
                background: pmMode === "teaser" ? "#1C1917" : "#F5F5F4",
                color: pmMode === "teaser" ? "#F5F5F4" : "#57534E",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                letterSpacing: 0.1,
              }}
              aria-pressed={pmMode === "teaser"}
              title="Anonymiserad teaser för köparkontakt före NDA"
            >
              Teaser
            </button>
          </div>

          <div className="imstudio-editor__two-pane">
            <aside className="imstudio-editor__dock">
              <div className="imstudio-editor__panel">
                {pmMode === "teaser" ? (
                  <TeaserSidebar
                    slug={slug}
                    teaserDraftId={teaserDraftId}
                    teaserTemplateId={teaserTemplateId}
                    onSelectTemplate={handleSelectTeaserTemplate}
                    teaserBody={teaserBody}
                    teaserAccessToken={teaserAccessToken}
                    teaserLoading={teaserLoading}
                    teaserError={teaserError}
                    onGenerate={handleGenerateTeaser}
                    onApprove={handleApproveTeaser}
                    onDownloadPdf={handleDownloadTeaserPdf}
                  />
                ) : (
                <>
                <button
                  type="button"
                  onClick={handleLoadAll}
                  disabled={!canLoadAll || loadingAll}
                  title={
                    !canLoadAll
                      ? "Kräver att alla underliggande agenter är klara eller redo att köras."
                      : loadingAll
                      ? "Genererar alla sektioner…"
                      : "Generera alla sektioner och skriv dem till IM:en"
                  }
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    marginBottom: 10,
                    borderRadius: 10,
                    border: "1px solid #1C1917",
                    background: !canLoadAll || loadingAll ? "#44403C" : "#1C1917",
                    color: "#FAFAF9",
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: 0.2,
                    cursor: !canLoadAll ? "not-allowed" : loadingAll ? "progress" : "pointer",
                    opacity: !canLoadAll ? 0.6 : 1,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  {loadingAll ? (
                    <>
                      <span
                        aria-hidden="true"
                        style={{
                          width: 12,
                          height: 12,
                          border: "2px solid currentColor",
                          borderTopColor: "transparent",
                          borderRadius: "50%",
                          animation: "imstudio-spin 0.9s linear infinite",
                        }}
                      />
                      <span>
                        {loadAllProgress && loadAllProgress.total > 0
                          ? `Skapar ${loadAllProgress.current} av ${loadAllProgress.total}…`
                          : "Skapar…"}
                      </span>
                    </>
                  ) : (
                    <>
                      <span aria-hidden="true" style={{ fontSize: 15, lineHeight: 1 }}>⚡</span>
                      <span>Ladda in allt</span>
                    </>
                  )}
                </button>
                {loadAllError ? (
                  <div
                    role="alert"
                    style={{
                      marginBottom: 10,
                      padding: "6px 8px",
                      fontSize: 11,
                      color: "#991B1B",
                      background: "#FEF2F2",
                      border: "1px solid #FECACA",
                      borderRadius: 6,
                    }}
                    title={loadAllError}
                  >
                    {loadAllError}
                  </div>
                ) : null}
                <div className="imstudio-editor__tabs">
                  <button
                    type="button"
                    className={`imstudio-editor__tab ${activeTab === "prewritten" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("prewritten")}
                  >
                    Prewritten
                  </button>
                  <button
                    type="button"
                    className={`imstudio-editor__tab ${activeTab === "blocks" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("blocks")}
                  >
                    Blocks
                  </button>
                  <button
                    type="button"
                    className={`imstudio-editor__tab ${activeTab === "brand" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("brand")}
                  >
                    Brand
                  </button>
                </div>

                {activeTab === "prewritten" ? (
                  <div className="imstudio-editor__tab-pane imstudio-editor__tab-pane--prewritten">
                    {/* Chat-likt header band — gör att AI-panelen känns som
                        en konversation med en assistent istället för en
                        formulärlista. Liten avatar + status, sen korten. */}
                    <div style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 12px", marginBottom: 14,
                      background: "linear-gradient(135deg, #F0F9FF 0%, #EFF6FF 100%)",
                      border: "1px solid #BFDBFE", borderRadius: 14,
                    }}>
                      <div style={{
                        flexShrink: 0, width: 32, height: 32, borderRadius: "50%",
                        background: "#0EA5E9", color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 14, fontWeight: 700,
                      }}>AI</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#0C4A6E" }}>
                          AI-assistent
                        </div>
                        <div style={{ fontSize: 11, color: "#0369A1" }}>
                          Jag förbereder textsektionerna åt dig — dra in dom i dokumentet när du är nöjd.
                        </div>
                      </div>
                    </div>

                    {prewrittenLoading ? (
                      <div className="imstudio-editor__status-box imstudio-editor__status-box--loading">
                        Loading saved text…
                      </div>
                    ) : null}

                    {prewrittenError ? (
                      <div className="imstudio-editor__status-box imstudio-editor__status-box--error">
                        {prewrittenError}
                      </div>
                    ) : null}

                    {!prewrittenLoading && !prewrittenError && prewrittenItems.length === 0 ? (
                      <div className="imstudio-editor__status-box imstudio-editor__status-box--empty">
                        No prewritten sections found.
                      </div>
                    ) : null}

                    <div className="imstudio-editor__button-list imstudio-editor__button-list--prewritten">
                      {/* Faktagranskning — hidden when no issues. Sits as
                          the first card so the broker sees pending review
                          items before scanning the section list. */}
                      {consistency && Array.isArray(consistency.issues) && consistency.issues.length > 0 && (
                        <ConsistencyCard
                          report={consistency}
                          orgnr={slug}
                          onUpdate={setConsistency}
                          expanded={expandedPrewrittenId === "__consistency__"}
                          onToggleExpanded={() => setExpandedPrewrittenId(
                            (cur) => (cur === "__consistency__" ? null : "__consistency__"),
                          )}
                        />
                      )}
                      {prewrittenItems.map((item) => (
                        <PrewrittenCard
                          key={item.id}
                          id={item.id}
                          item={item}
                          orgnr={slug}
                          progress={prewrittenProgress[item.sectionId] || null}
                          isWorking={workingPrewrittenId === item.sectionId}
                          onPrepare={() => handlePreparePrewritten(item)}
                          onInsert={() => insertPrewrittenWithAnimation(item)}
                          expanded={expandedPrewrittenId === item.sectionId}
                          onToggleExpanded={() => setExpandedPrewrittenId(
                            (cur) => (cur === item.sectionId ? null : item.sectionId),
                          )}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                {activeTab === "blocks" ? (
                  <div className="imstudio-editor__tab-pane">
                    <h1 className="imstudio-editor__heading">Blocks</h1>
                    <p className="imstudio-editor__subtext">
                      Drag section blocks from here into the page.
                    </p>

                    {/* Templates were previously listed here too (Deal teaser / Simple newsletter
                        / saved templates). They've been removed — full-page templates live in the
                        template picker step before the editor opens, not inside the editor itself,
                        so the broker can't accidentally swap out their entire layout mid-edit. */}

                    <div className="imstudio-editor__section">
                      <div className="imstudio-editor__section-title">Drag into canvas</div>
                      <div className="imstudio-editor__button-list">
                        {Object.keys(sectionLibrary).map((key) => (
                          <LibraryDragItem
                            key={key}
                            id={`library-${key}`}
                            label={sectionLibrary[key].label}
                            data={{
                              source: "library",
                              sectionType: key,
                              label: sectionLibrary[key].label,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}

                {activeTab === "brand" ? (
                  <div className="imstudio-editor__tab-pane">
                    <h1 className="imstudio-editor__heading">Brand & export</h1>
                    <p className="imstudio-editor__subtext">Manage title, logo, and export.</p>

                    <label className="imstudio-editor__field">
                      <span className="imstudio-editor__label">Document title</span>
                      <input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="imstudio-editor__input"
                      />
                    </label>

                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="imstudio-editor__upload-button"
                    >
                      <ImageIcon size={16} />
                      Upload logo
                    </button>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="imstudio-editor__hidden-input"
                      onChange={handleLogoUpload}
                    />

                    <details style={{ marginTop: 18, borderTop: "1px solid #e7e5e4", paddingTop: 14 }}>
                      <summary style={{ cursor: "pointer", fontWeight: 700, color: "#1c1917" }}>
                        Broker presentation profile {presentationProfile?.version ? `· v${presentationProfile.version}` : ""}
                      </summary>
                      <p style={{ fontSize: 12, color: "#78716c", lineHeight: 1.45 }}>
                        Saved for future IMs for this brokerage. These settings control appearance and order only.
                      </p>
                      {(() => {
                        const draft = presentationProfileDraft || presentationProfile?.configuration || {};
                        const branding = draft.branding || {};
                        const charts = draft.chart || {};
                        const cover = draft.cover || {};
                        const layout = draft.layout || {};
                        const ordered = (layout.section_order || PRESENTATION_SECTION_LABELS.map(([id]) => id))
                          .filter((id) => PRESENTATION_SECTION_LABELS.some(([known]) => known === id));
                        const moveProfileSection = (index, delta) => {
                          const target = index + delta;
                          if (target < 0 || target >= ordered.length) return;
                          const next = [...ordered];
                          [next[index], next[target]] = [next[target], next[index]];
                          updatePresentationDraft("layout", "section_order", next);
                        };
                        const colorField = (group, key, label, value) => (
                          <label key={`${group}-${key}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 12, margin: "6px 0" }}>
                            {label}<input type="color" value={value || "#1c2d50"} onChange={(e) => updatePresentationDraft(group, key, e.target.value)} aria-label={label} />
                          </label>
                        );
                        return (
                          <div style={{ display: "grid", gap: 10, fontSize: 12 }}>
                            <div style={{ padding: 10, border: "1px solid #e7e5e4", display: "grid", gap: 4 }}>
                              <strong>Branding & typografi</strong>
                              {colorField("branding", "primary_color", "Primärfärg", branding.primary_color)}
                              {colorField("branding", "secondary_color", "Sekundärfärg", branding.secondary_color)}
                              {colorField("branding", "accent_color", "Accentfärg", branding.accent_color)}
                              {colorField("branding", "text_color", "Textfärg", branding.text_color)}
                              {colorField("branding", "background_color", "Bakgrund", branding.background_color)}
                              <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                                Rubriktypsnitt
                                <select value={branding.font_heading || "Arial"} onChange={(e) => updatePresentationDraft("branding", "font_heading", e.target.value)}>
                                  {["Arial", "Helvetica", "Georgia", "Times New Roman", "Inter"].map((font) => <option key={font}>{font}</option>)}
                                </select>
                              </label>
                              <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                                Brödtext
                                <select value={branding.font_body || "Arial"} onChange={(e) => updatePresentationDraft("branding", "font_body", e.target.value)}>
                                  {["Arial", "Helvetica", "Georgia", "Times New Roman", "Inter"].map((font) => <option key={font}>{font}</option>)}
                                </select>
                              </label>
                            </div>
                            <div style={{ padding: 10, border: "1px solid #e7e5e4" }}>
                              <strong>Diagramfärger</strong>
                              {colorField("chart", "revenue_color", "Omsättning", charts.revenue_color)}
                              {colorField("chart", "ebitda_color", "EBITDA", charts.ebitda_color)}
                              {colorField("chart", "margin_color", "Marginal", charts.margin_color)}
                              {colorField("chart", "grid_color", "Rutnät / axlar", charts.grid_color)}
                            </div>
                            <div style={{ padding: 10, border: "1px solid #e7e5e4", display: "grid", gap: 8 }}>
                              <strong>Omslag & fasta bilder</strong>
                              <label style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                Omslagsstil
                                <select value={cover.style || "editorial"} onChange={(e) => updatePresentationDraft("cover", "style", e.target.value)}>
                                  <option value="editorial">Redaktionell</option><option value="image">Med omslagsbild</option><option value="minimal">Minimal</option>
                                </select>
                              </label>
                              <label style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                Justering
                                <select value={cover.alignment || "left"} onChange={(e) => updatePresentationDraft("cover", "alignment", e.target.value)}>
                                  <option value="left">Vänster</option><option value="center">Centrerad</option><option value="right">Höger</option>
                                </select>
                              </label>
                              <input type="text" value={cover.confidentiality_label || ""} maxLength={100} aria-label="Sekretessrad" onChange={(e) => updatePresentationDraft("cover", "confidentiality_label", e.target.value)} placeholder="Sekretessrad" />
                              <input type="text" value={cover.subtitle || ""} maxLength={180} aria-label="Omslagsunderrubrik" onChange={(e) => updatePresentationDraft("cover", "subtitle", e.target.value)} placeholder="Valfri underrubrik" />
                              <label><input type="checkbox" checked={cover.show_date !== false} onChange={(e) => updatePresentationDraft("cover", "show_date", e.target.checked)} /> Visa datum</label>
                              <label>Omslagsbild<input type="file" accept="image/*" onChange={(e) => handlePresentationAssetUpload(e, "cover")} /></label>
                              <label>Sektionsbild<input type="file" accept="image/*" onChange={(e) => handlePresentationAssetUpload(e, "section")} /></label>
                              {presentationProfile?.assets?.cover && <img src={presentationProfile.assets.cover} alt="Omslagsbild" style={{ width: "100%", maxHeight: 100, objectFit: "cover" }} />}
                            </div>
                            <div style={{ padding: 10, border: "1px solid #e7e5e4" }}>
                              <strong>Föredragen sektionsordning</strong>
                              {ordered.map((id, index) => (
                                <div key={id} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 5 }}>
                                  <span style={{ flex: 1 }}>{PRESENTATION_SECTION_LABELS.find(([key]) => key === id)?.[1] || id}</span>
                                  <button type="button" onClick={() => moveProfileSection(index, -1)} disabled={!index} aria-label="Flytta upp">↑</button>
                                  <button type="button" onClick={() => moveProfileSection(index, 1)} disabled={index === ordered.length - 1} aria-label="Flytta ner">↓</button>
                                </div>
                              ))}
                              <label style={{ display: "block", marginTop: 8 }}><input type="checkbox" checked={layout.header_enabled || false} onChange={(e) => updatePresentationDraft("layout", "header_enabled", e.target.checked)} /> Sidhuvud</label>
                              <label style={{ display: "block" }}><input type="checkbox" checked={layout.footer_enabled !== false} onChange={(e) => updatePresentationDraft("layout", "footer_enabled", e.target.checked)} /> Sidfot / sidnumrering</label>
                              <label style={{ display: "block", marginTop: 6 }}>Marginal
                                <input type="range" min="30" max="80" value={layout.margin_x || 48} onChange={(e) => updatePresentationDraft("layout", "margin_x", Number(e.target.value))} />
                              </label>
                            </div>
                            <div style={{ padding: 12, background: branding.background_color || "#fff", border: `2px solid ${branding.primary_color || "#1c2d50"}`, color: branding.text_color || "#172033", fontFamily: branding.font_body || "Arial" }}>
                              <strong style={{ color: branding.primary_color }}>Profilförhandsvisning</strong>
                              <span style={{ float: "right", width: 18, height: 18, background: branding.accent_color }} />
                              <div style={{ marginTop: 8 }}>Samma bolagsfakta, brokerprofilens visuella identitet.</div>
                            </div>
                            {presentationProfileError && <div role="alert" style={{ color: "#b91c1c" }}>{presentationProfileError}</div>}
                            <button type="button" onClick={persistPresentationProfile} disabled={presentationProfileSaving} style={{ padding: 9, background: branding.primary_color || "#1c2d50", color: "white", border: 0, cursor: "pointer" }}>
                              {presentationProfileSaving ? "Sparar…" : "Spara brokerprofil"}
                            </button>
                          </div>
                        );
                      })()}
                    </details>

                    <button
                      type="button"
                      onClick={exportPdf}
                      disabled={exporting}
                      className="imstudio-editor__export-button"
                    >
                      <Download size={16} />
                      {exporting ? "Exporting PDF..." : "Export PDF"}
                    </button>
                  </div>
                ) : null}
                </>
                )}
              </div>
            </aside>

            <section className="imstudio-editor__canvas-shell">
              {pmMode === "im" ? (
              <div
                className="imstudio-editor__canvas-toolbar imstudio-editor__canvas-toolbar--responsive"
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {/* ─── Row 1: StyleToolbar + primary PM-actions ─────
                    Bryts till två-rader på smala skärmar via
                    imstudio-editor__toolbar-row CSS-klassen — annars
                    klipps Färdigställ PM på 768px-skärmar. */}
                <div
                  className="imstudio-editor__toolbar-row"
                  style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}
                >
                  <div
                    className="imstudio-editor__toolbar-style"
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    {selectedSection ? (
                      <StyleToolbar
                        section={selectedSection}
                        onChange={(patch) => updateSection(selectedSection.id, patch)}
                        onDelete={() => removeSection(selectedSection.id)}
                      />
                    ) : (
                      <div className="imstudio-editor__toolbar imstudio-editor__toolbar--empty">
                        Select a section in the preview to edit it
                      </div>
                    )}
                  </div>

                  <div
                    className="imstudio-editor__toolbar-actions"
                    style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}
                  >
                  <button
                    type="button"
                    className="imstudio-editor__action-btn imstudio-editor__action-btn--ghost"
                    onClick={() => setPreviewMode((v) => !v)}
                    style={{ flexShrink: 0 }}
                    title="Visa PM som mottagaren ser den (göm editor-chrome)"
                  >
                    {previewMode ? "✏ Redigera" : "👁 Förhandsgranska"}
                  </button>

                  <button
                    type="button"
                    className="imstudio-editor__action-btn imstudio-editor__action-btn--ghost"
                    onClick={downloadPdfLocal}
                    disabled={exporting}
                    style={{ flexShrink: 0 }}
                    title="Ladda ner PM som PDF till din dator"
                  >
                    {exporting ? "Renderar…" : "Ladda ner PM"}
                  </button>

                  <button
                    type="button"
                    className="imstudio-editor__action-btn"
                    onClick={async () => {
                      // Save PDF on backend FIRST. Only mark as finalized if
                      // the upload succeeded — otherwise pipeline_send_pm will
                      // refuse with "Ingen PM-PDF hittades". Previous version
                      // ran upload and finalize in parallel which masked upload
                      // failures behind a happy finalize response.
                      const uploaded = await exportPdf();
                      if (!uploaded) return;  // exportPdf already alerted

                      try {
                        await apiFetch(`/im-studio/company/${encodeURIComponent(slug)}/finalize/`, {
                          method: "POST",
                          body: { finalized: true },
                        });
                        alert("PM färdigställt. Du kan nu skicka det till köpare i pipelinen.");
                      } catch (e) {
                        console.error("Finalize failed", e);
                        alert("PDF sparades men kunde inte markeras som färdigställd — försök igen.");
                      }
                    }}
                    disabled={exporting}
                    style={{
                      flexShrink: 0,
                      background: "#1C1917",
                      color: "#fff",
                    }}
                    title="Markera PM som färdigställt och spara PDF för utskick via pipeline"
                  >
                    {exporting ? "Sparar…" : "Färdigställ PM"}
                  </button>
                  </div>
                </div>

                {/* Row 2: template actions. "Ladda in allt" lives in the
                    left dock now so it stays visible while scrolling. */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="imstudio-editor__action-btn imstudio-editor__action-btn--ghost"
                    onClick={() => {
                      setWizardStep(2);
                      setShowWizard(true);
                    }}
                    style={{ flexShrink: 0 }}
                    title="Välj en annan mall för detta projekt"
                  >
                    Välj ny mall
                  </button>

                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <button
                      type="button"
                      className="imstudio-editor__action-btn"
                      onClick={() => setSaveModalOpen((v) => !v)}
                      title="Spara nuvarande layout som en mall"
                    >
                      Spara mall
                    </button>
                    {saveModalOpen && (
                      <div
                        className="imstudio-save-modal"
                        style={{
                          position: "absolute",
                          top: "calc(100% + 8px)",
                          left: 0,
                          zIndex: 50,
                          minWidth: 280,
                          background: "#FFFFFF",
                          border: "1px solid #E7E5E4",
                          borderRadius: 8,
                          padding: 12,
                          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                        }}
                      >
                        <input
                          className="imstudio-save-input"
                          placeholder="Mallnamn…"
                          value={saveTemplateName}
                          onChange={(e) => setSaveTemplateName(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleSaveTemplate()}
                          autoFocus
                        />
                        <div className="imstudio-save-actions" style={{ marginTop: 8 }}>
                          <button
                            className="imstudio-editor__action-btn"
                            onClick={handleSaveTemplate}
                            disabled={saving || !saveTemplateName.trim()}
                          >
                            {saving ? "Sparar…" : "Spara"}
                          </button>
                          <button
                            className="imstudio-editor__action-btn imstudio-editor__action-btn--ghost"
                            onClick={() => { setSaveModalOpen(false); setSaveTemplateName(""); }}
                          >
                            Avbryt
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              </div>
              ) : null}

              <div className="imstudio-editor__preview-shell">
                <div className="imstudio-editor__preview-topline">
                  <LayoutTemplate size={16} />
                  {pmMode === "teaser" ? "Teaser preview" : "Live preview"}
                </div>

                {pmMode === "im" ? (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                  <CanvasDropZone>
                    <div
                      ref={previewRef}
                      className={`imstudio-editor__preview-page${previewMode ? " is-preview-mode" : ""}`}
                      style={{ maxWidth: PAGE_WIDTH, position: "relative" }}
                    >
                      {/* PageBreakOverlay removed — real pagination (pageGroups
                          below) renders distinct page containers with their own
                          "Sida X" gap dividers, so the soft-pagination overlay is
                          redundant. */}
                      <style>{`
                        .imstudio-editor__preview-page.is-preview-mode [data-pdf-hide],
                        .imstudio-editor__preview-page.is-preview-mode .imstudio-editor__hover-toolbar,
                        .imstudio-editor__preview-page.is-preview-mode .imstudio-editor__drag-handle,
                        .imstudio-editor__preview-page.is-preview-mode .imstudio-editor__block-topbar {
                          display: none !important;
                        }
                        .imstudio-editor__preview-page.is-preview-mode .imstudio-editor__preview-block--selected {
                          outline: none !important;
                        }
                      `}</style>
                      <SortableContext
                        items={sections.map((section) => section.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {/* Real pagination (Option B):
                            Each section renders in its own A4-sized "page
                            card" with white bg, border and shadow, separated
                            by a REAL DOM gap containing the "SIDA N" label.
                            The old PageBreakOverlay drew the SIDA band as an
                            absolute overlay on top of flowing content, which
                            sliced through text whenever a section was taller
                            than one A4. These page cards never overlap the
                            label, so text is never cut. Sections that
                            overflow one A4 grow the card — the broker sees
                            that the content won't fit a page and can split
                            it manually. */}
                        <div
                          className="imstudio-editor__pages-container"
                          ref={previewRef}
                          style={{
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            gap: 24,
                          }}
                        >
                          <InsertZone
                            onInsert={(type) => insertSectionAtIndex(createSectionBlock(type), 0)}
                          />
                          {sections.map((section, globalIdx) => (
                            <React.Fragment key={section.id}>
                              <div
                                className="imstudio-editor__page-section-wrap"
                                ref={(el) => { sectionDomRefs.current[section.id] = el; }}
                                style={{
                                  minHeight: PAGE_HEIGHT_PX,
                                  background: "#ffffff",
                                  border: "1px solid #E7E5E4",
                                  borderRadius: 4,
                                  boxShadow: "0 1px 3px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.04)",
                                  overflow: "hidden",
                                }}
                              >
                                <SortableSectionBlock
                                  section={section}
                                  isSelected={section.id === selectedId}
                                  isFirst={globalIdx === 0}
                                  isLast={globalIdx === sections.length - 1}
                                  basePage={basePages[globalIdx] || 1}
                                  onSelect={() => setSelectedId(section.id)}
                                  onChildChange={(childId, patch) => updateChild(section.id, childId, patch)}
                                  onDelete={() => removeSection(section.id)}
                                  onDuplicate={() => duplicateSection(section.id)}
                                  onMoveUp={() => moveSectionDir(section.id, "up")}
                                  onMoveDown={() => moveSectionDir(section.id, "down")}
                                />
                              </div>
                              {globalIdx < sections.length - 1 ? (
                                <div
                                  data-pdf-hide
                                  aria-hidden="true"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 12,
                                    padding: "4px 0",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.6,
                                    color: "#78716C",
                                  }}
                                >
                                  <span style={{ flex: 1, height: 1, background: "#D6D3D1", maxWidth: 160 }} />
                                  <span>Sida {basePages[globalIdx + 1] || globalIdx + 2}</span>
                                  <span style={{ flex: 1, height: 1, background: "#D6D3D1", maxWidth: 160 }} />
                                </div>
                              ) : null}
                              <InsertZone
                                onInsert={(type) => insertSectionAtIndex(createSectionBlock(type), globalIdx + 1)}
                              />
                            </React.Fragment>
                          ))}
                        </div>
                      </SortableContext>
                    </div>
                  </CanvasDropZone>
                  </div>
                ) : (
                  <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
                    <div style={{ width: "100%", maxWidth: 640 }}>
                      <TeaserCanvas
                        teaserBody={teaserBody}
                        teaserDraftId={teaserDraftId}
                        editable={!!teaserDraftId && !teaserAccessToken}
                        onBodyChange={handleTeaserBodyChange}
                        pageWidth={640}
                      />
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>

          <DragOverlay>
            {activeDrag ? <DragGhost label={activeDrag.label || activeDrag.heading || "Section"} /> : null}
          </DragOverlay>
        </DndContext>
      </div>
      </IMStudioLifecycleWrap>
    </Main>
  );
}

function IMStudioLifecycleWrap({ slug, children }) {
  const { workspace, reload } = useWorkspaceLifecycle(slug);
  return (
    <>
      <WorkspaceCloseBanner workspace={workspace} />
      <WorkspaceLockOverlay workspace={workspace} onActivate={reload}>
        {children}
      </WorkspaceLockOverlay>
    </>
  );
}

// SVG-based mini preview for each built-in template. Three distinct
// silhouettes so the broker can see at a glance what each layout looks
// like without rendering the real PM. Falls through to a neutral grid
// for user-saved templates.
function TemplateThumbnail({ name }) {
  if (name === "Visuell") {
    return (
      <div style={{ height: 160, background: "#FFFBEB", padding: 16 }}>
        <svg viewBox="0 0 100 80" style={{ width: "100%", height: "100%" }}>
          <rect x="0" y="0" width="100" height="20" rx="2" fill="#FCD34D" />
          <rect x="0" y="24" width="58" height="36" rx="2" fill="#fff" stroke="#E5E7EB" />
          <circle cx="29" cy="42" r="8" fill="#D1D5DB" />
          <rect x="62" y="24" width="38" height="8" rx="1" fill="#1F2937" />
          <rect x="62" y="36" width="38" height="3" rx="1" fill="#9CA3AF" />
          <rect x="62" y="41" width="30" height="3" rx="1" fill="#9CA3AF" />
          <rect x="62" y="46" width="34" height="3" rx="1" fill="#9CA3AF" />
          <rect x="62" y="51" width="20" height="6" rx="1" fill="#1F2937" />
          <rect x="0" y="64" width="100" height="2" rx="1" fill="#E5E7EB" />
          <rect x="0" y="70" width="48" height="10" rx="1" fill="#fff" stroke="#E5E7EB" />
          <rect x="52" y="70" width="48" height="10" rx="1" fill="#fff" stroke="#E5E7EB" />
        </svg>
      </div>
    );
  }
  if (name === "Premium") {
    return (
      <div style={{ height: 160, background: "#0F172A", padding: 16 }}>
        <svg viewBox="0 0 100 80" style={{ width: "100%", height: "100%" }}>
          <rect x="0" y="0" width="100" height="16" rx="1" fill="#FBBF24" />
          <rect x="0" y="20" width="100" height="22" rx="1" fill="#1E293B" stroke="#FBBF24" strokeWidth="0.5" />
          <rect x="6" y="26" width="40" height="3" rx="1" fill="#FBBF24" />
          <rect x="6" y="32" width="60" height="2" rx="1" fill="#94A3B8" />
          <rect x="6" y="36" width="55" height="2" rx="1" fill="#94A3B8" />
          <rect x="0" y="46" width="48" height="34" rx="1" fill="#1E293B" />
          <rect x="52" y="46" width="48" height="34" rx="1" fill="#1E293B" />
          <rect x="6" y="52" width="20" height="2" rx="1" fill="#FBBF24" />
          <rect x="58" y="52" width="20" height="2" rx="1" fill="#FBBF24" />
        </svg>
      </div>
    );
  }
  if (name === "Klassisk") {
    return (
      <div style={{ height: 160, background: "#FAFAF9", padding: 16 }}>
        <svg viewBox="0 0 100 80" style={{ width: "100%", height: "100%" }}>
          <rect x="0" y="0" width="100" height="80" rx="2" fill="#fff" stroke="#E7E5E4" />
          <rect x="6" y="6" width="40" height="4" rx="1" fill="#1C1917" />
          <rect x="6" y="14" width="88" height="2" rx="1" fill="#A8A29E" />
          <rect x="6" y="18" width="88" height="2" rx="1" fill="#A8A29E" />
          <rect x="6" y="22" width="60" height="2" rx="1" fill="#A8A29E" />
          <rect x="6" y="32" width="40" height="3" rx="1" fill="#1C1917" />
          <rect x="6" y="38" width="88" height="2" rx="1" fill="#A8A29E" />
          <rect x="6" y="42" width="88" height="2" rx="1" fill="#A8A29E" />
          <rect x="6" y="46" width="70" height="2" rx="1" fill="#A8A29E" />
          <rect x="6" y="56" width="40" height="3" rx="1" fill="#1C1917" />
          <rect x="6" y="62" width="88" height="2" rx="1" fill="#A8A29E" />
          <rect x="6" y="66" width="88" height="2" rx="1" fill="#A8A29E" />
          <rect x="6" y="70" width="50" height="2" rx="1" fill="#A8A29E" />
        </svg>
      </div>
    );
  }
  // Fallback for user-saved templates
  return (
    <div style={{ height: 160, background: "#F5F5F4", display: "flex",
                  alignItems: "center", justifyContent: "center" }}>
      <svg width="32" height="32" fill="none" stroke="#A8A29E" strokeWidth="1.5" viewBox="0 0 24 24">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <line x1="3" y1="9" x2="21" y2="9"/>
        <line x1="9" y1="3" x2="9" y2="21"/>
      </svg>
    </div>
  );
}

// Slim "+"-zone rendered between rows in the canvas. Invisible at rest
// (just a thin line on hover) so it doesn't add visual noise to a busy
// PM; clicking expands a tiny picker with every section type from the
// library. Marked data-pdf-hide so html2canvas skips it for PDF export.
function InsertZone({ onInsert }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  return (
    <div
      data-pdf-hide
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        height: open ? 64 : 16,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "height 0.15s",
      }}
    >
      {/* Faint horizontal line — fully visible on hover, hairline at rest */}
      <div
        style={{
          position: "absolute",
          left: 8,
          right: 8,
          height: 2,
          background: hover || open ? "#1c1917" : "transparent",
          borderRadius: 1,
          transition: "background 0.15s",
        }}
      />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Lägg till block här"
        style={{
          position: "relative",
          zIndex: 2,
          width: 28,
          height: 28,
          borderRadius: "50%",
          border: "none",
          background: hover || open ? "#1c1917" : "rgba(28,25,23,0.4)",
          color: "#fff",
          fontSize: 16,
          fontWeight: 700,
          cursor: "pointer",
          opacity: hover || open ? 1 : 0,
          transition: "opacity 0.15s, background 0.15s",
        }}
      >
        +
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            marginTop: 4,
            padding: 8,
            background: "#fff",
            border: "1px solid #e7e5e4",
            borderRadius: 10,
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            minWidth: 240,
          }}
          onMouseLeave={() => setOpen(false)}
        >
          {Object.keys(sectionLibrary).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => { onInsert(key); setOpen(false); }}
              style={{
                padding: "8px 10px",
                fontSize: 12,
                fontWeight: 500,
                background: "#fafaf9",
                border: "1px solid #e7e5e4",
                borderRadius: 6,
                cursor: "pointer",
                color: "#1c1917",
                whiteSpace: "nowrap",
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = "#1c1917"; e.currentTarget.style.color = "#fff"; }}
              onMouseOut={(e) => { e.currentTarget.style.background = "#fafaf9"; e.currentTarget.style.color = "#1c1917"; }}
            >
              {sectionLibrary[key].label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LibraryDragItem({ id, label, data }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <button
      ref={setNodeRef}
      style={style}
      type="button"
      className="imstudio-editor__add-button"
      {...listeners}
      {...attributes}
    >
      <span>{label}</span>
      <Plus size={15} />
    </button>
  );
}

// ─── Underlag (källdata) renderer per agent type ─────────────────────────────
// Renders the same context the agent's prompt receives, so the broker can
// sanity-check what the AI is allowed to say. The validator only accepts
// numbers from this same payload, so this view is "what the agent knows".

function _fmtPct(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1).replace(".", ",")} %`;
}

function _fmtMSEK(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return `${n.toFixed(1).replace(".", ",")} MSEK`;
}

function _fmtMultiple(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return `${n.toFixed(1).replace(".", ",")}×`;
}

function FactSourcePanelFinancial({ ctx }) {
  if (!ctx) return null;
  const periods = Array.isArray(ctx.periods) ? ctx.periods : [];
  const meta = ctx.meta || {};
  const sie = ctx.sie_ltm || null;
  return (
    <div style={{ fontSize: 12, color: "#374151" }}>
      {meta.revenue_cagr_pct != null && (
        <div style={{ marginBottom: 6 }}>
          <strong>CAGR omsättning ({meta.revenue_cagr_years || "?"} år):</strong>{" "}
          <span style={{ color: "#047857", fontWeight: 600 }}>{_fmtPct(meta.revenue_cagr_pct)}</span>
        </div>
      )}
      {periods.length > 0 && (
        <div style={{ overflowX: "auto", marginBottom: 6 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 11, width: "100%" }}>
            <thead>
              <tr style={{ background: "#F3F4F6", textAlign: "left" }}>
                <th style={{ padding: "4px 6px" }}>År</th>
                <th style={{ padding: "4px 6px", textAlign: "right" }}>Omsättning</th>
                <th style={{ padding: "4px 6px", textAlign: "right" }}>YoY</th>
                <th style={{ padding: "4px 6px", textAlign: "right" }}>RAFI-marginal</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p, i) => (
                <tr key={i} style={{ borderTop: "1px solid #e5e7eb" }}>
                  <td style={{ padding: "4px 6px" }}>{p.fiscal_year_label || "—"}</td>
                  <td style={{ padding: "4px 6px", textAlign: "right" }}>{_fmtMSEK(p.revenue_msek)}</td>
                  <td style={{ padding: "4px 6px", textAlign: "right", color: p.revenue_yoy_pct >= 0 ? "#047857" : "#B91C1C" }}>
                    {p.revenue_yoy_pct == null ? "—" : _fmtPct(p.revenue_yoy_pct)}
                  </td>
                  <td style={{ padding: "4px 6px", textAlign: "right" }}>
                    {p.rafi_margin_pct == null ? "—" : _fmtPct(p.rafi_margin_pct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {sie && (sie.ltm_total_revenue_msek != null) && (
        <div>
          <strong>SIE LTM:</strong> {_fmtMSEK(sie.ltm_total_revenue_msek)}
          {sie.top_n_share != null && ` • topp ${sie.top_n || "?"}: ${_fmtPct(sie.top_n_share * 100)}`}
        </div>
      )}
    </div>
  );
}

function FactSourcePanelValuation({ ctx }) {
  if (!ctx) return null;
  const fs = ctx.factor_summary || {};
  const comps = ctx.branch_comps_range || null;
  const v = ctx.valuation || {};
  const factorChip = (label, summary) => (
    <span
      key={label}
      style={{
        display: "inline-block", padding: "3px 8px", marginRight: 6, marginBottom: 4,
        background: "#F3F4F6", border: "1px solid #E5E7EB", borderRadius: 999, fontSize: 11,
      }}
    >
      <span style={{ color: "#6b7280" }}>{label}</span>
      <span style={{ marginLeft: 6, color: "#111827" }}>{summary || "—"}</span>
    </span>
  );
  return (
    <div style={{ fontSize: 12, color: "#374151" }}>
      <div style={{ marginBottom: 6 }}>
        {factorChip("Storlek", fs.size_factor_summary)}
        {factorChip("Tillväxt", fs.growth_factor_summary)}
        {factorChip("Lönsamhet", fs.profitability_factor_summary)}
        {factorChip("Risk", fs.risk_factor_summary)}
      </div>
      <div style={{ marginBottom: 6 }}>
        <strong>Multipel:</strong> pre-risk {_fmtMultiple(v.pre_risk_multiple)} • justerad {_fmtMultiple(v.adjusted_multiple)}
      </div>
      {comps ? (
        <div
          style={{
            padding: "6px 10px", background: "#EFF6FF", border: "1px solid #BFDBFE",
            borderRadius: 8, fontSize: 11,
          }}
        >
          <strong>Peer-multiplar ({comps.metric}):</strong>{" "}
          låg {_fmtMultiple(comps.low)} • median {_fmtMultiple(comps.median)} • hög {_fmtMultiple(comps.high)}
          {comps.source && <span style={{ color: "#6b7280", marginLeft: 6 }}>• {comps.source}</span>}
        </div>
      ) : (
        <div style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 11 }}>
          Inga peer-multiplar i databasen för {v.branch_label || v.branch_key || "denna bransch"} —
          AI hoppar branschjämförelse.
        </div>
      )}
    </div>
  );
}

function FactSourcePanelRisk({ ctx }) {
  if (!ctx) return null;
  const scores = ctx.scores || {};
  const labels = {
    key_person_risk: "Nyckelperson",
    customer_concentration: "Kundkoncentration",
    supplier_dependency: "Leverantör",
    contract_quality: "Avtalskvalitet",
    financial_health: "Finansiell hälsa",
    compliance_risk: "Compliance",
  };
  const level = ctx.meta?.qualitative_level || "—";
  const has = !!ctx.has_profile;
  return (
    <div style={{ fontSize: 12, color: "#374151" }}>
      <div style={{ marginBottom: 6 }}>
        <strong>Riskprofil:</strong> {has ? level : "saknas"}
        {has && scores.total_risk_score != null && ` (totalt ${scores.total_risk_score})`}
      </div>
      {has ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
          {Object.entries(labels).map(([k, lbl]) => {
            const v = scores[k] || 0;
            const tone = v >= 4 ? "#B91C1C" : v >= 3 ? "#92400E" : v > 0 ? "#047857" : "#9ca3af";
            return (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "2px 6px", background: "#F9FAFB", borderRadius: 4 }}>
                <span>{lbl}</span>
                <span style={{ color: tone, fontWeight: 600 }}>{v > 0 ? `${v}/5` : "—"}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 11 }}>
          Riskprofilen är inte ifylld — AI använder enbart aggregerade signaler (revenue, top_n_share m.m.).
        </div>
      )}
    </div>
  );
}

function FactSourcePanelWeb({ ctx }) {
  if (!ctx) return null;
  const urls = Array.isArray(ctx.selected_urls) ? ctx.selected_urls : [];
  const sitemaps = Array.isArray(ctx.sitemaps) ? ctx.sitemaps : [];
  const quality = ctx.quality || {};
  return (
    <div style={{ fontSize: 12, color: "#374151" }}>
      <div style={{ marginBottom: 6 }}>
        <strong>Källa:</strong> {ctx.base_url || "—"}
      </div>
      <div style={{ marginBottom: 6 }}>
        <strong>Skrapade sidor:</strong> {urls.length} av {sitemaps.length} sitemap-källor
        {quality.total_chars != null && ` • ${quality.total_chars} tecken totalt`}
      </div>
      {urls.length > 0 && (
        <details>
          <summary style={{ cursor: "pointer", color: "#6b7280" }}>Visa URL-lista</summary>
          <ul style={{ margin: "4px 0 0 0", paddingLeft: 16, fontSize: 11, maxHeight: 160, overflowY: "auto" }}>
            {urls.slice(0, 30).map((u, i) => (
              <li key={i} style={{ wordBreak: "break-all" }}>{u}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function FactSourcePanel({ commentType, ctx }) {
  if (commentType === "financials") return <FactSourcePanelFinancial ctx={ctx} />;
  if (commentType === "valuation") return <FactSourcePanelValuation ctx={ctx} />;
  if (commentType === "risks") return <FactSourcePanelRisk ctx={ctx} />;
  if (commentType === "web_profile") return <FactSourcePanelWeb ctx={ctx} />;
  return null;
}

// ─── Diagnostics drawer (right side) ─────────────────────────────────────────
// Verbose detail (gateFields, whyDisabled, agent timestamps, validator
// warnings) lives here so the inline card stays at-a-glance.

function DiagnosticsDrawer({ open, onClose, item }) {
  if (!open || !item) return null;
  return (
    <div
      style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: "min(420px, 90vw)",
        background: "#fff", borderLeft: "1px solid #e5e7eb", boxShadow: "-4px 0 12px rgba(0,0,0,0.06)",
        zIndex: 50, padding: "16px 18px", overflowY: "auto",
      }}
      role="dialog"
      aria-label="Agent-diagnostik"
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
        <strong style={{ fontSize: 14 }}>Diagnostik — {item.heading || item.label}</strong>
        <button
          type="button"
          onClick={onClose}
          style={{
            marginLeft: "auto", border: "none", background: "transparent",
            cursor: "pointer", color: "#6b7280", fontSize: 18, lineHeight: 1,
          }}
          aria-label="Stäng"
        >
          ✕
        </button>
      </div>

      <DrawerSection title="Status">
        <div style={{ fontSize: 12, color: "#374151" }}>
          <div>Sektion: <code>{item.commentType || "—"}</code></div>
          <div>HITL-status: <strong>{item.hitlStatus || "saknas"}</strong></div>
          {item.approvedAt && <div>Godkänd: {new Date(item.approvedAt).toLocaleString("sv-SE")}</div>}
          {item.approvedByEmail && <div>Av: {item.approvedByEmail}</div>}
          <div>Versioner: {item.revisionCount || 0}</div>
        </div>
      </DrawerSection>

      {Array.isArray(item.gateFields) && item.gateFields.length > 0 && (
        <DrawerSection title="Indata">
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
            {item.gateFields.map((f, i) => (
              <li key={i} style={{ color: f.done ? "#047857" : "#B91C1C", marginBottom: 2 }}>
                {f.done ? "✓" : "✗"} {f.label}
              </li>
            ))}
          </ul>
        </DrawerSection>
      )}

      {Array.isArray(item.whyDisabled) && item.whyDisabled.length > 0 && (
        <DrawerSection title="Blockerar generering">
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: "#92400E" }}>
            {item.whyDisabled.map((reason, i) => (
              <li key={i} style={{ marginBottom: 2 }}>{reason}</li>
            ))}
          </ul>
        </DrawerSection>
      )}

      <DrawerSection title="Tillstånd">
        <pre style={{ fontSize: 10, color: "#374151", background: "#F9FAFB", padding: 8, borderRadius: 6, overflowX: "auto", maxHeight: 200 }}>
          {JSON.stringify({
            sectionId: item.sectionId,
            agentKey: item.agentKey,
            action: item.action,
            canGenerate: item.canGenerate,
            exists: item.exists,
            hasContent: item.hasContent,
            stateClass: item.stateClass,
          }, null, 2)}
        </pre>
      </DrawerSection>
    </div>
  );
}

function DrawerSection({ title, children }) {
  return (
    <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #f3f4f6" }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "#6b7280", marginBottom: 6, letterSpacing: 0.4 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

// Faktagranskning-card. Renders at the top of the prewritten list when
// cross-validator found issues. Each row is a checkbox the broker ticks
// to mark "reviewed"; struck-through styling makes the resolved state
// obvious. Toggle is optimistic — local state flips immediately, server
// PATCH runs in the background, rollback on error.
function ConsistencyCard({ report, orgnr, onUpdate, expanded, onToggleExpanded }) {
  const issues = Array.isArray(report?.issues) ? report.issues : [];
  const checkedCount = issues.filter((i) => i?.checked).length;
  const total = issues.length;
  const isCollapsible = typeof expanded === "boolean" && typeof onToggleExpanded === "function";
  const isExpanded = isCollapsible ? expanded : true;

  // Per-issue inflight flag so the user gets a subtle pending cue and
  // can't double-toggle the same row mid-flight.
  const [pending, setPending] = useState({});

  async function toggle(issue) {
    if (!issue?.id || !orgnr || pending[issue.id]) return;
    const nextChecked = !issue.checked;

    // Optimistic update — flip locally immediately.
    const optimistic = {
      ...report,
      issues: issues.map((i) => (i.id === issue.id ? { ...i, checked: nextChecked } : i)),
    };
    onUpdate?.(optimistic);
    setPending((p) => ({ ...p, [issue.id]: true }));

    try {
      const resp = await patchConsistencyIssue(orgnr, issue.id, nextChecked);
      if (resp?.consistency && Array.isArray(resp.consistency.issues)) {
        onUpdate?.(resp.consistency);
      }
    } catch (err) {
      // Rollback on failure.
      onUpdate?.(report);
      // eslint-disable-next-line no-console
      console.warn("[ConsistencyCard] toggle failed:", err?.message || err);
    } finally {
      setPending((p) => {
        const next = { ...p };
        delete next[issue.id];
        return next;
      });
    }
  }

  const sevColor = (s) => (s === "error" ? "#dc2626" : "#d97706");

  return (
    <div
      className={`imstudio-editor__prewritten-card imstudio-editor__consistency-card ${
        isExpanded ? "is-expanded" : "is-collapsed"
      }`}
      data-prewritten-id="__consistency__"
    >
      <div
        className="imstudio-gate__header"
        onClick={isCollapsible ? onToggleExpanded : undefined}
        style={isCollapsible ? { cursor: "pointer" } : undefined}
      >
        <div
          className="imstudio-gate__icon"
          style={{ background: "#fef3c7", color: "#b45309" }}
          aria-hidden="true"
        >
          !
        </div>
        <div className="imstudio-gate__title-wrap">
          <div className="imstudio-gate__title">Faktagranskning</div>
          <div className="imstudio-gate__subtitle">
            {checkedCount} av {total} åtgärdade
          </div>
        </div>
        <span
          className="imstudio-gate__badge"
          style={{ background: "#fef3c7", color: "#92400e" }}
        >
          {total}
        </span>
      </div>

      {isExpanded && (
        <div style={{ padding: "8px 14px 14px" }}>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {issues.map((issue) => {
              const isPending = !!pending[issue.id];
              const labelStyle = {
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                padding: "8px 0",
                borderBottom: "1px solid #f3f4f6",
                cursor: isPending ? "wait" : "pointer",
                opacity: isPending ? 0.6 : 1,
              };
              const textStyle = {
                fontSize: 13,
                lineHeight: 1.4,
                color: issue.checked ? "#9ca3af" : "#111827",
                textDecoration: issue.checked ? "line-through" : "none",
                flex: 1,
              };
              return (
                <li key={issue.id}>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={!!issue.checked}
                      disabled={isPending}
                      onChange={() => toggle(issue)}
                      style={{ marginTop: 3 }}
                    />
                    <span style={textStyle}>
                      <span
                        aria-hidden="true"
                        style={{
                          display: "inline-block",
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          marginRight: 8,
                          background: sevColor(issue.severity),
                          verticalAlign: "middle",
                        }}
                      />
                      {issue.message}
                      {Array.isArray(issue.sections) && issue.sections.length > 0 && (
                        <span
                          style={{
                            display: "block",
                            marginTop: 4,
                            fontSize: 11,
                            color: "#6b7280",
                          }}
                        >
                          {issue.sections.join(" · ")}
                        </span>
                      )}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function PrewrittenCard({ id, item, progress, isWorking, onPrepare, onInsert, orgnr, expanded, onToggleExpanded }) {
  const navigate = useNavigate();
  const [websiteInputOpen, setWebsiteInputOpen] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [websiteBusy, setWebsiteBusy] = useState(false);
  const [websiteErr, setWebsiteErr] = useState("");

  // ── HITL state ────────────────────────────────────────────────────────
  const [hitlBusy, setHitlBusy] = useState(false);
  const [hitlErr, setHitlErr] = useState("");
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineFeedback, setRefineFeedback] = useState("");
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [revisions, setRevisions] = useState(null);

  // ── Broker-edit modal: full-screen text editor for the current body.
  // Opens from the "Redigera" button on cards that have content and a
  // comment_type (i.e. a server-side section — manual sections stay
  // editable through the main canvas). The modal pre-fills with the
  // current body flattened to plain text; on save we re-wrap into
  // simple <p>/<h3> HTML the backend already expects.
  const [editOpen, setEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editErr, setEditErr] = useState("");

  function openEdit() {
    setEditDraft(_bodyToPlainText(item.body));
    setEditErr("");
    setEditOpen(true);
  }

  async function saveEdit({ insertAfter = false } = {}) {
    if (!item.commentType) return;
    const payload = _plainTextToBody(editDraft);
    if (!payload.trim()) {
      setEditErr("Texten får inte vara tom.");
      return;
    }
    if (payload.length > 30000) {
      setEditErr("Texten är för lång (max 30 000 tecken).");
      return;
    }
    setEditSaving(true);
    setEditErr("");
    try {
      await editAgentComment(orgnr, item.commentType, payload);
      await onPrepare?.();
      setEditOpen(false);
      if (insertAfter) onInsert?.();
    } catch (err) {
      setEditErr(err?.message || "Kunde inte spara ändringarna.");
    } finally {
      setEditSaving(false);
    }
  }

  // ── Underlag panel + diagnostics drawer ──────────────────────────────
  const [factsOpen, setFactsOpen] = useState(false);
  const [factsCtx, setFactsCtx] = useState(null);
  const [factsLoading, setFactsLoading] = useState(false);
  const [factsErr, setFactsErr] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  async function toggleFacts() {
    if (factsOpen) {
      setFactsOpen(false);
      return;
    }
    setFactsOpen(true);
    if (factsCtx || !item.commentType) return;
    setFactsLoading(true);
    setFactsErr("");
    try {
      const data = await fetchAgentContext(orgnr, item.commentType);
      setFactsCtx(data?.context || null);
    } catch (err) {
      setFactsErr(err?.message || "Kunde inte hämta underlag.");
    } finally {
      setFactsLoading(false);
    }
  }

  const isApproved = item.hitlStatus === "approved";

  async function handleApprove() {
    if (!item.commentType) return;
    setHitlBusy(true);
    setHitlErr("");
    try {
      await approveAgentComment(orgnr, item.commentType);
      onPrepare?.();
    } catch (err) {
      setHitlErr(err?.message || "Kunde inte godkänna.");
    } finally {
      setHitlBusy(false);
    }
  }

  async function handleReopen() {
    if (!item.commentType) return;
    setHitlBusy(true);
    setHitlErr("");
    try {
      await reopenAgentComment(orgnr, item.commentType);
      onPrepare?.();
    } catch (err) {
      setHitlErr(err?.message || "Kunde inte öppna för redigering.");
    } finally {
      setHitlBusy(false);
    }
  }

  async function handleRefineSubmit(e) {
    e?.preventDefault?.();
    const fb = (refineFeedback || "").trim();
    if (!fb || !item.commentType) return;
    setHitlBusy(true);
    setHitlErr("");
    try {
      await refineAgentComment(orgnr, item.commentType, fb);
      setRefineOpen(false);
      setRefineFeedback("");
      onPrepare?.();
    } catch (err) {
      setHitlErr(err?.message || "Refine misslyckades.");
    } finally {
      setHitlBusy(false);
    }
  }

  async function openRevisions() {
    if (!item.commentType) return;
    setRevisionsOpen(true);
    if (revisions !== null) return;
    setHitlBusy(true);
    setHitlErr("");
    try {
      const data = await fetchAgentRevisions(orgnr, item.commentType);
      setRevisions(Array.isArray(data?.revisions) ? data.revisions : []);
    } catch (err) {
      setHitlErr(err?.message || "Kunde inte hämta versionshistorik.");
      setRevisions([]);
    } finally {
      setHitlBusy(false);
    }
  }

  async function handleRestore(revisionId) {
    if (!item.commentType) return;
    if (!window.confirm("Återställ den här versionen som nuvarande utkast?")) return;
    setHitlBusy(true);
    setHitlErr("");
    try {
      await restoreAgentRevision(orgnr, item.commentType, revisionId);
      setRevisionsOpen(false);
      setRevisions(null);
      onPrepare?.();
    } catch (err) {
      setHitlErr(err?.message || "Kunde inte återställa.");
    } finally {
      setHitlBusy(false);
    }
  }
  const isBlocked = item.action === "check" && item.whyDisabled?.length > 0;
  const cardStateClass = item.stateClass || "is-empty";
  const hasDrag = item.hasContent;

  // Gate-card data
  const gateFields = Array.isArray(item.gateFields) ? item.gateFields : [];
  const totalFields = gateFields.length;
  const doneFields = gateFields.filter((f) => f.done).length;
  const hasGate = totalFields > 0;
  const allDone = hasGate && doneFields === totalFields;
  const noneDone = hasGate && doneFields === 0;

  // Status badge
  let badgeLabel = "Tom";
  let badgeClass = "empty";
  if (item.hasContent) {
    badgeLabel = "Klar";
    badgeClass = "ready";
  } else if (item.canGenerate || allDone) {
    badgeLabel = "Kan genereras";
    badgeClass = "generatable";
  } else if (hasGate && !noneDone) {
    badgeLabel = "Delvis klar";
    badgeClass = "partial";
  } else if (hasGate && noneDone) {
    badgeLabel = "Saknas data";
    badgeClass = "blocked";
  }

  // Lock / check icon
  const iconChar = item.hasContent || allDone ? "✓" : isBlocked || noneDone ? "🔒" : "·";

  // Primary CTA — when blocked, navigate to the data-fill page; otherwise call onPrepare.
  const cta = isBlocked && hasGate ? gateCtaFor(item.agentKey, orgnr) : null;
  const primaryActionLabel = isWorking
    ? "Arbetar…"
    : item.action === "refresh"
    ? "Uppdatera"
    : item.action === "generate"
    ? "Generera"
    : cta
    ? cta.label
    : "Kontrollera";

  function handleCtaClick() {
    // Web agent has no real "set website" page in the app — let the broker
    // type the URL inline and run the web agent right from this card.
    if (isBlocked && item.agentKey === "web") {
      setWebsiteInputOpen((v) => !v);
      return;
    }
    if (isBlocked && cta) {
      navigate(cta.to);
    } else {
      onPrepare?.();
    }
  }

  async function handleWebsiteSubmit(e) {
    e?.preventDefault?.();
    const trimmed = (websiteUrl || "").trim();
    if (!trimmed) {
      setWebsiteErr("Ange en URL.");
      return;
    }
    const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    setWebsiteBusy(true);
    setWebsiteErr("");
    try {
      await runWebAgent(orgnr, normalized);
      setWebsiteInputOpen(false);
      setWebsiteUrl("");
      onPrepare?.();
    } catch (err) {
      setWebsiteErr(err?.message || "Kunde inte spara webbplatsen.");
    } finally {
      setWebsiteBusy(false);
    }
  }

  // When `expanded` is controlled by a parent, the card collapses to a
  // thin header-only row. Click header → toggle. Default behaviour
  // (uncontrolled) stays expanded so call sites that don't pass the
  // prop still work.
  const isCollapsible = typeof expanded === "boolean" && typeof onToggleExpanded === "function";
  const isExpanded = isCollapsible ? expanded : true;

  // Compact preview line for the header — same source of truth as the
  // draggable snippet below, just shorter. Only visible when the row
  // is collapsed AND has content; otherwise the badge conveys state.
  const headerPreview = React.useMemo(
    () => (item.hasContent ? _previewText(item.body, 80) : ""),
    [item.hasContent, item.body],
  );

  return (
    <>
    <div
      className={`imstudio-editor__prewritten-card imstudio-editor__prewritten-card--gate ${cardStateClass} ${
        isWorking ? "is-working" : ""
      } ${hasDrag ? "has-content" : "has-no-content"} ${isExpanded ? "is-expanded" : "is-collapsed"}`}
      data-prewritten-id={item.sectionId}
      data-agent-key={item.agentKey || "manual"}
    >
      <div
        className="imstudio-gate__header"
        onClick={isCollapsible ? onToggleExpanded : undefined}
        style={isCollapsible ? { cursor: "pointer" } : undefined}
      >
        {/* Left grip — draggable when the row has content. Wrapped in
            its own component so the drag listeners are scoped to the
            grip only; clicking the rest of the header still expands
            the row without accidentally starting a drag. */}
        {item.hasContent ? (
          <PrewrittenRowGrip id={id} item={item} />
        ) : (
          <div className={`imstudio-gate__icon imstudio-gate__icon--${badgeClass}`}>{iconChar}</div>
        )}
        <div className="imstudio-gate__title-wrap">
          <div className="imstudio-gate__title">{item.heading}</div>
          {hasGate ? (
            <div className="imstudio-gate__subtitle">
              {doneFields} av {totalFields} fält ifyllda
            </div>
          ) : item.hasContent ? (
            // Compact one-line preview replaces the redundant "Redo
            // att dra in" subtitle. Actual state lives in the badge.
            <div
              className="imstudio-gate__subtitle"
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "#78716C",
              }}
            >
              {headerPreview}
            </div>
          ) : (
            <div className="imstudio-gate__subtitle">
              {item.source === "composer"
                ? "Genereras via ”Ladda in allt”"
                : "Manuellt avsnitt"}
            </div>
          )}
        </div>
        <span className={`imstudio-gate__badge imstudio-gate__badge--${badgeClass}`}>{badgeLabel}</span>
        {/* Quick-insert — one-click alternative to drag. Only shown
            when the section has content. Click bubble is stopped so
            the header's expand-toggle doesn't fire simultaneously. */}
        {item.hasContent && onInsert ? (
          <button
            type="button"
            aria-label="Sätt in i dokumentet"
            title="Sätt in i slutet av dokumentet"
            onClick={(e) => {
              e.stopPropagation();
              onInsert();
            }}
            style={{
              marginLeft: 6,
              width: 28,
              height: 28,
              borderRadius: 6,
              border: "1px solid #E5E7EB",
              background: "#FFFFFF",
              color: "#111827",
              fontSize: 16,
              fontWeight: 700,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}
          >
            +
          </button>
        ) : null}
      </div>

      {hasGate && (
        <div
          className={`imstudio-gate__progress imstudio-gate__progress--${badgeClass}`}
          aria-label={`${doneFields} av ${totalFields}`}
        >
          <div
            className="imstudio-gate__progress-fill"
            style={{ width: totalFields ? `${(doneFields / totalFields) * 100}%` : "0%" }}
          />
        </div>
      )}

      {hasGate && (
        <ul className="imstudio-gate__fields">
          {gateFields.map((f, i) => (
            <li
              key={i}
              className={`imstudio-gate__field ${f.done ? "is-done" : "is-missing"}`}
            >
              <span className="imstudio-gate__dot" aria-hidden="true" />
              <span className="imstudio-gate__field-label">{f.label}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Expanded state — draggable preview + "Visa / redigera" link.
          Full-text view and in-place edit happen in a modal to avoid
          inflating the dock when a section runs long; brokers see the
          snippet here and open the modal when they need to read or
          rewrite the whole thing. */}
      {item.hasContent && isExpanded && (
        <div className="imstudio-editor__prewritten-body-wrap">
          <PrewrittenDragItem id={id} item={item} />
          {item.commentType ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openEdit();
              }}
              style={{
                marginTop: 6,
                padding: "6px 10px",
                fontSize: 11,
                fontWeight: 600,
                border: "1px solid #D6D3D1",
                borderRadius: 6,
                background: "#FFFFFF",
                color: "#1C1917",
                cursor: "pointer",
                alignSelf: "flex-start",
              }}
            >
              Visa / redigera hela texten
            </button>
          ) : null}
        </div>
      )}

      {/* ── HITL: status pill + Godkänn / Skriv om / Versionshistorik ── */}
      {item.hasContent && item.commentType && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            margin: "8px 0 4px 0",
            padding: "10px 12px",
            background: isApproved ? "#ECFDF5" : "#FFFBEB",
            border: `1px solid ${isApproved ? "#A7F3D0" : "#FDE68A"}`,
            borderRadius: 8,
            fontSize: 12,
          }}
        >
          <span style={{ fontWeight: 600, color: isApproved ? "#047857" : "#92400E" }}>
            {isApproved ? "✓ Godkänd" : "Utkast"}
          </span>
          {isApproved && item.approvedAt && (
            <span style={{ color: "#047857" }}>
              {new Date(item.approvedAt).toLocaleString("sv-SE", {
                year: "numeric", month: "2-digit", day: "2-digit",
                hour: "2-digit", minute: "2-digit",
              })}
              {item.approvedByEmail ? ` • ${item.approvedByEmail}` : ""}
            </span>
          )}

          <span style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
            {!isApproved && (
              <button
                type="button"
                onClick={handleApprove}
                disabled={hitlBusy || isWorking}
                style={{
                  padding: "4px 10px", fontSize: 12, fontWeight: 600,
                  border: "none", borderRadius: 6, cursor: "pointer",
                  background: "#047857", color: "#fff",
                }}
              >
                Godkänn
              </button>
            )}
            {isApproved && (
              <button
                type="button"
                onClick={handleReopen}
                disabled={hitlBusy || isWorking}
                style={{
                  padding: "4px 10px", fontSize: 12,
                  border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer",
                  background: "#fff", color: "#374151",
                }}
              >
                Återöppna
              </button>
            )}
            {item.refinable && !isApproved && (
              <button
                type="button"
                onClick={() => setRefineOpen((v) => !v)}
                disabled={hitlBusy || isWorking}
                style={{
                  padding: "4px 10px", fontSize: 12,
                  border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer",
                  background: "#fff", color: "#374151",
                }}
              >
                {refineOpen ? "Avbryt" : "Skriv om"}
              </button>
            )}
            {item.revisionCount > 0 && (
              <button
                type="button"
                onClick={openRevisions}
                disabled={hitlBusy}
                style={{
                  padding: "4px 10px", fontSize: 12,
                  border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer",
                  background: "#fff", color: "#374151",
                }}
                title={`${item.revisionCount} versioner`}
              >
                Versionshistorik ({item.revisionCount})
              </button>
            )}
          </span>

          {hitlErr && (
            <div style={{ flexBasis: "100%", color: "#B91C1C", fontSize: 12, marginTop: 4 }}>
              {hitlErr}
            </div>
          )}
        </div>
      )}

      {refineOpen && item.refinable && !isApproved && (
        <form
          onSubmit={handleRefineSubmit}
          style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}
        >
          <textarea
            placeholder="Beskriv vad som ska ändras (t.ex. 'tona ner kundkoncentration', 'kortare stycke 4')"
            value={refineFeedback}
            onChange={(e) => setRefineFeedback(e.target.value)}
            disabled={hitlBusy}
            rows={3}
            maxLength={4000}
            style={{
              padding: "8px 10px", fontSize: 13,
              border: "1px solid #d1d5db", borderRadius: 8, resize: "vertical",
            }}
          />
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button
              type="submit"
              disabled={hitlBusy || !refineFeedback.trim()}
              style={{
                padding: "6px 14px", fontSize: 13, fontWeight: 600,
                background: "#111827", color: "#fff", border: "none",
                borderRadius: 8, cursor: hitlBusy ? "wait" : "pointer",
              }}
            >
              {hitlBusy ? "Skriver om…" : "Skicka och regenerera"}
            </button>
          </div>
        </form>
      )}

      {revisionsOpen && (
        <div
          style={{
            margin: "0 0 8px 0",
            padding: 10,
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            background: "#F9FAFB",
            maxHeight: 320,
            overflowY: "auto",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
            <strong style={{ fontSize: 13 }}>Versionshistorik</strong>
            <button
              type="button"
              onClick={() => setRevisionsOpen(false)}
              style={{
                marginLeft: "auto", border: "none", background: "transparent",
                cursor: "pointer", color: "#6b7280", fontSize: 16, lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
          {!revisions && <div style={{ color: "#6b7280", fontSize: 12 }}>Laddar…</div>}
          {revisions && revisions.length === 0 && (
            <div style={{ color: "#6b7280", fontSize: 12 }}>Inga tidigare versioner.</div>
          )}
          {revisions && revisions.map((r, i) => {
            const sourceLabel = {
              generated: "Genererad",
              refined: "Refinerad",
              manual: "Manuell",
            }[r.source] || r.source;
            return (
              <div
                key={r.id}
                style={{
                  borderTop: i === 0 ? "none" : "1px solid #e5e7eb",
                  padding: "8px 0",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151", marginBottom: 4 }}>
                  <span style={{ fontWeight: 600 }}>#{r.id}</span>
                  <span style={{ color: "#6b7280" }}>{sourceLabel}</span>
                  {r.created_at && (
                    <span style={{ color: "#9ca3af" }}>
                      {new Date(r.created_at).toLocaleString("sv-SE", {
                        year: "numeric", month: "2-digit", day: "2-digit",
                        hour: "2-digit", minute: "2-digit",
                      })}
                    </span>
                  )}
                  {r.created_by_email && (
                    <span style={{ color: "#9ca3af" }}>• {r.created_by_email}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRestore(r.id)}
                    disabled={hitlBusy}
                    style={{
                      marginLeft: "auto",
                      padding: "2px 8px", fontSize: 11,
                      border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer",
                      background: "#fff", color: "#374151",
                    }}
                  >
                    Återställ
                  </button>
                </div>
                {r.feedback && (
                  <div style={{ fontSize: 11, color: "#6b7280", fontStyle: "italic", marginBottom: 4 }}>
                    "{r.feedback}"
                  </div>
                )}
                <div
                  style={{
                    fontSize: 12, color: "#374151", whiteSpace: "pre-wrap",
                    maxHeight: 120, overflowY: "auto",
                    padding: 8, background: "#fff",
                    border: "1px solid #e5e7eb", borderRadius: 6,
                  }}
                >
                  {r.body}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Once a section has generated content, the primary affordance is
          "insert into the document" (rendered above the body via onInsert).
          Composer-source sections have no per-section endpoint — they only
          fill via "Ladda in allt" — so the CTA would be a dead-end click;
          the subtitle "Genereras via 'Ladda in allt'" already tells the
          broker what to do. Legacy agent-source sections (financials,
          valuation, risks, web, overview) still have runnable endpoints,
          so their CTA remains. */}
      {!item.hasContent && item.source !== "composer" && (
        <button
          type="button"
          className={`imstudio-gate__cta imstudio-gate__cta--${badgeClass}`}
          onClick={handleCtaClick}
          disabled={isWorking}
        >
          {primaryActionLabel}
        </button>
      )}

      {/* Once content exists, the bottom slot becomes a labelled drag
          handle so the broker can pull the section into the document
          without hunting for the small body-preview drag area. */}
      {item.hasContent && (
        <PrewrittenDragHandle id={id} item={item} />
      )}

      <DiagnosticsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        item={item}
      />

      {(websiteInputOpen || (isBlocked && item.agentKey === "web")) && item.agentKey === "web" && (
        <form
          onSubmit={handleWebsiteSubmit}
          style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}
        >
          <div
            style={{
              flex: "1 1 200px",
              display: "flex",
              alignItems: "center",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              background: websiteBusy ? "#f3f4f6" : "#fff",
              overflow: "hidden",
            }}
          >
            <span
              style={{
                padding: "8px 0 8px 10px",
                fontSize: 13,
                color: "#9ca3af",
                userSelect: "none",
                pointerEvents: "none",
              }}
            >
              https://
            </span>
            <input
              type="text"
              inputMode="url"
              autoComplete="off"
              placeholder="bolaget.se"
              value={websiteUrl}
              onChange={(e) =>
                setWebsiteUrl(e.target.value.replace(/^https?:\/\//i, ""))
              }
              disabled={websiteBusy}
              style={{
                flex: 1,
                padding: "8px 10px 8px 2px",
                fontSize: 13,
                border: "none",
                outline: "none",
                background: "transparent",
                minWidth: 0,
              }}
            />
          </div>
          <button
            type="submit"
            disabled={websiteBusy || !websiteUrl.trim()}
            style={{ padding: "8px 14px", fontSize: 13, fontWeight: 600, background: "#111827", color: "#fff", border: "none", borderRadius: 8, cursor: websiteBusy ? "wait" : "pointer" }}
          >
            {websiteBusy ? "Sparar…" : "Spara & analysera"}
          </button>
          {websiteErr && (
            <div style={{ flexBasis: "100%", color: "#B91C1C", fontSize: 12 }}>{websiteErr}</div>
          )}
        </form>
      )}

      {progress ? (
        <div
          className={`imstudio-editor__prewritten-progress imstudio-editor__prewritten-progress--${progress.type}`}
        >
          {progress.text}
        </div>
      ) : null}
    </div>
    {editOpen ? (
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Redigera ${item.heading || "sektion"}`}
        onClick={() => (editSaving ? null : setEditOpen(false))}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.55)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#FFFFFF",
            borderRadius: 14,
            boxShadow: "0 24px 60px rgba(15,23,42,0.35)",
            width: "min(820px, 100%)",
            maxHeight: "calc(100vh - 48px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 12,
              padding: "16px 20px",
              borderBottom: "1px solid #E7E5E4",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1C1917" }}>
                Redigera {item.heading || "sektion"}
              </div>
              <div style={{ fontSize: 12, color: "#78716C", marginTop: 2 }}>
                Dubbelradbrytning startar nytt stycke. Rader som börjar med <code>## </code> blir underrubriker.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setEditOpen(false)}
              disabled={editSaving}
              aria-label="Stäng"
              style={{
                border: "none",
                background: "transparent",
                color: "#78716C",
                fontSize: 20,
                cursor: editSaving ? "not-allowed" : "pointer",
                padding: 4,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
          <textarea
            value={editDraft}
            onChange={(e) => setEditDraft(e.target.value)}
            disabled={editSaving}
            spellCheck
            autoFocus
            style={{
              flex: 1,
              minHeight: 320,
              padding: "16px 20px",
              fontFamily: "inherit",
              fontSize: 14,
              lineHeight: 1.6,
              color: "#1C1917",
              border: "none",
              outline: "none",
              resize: "none",
              background: "#FFFFFF",
            }}
          />
          {editErr ? (
            <div
              role="alert"
              style={{
                margin: "0 20px",
                padding: "8px 10px",
                fontSize: 12,
                color: "#991B1B",
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                borderRadius: 6,
              }}
            >
              {editErr}
            </div>
          ) : null}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              padding: "14px 20px",
              borderTop: "1px solid #E7E5E4",
              background: "#FAFAF9",
            }}
          >
            <button
              type="button"
              onClick={() => setEditOpen(false)}
              disabled={editSaving}
              style={{
                padding: "8px 14px",
                fontSize: 13,
                fontWeight: 600,
                border: "1px solid #D6D3D1",
                background: "#FFFFFF",
                color: "#1C1917",
                borderRadius: 8,
                cursor: editSaving ? "not-allowed" : "pointer",
              }}
            >
              Avbryt
            </button>
            <button
              type="button"
              onClick={() => saveEdit({ insertAfter: false })}
              disabled={editSaving}
              style={{
                padding: "8px 14px",
                fontSize: 13,
                fontWeight: 600,
                border: "1px solid #1C1917",
                background: "#FFFFFF",
                color: "#1C1917",
                borderRadius: 8,
                cursor: editSaving ? "progress" : "pointer",
              }}
            >
              {editSaving ? "Sparar…" : "Spara"}
            </button>
            {onInsert ? (
              <button
                type="button"
                onClick={() => saveEdit({ insertAfter: true })}
                disabled={editSaving}
                style={{
                  padding: "8px 14px",
                  fontSize: 13,
                  fontWeight: 700,
                  border: "1px solid #1C1917",
                  background: "#1C1917",
                  color: "#FAFAF9",
                  borderRadius: 8,
                  cursor: editSaving ? "progress" : "pointer",
                }}
              >
                {editSaving ? "Sparar…" : "Spara och infoga"}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    ) : null}
    </>
  );
}

// Strip composer HTML down to plain text for the sidebar preview.
// Cached via useMemo at each call site so this doesn't re-run every
// render. Falls back to the raw body if the parser returns nothing.
function _previewText(body, limit = 140) {
  if (!body) return "";
  try {
    const blocks = _parseComposerHtml(body);
    const joined = blocks.map((b) => b.text).join(" ").trim();
    const text = joined || body;
    return text.length > limit ? `${text.slice(0, limit).trim()}…` : text;
  } catch {
    return body.length > limit ? `${body.slice(0, limit)}…` : body;
  }
}

function PrewrittenDragItem({ id, item }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data: {
      source: "prewritten",
      item,
      label: item.label,
      heading: item.heading,
    },
  });

  const preview = React.useMemo(
    () => _previewText(item.body, 140),
    [item.body],
  );

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <button
      ref={setNodeRef}
      style={style}
      type="button"
      className="imstudio-editor__snippet-button"
      {...listeners}
      {...attributes}
    >
      <div className="imstudio-editor__snippet-body">{preview}</div>
    </button>
  );
}

/**
 * Small grip icon on the left of the row header. Drag-source only —
 * click behaviour bubbles to the header (which toggles expand). Same
 * drag payload as PrewrittenDragItem so ``handleDragEnd`` routes the
 * drop through the existing insert path. The activation-distance in
 * the app's PointerSensor means a click without motion never turns
 * into a drag.
 */
function PrewrittenRowGrip({ id, item }) {
  const gripId = `${id}-grip`;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: gripId,
    data: {
      source: "prewritten",
      item,
      label: item.label,
      heading: item.heading,
    },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      // Stop click bubbling so grabbing the grip doesn't also toggle
      // the row's expanded state (headers use onClick above).
      onClick={(e) => e.stopPropagation()}
      title="Dra för att lägga in i dokumentet"
      style={{
        width: 24,
        height: 32,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: isDragging ? "grabbing" : "grab",
        color: isDragging ? "#111827" : "#9CA3AF",
        fontSize: 14,
        lineHeight: 1,
        userSelect: "none",
        touchAction: "none",
        opacity: isDragging ? 0.6 : 1,
        transform: CSS.Translate.toString(transform),
      }}
    >
      {/* Two vertical dot columns — established "handle" glyph. */}
      <span aria-hidden="true">⋮⋮</span>
    </div>
  );
}

/**
 * Labeled drag handle that replaces the bottom "Uppdatera"/"Generera" CTA
 * when a prewritten card has content. Same drag-source data as
 * PrewrittenDragItem so the existing handleDragEnd routing works without
 * change. The body-text preview above stays draggable too — this is the
 * more discoverable affordance for brokers who don't realise the preview
 * itself is grabbable.
 */
function PrewrittenDragHandle({ id, item }) {
  const dragId = `${id}-handle`;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragId,
    data: {
      source: "prewritten",
      item,
      label: item.label,
      heading: item.heading,
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.7 : 1,
    width: "100%",
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 700,
    background: isDragging ? "#1F2937" : "#111827",
    color: "#FFFFFF",
    border: "none",
    borderRadius: 10,
    cursor: isDragging ? "grabbing" : "grab",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    userSelect: "none",
    touchAction: "none",
    boxShadow: isDragging
      ? "0 4px 12px rgba(0,0,0,0.18)"
      : "0 1px 2px rgba(0,0,0,0.10)",
    transition: "box-shadow 120ms ease, background 120ms ease",
  };

  return (
    <button
      ref={setNodeRef}
      type="button"
      className="imstudio-editor__drag-handle-button"
      style={style}
      title="Dra och släpp in i dokumentet"
      {...listeners}
      {...attributes}
    >
      <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>⠿</span>
      <span>Dra in i dokumentet</span>
      <span aria-hidden="true" style={{ fontSize: 14, opacity: 0.7 }}>↗</span>
    </button>
  );
}

function CanvasDropZone({ children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: "canvas-dropzone",
  });

  return (
    <div
      ref={setNodeRef}
      className={`imstudio-editor__canvas-dropzone ${isOver ? "is-over" : ""}`}
    >
      {children}
    </div>
  );
}

function DragGhost({ label }) {
  return <div className="imstudio-editor__drag-ghost">{label}</div>;
}

function StyleToolbar({ section, onChange, onDelete }) {
  const supportsTypography =
    section.type !== "imageSection" && section.type !== "dividerSection";

  const supportsBackground = true;

  return (
    <div className="imstudio-editor__toolbar">
      <div className="imstudio-editor__toolbar-group">
        <span className="imstudio-editor__toolbar-label">{section.label}</span>
      </div>

      {supportsTypography ? (
        <>
          <select
            className="imstudio-editor__toolbar-select"
            value={section.fontFamily || FONT_OPTIONS[0].value}
            onChange={(e) => onChange({ fontFamily: e.target.value })}
          >
            {FONT_OPTIONS.map((font) => (
              <option key={font.value} value={font.value}>
                {font.label}
              </option>
            ))}
          </select>

          <input
            className="imstudio-editor__toolbar-number"
            type="number"
            min="10"
            max="36"
            value={section.fontSize || 16}
            onChange={(e) => onChange({ fontSize: Number(e.target.value) || 16 })}
          />

          <select
            className="imstudio-editor__toolbar-select"
            value={section.fontWeight || 400}
            onChange={(e) => onChange({ fontWeight: Number(e.target.value) })}
          >
            <option value={400}>Regular</option>
            <option value={500}>Medium</option>
            <option value={600}>Semibold</option>
            <option value={700}>Bold</option>
          </select>

          <div className="imstudio-editor__align-group">
            <button
              type="button"
              className={`imstudio-editor__icon-button ${
                (section.textAlign || "left") === "left" ? "imstudio-editor__icon-button--active" : ""
              }`}
              onClick={() => onChange({ textAlign: "left" })}
            >
              <AlignLeft size={15} />
            </button>
            <button
              type="button"
              className={`imstudio-editor__icon-button ${
                section.textAlign === "center" ? "imstudio-editor__icon-button--active" : ""
              }`}
              onClick={() => onChange({ textAlign: "center" })}
            >
              <AlignCenter size={15} />
            </button>
            <button
              type="button"
              className={`imstudio-editor__icon-button ${
                section.textAlign === "right" ? "imstudio-editor__icon-button--active" : ""
              }`}
              onClick={() => onChange({ textAlign: "right" })}
            >
              <AlignRight size={15} />
            </button>
          </div>

          <ColorSwatches
            label="Text"
            selected={section.textColor || "#0f172a"}
            onSelect={(value) => onChange({ textColor: value })}
          />
        </>
      ) : null}

      {supportsBackground ? (
        <ColorSwatches
          label="Bg"
          selected={section.bg || section.backgroundColor || "transparent"}
          onSelect={(value) => {
            if (section.type === "hero" || section.type === "ctaSection") {
              onChange({ bg: value });
            } else {
              onChange({ backgroundColor: value });
            }
          }}
        />
      ) : null}

      {"paddingY" in section ? (
        <input
          className="imstudio-editor__toolbar-number"
          type="number"
          min="0"
          max="120"
          value={section.paddingY || 0}
          onChange={(e) => onChange({ paddingY: Number(e.target.value) || 0 })}
          title="Vertical padding"
        />
      ) : null}

      {"paddingX" in section ? (
        <input
          className="imstudio-editor__toolbar-number"
          type="number"
          min="0"
          max="120"
          value={section.paddingX || 0}
          onChange={(e) => onChange({ paddingX: Number(e.target.value) || 0 })}
          title="Horizontal padding"
        />
      ) : null}

      <button type="button" className="imstudio-editor__danger-button" onClick={onDelete}>
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function ColorSwatches({ label, selected, onSelect }) {
  return (
    <div className="imstudio-editor__swatches">
      <span className="imstudio-editor__toolbar-mini-label">{label}</span>
      <div className="imstudio-editor__swatches-list">
        {COLOR_SWATCHES.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => onSelect(color)}
            className={`imstudio-editor__swatch ${
              selected === color ? "imstudio-editor__swatch--active" : ""
            }`}
            style={{ background: color }}
            title={color}
          />
        ))}
      </div>
    </div>
  );
}

function InlineEditableText({
  as = "div",
  value,
  className,
  style,
  placeholder,
  onChange,
  onSelect,
}) {
  const Tag = as;
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    if (ref.current.textContent !== (value || "")) {
      ref.current.textContent = value || "";
    }
  }, [value]);

  return (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      className={`${className || ""} imstudio-editor__inline-editable`}
      style={style}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.();
      }}
      onInput={(e) => {
        onChange?.(e.currentTarget.textContent || "");
      }}
      data-placeholder={placeholder || ""}
    />
  );
}

function InlineEditableLink({ text, className, onTextChange, onSelect }) {
  return (
    <span
      className={className}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.();
      }}
    >
      <InlineEditableText
        as="span"
        value={text}
        placeholder="Button text"
        onSelect={onSelect}
        onChange={onTextChange}
      />
    </span>
  );
}

function SortableSectionBlock({
  section, isSelected, isFirst, isLast, basePage,
  onSelect, onChildChange,
  onDelete, onDuplicate, onMoveUp, onMoveDown,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isOver, active } = useSortable({
    id: section.id,
    data: {
      source: "canvas",
      blockId: section.id,
      label: section.label,
    },
  });

  // Visa drop-highlight när det är en aktiv drag och MUSEN HOVRAR ÖVER
  // den här sektionen. Skiljer mellan "infoga IN i sektion" (prewritten/
  // canvas-omsortering) och "infoga MELLAN sektioner" (library) så
  // användaren ser exakt vad släppen innebär.
  const activeSource = active?.data?.current?.source;
  const showDropTarget = isOver && !!active && active.id !== section.id;
  const dropMode = activeSource === "prewritten" ? "into" : "reorder";

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: "relative",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        "imstudio-editor__sortable-wrap"
        + (isSelected ? " is-selected" : "")
        + (showDropTarget ? ` is-drop-target is-drop-${dropMode}` : "")
      }
    >
      <SectionPreview
        section={section}
        isSelected={isSelected}
        basePage={basePage}
        onSelect={onSelect}
        onChildChange={onChildChange}
      />

      {/* Hover-toolbar — vertical strip just outside the block on the
          right. Mailchimp puts move/duplicate/delete there; we mirror it.
          Hidden by default, fades in on hover or when block is selected.
          Marked data-pdf-hide so html2canvas skips it for PDF export. */}
      <div
        data-pdf-hide
        className="imstudio-editor__hover-toolbar"
        style={{
          position: "absolute",
          top: 8,
          right: -38,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          padding: 4,
          background: "#fff",
          border: "1px solid #e7e5e4",
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          opacity: isSelected ? 1 : 0,
          transition: "opacity 0.15s",
          zIndex: 5,
          pointerEvents: isSelected ? "auto" : "none",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; e.currentTarget.style.pointerEvents = "auto"; }}
        onMouseLeave={(e) => { if (!isSelected) { e.currentTarget.style.opacity = 0; e.currentTarget.style.pointerEvents = "none"; } }}
      >
        <button
          type="button"
          {...attributes}
          {...listeners}
          title="Flytta"
          style={hoverBtnStyle}
        >
          <GripVertical size={14} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMoveUp?.(); }}
          disabled={isFirst}
          title="Flytta upp"
          style={{ ...hoverBtnStyle, opacity: isFirst ? 0.3 : 1, cursor: isFirst ? "not-allowed" : "pointer" }}
        >
          ↑
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMoveDown?.(); }}
          disabled={isLast}
          title="Flytta ner"
          style={{ ...hoverBtnStyle, opacity: isLast ? 0.3 : 1, cursor: isLast ? "not-allowed" : "pointer" }}
        >
          ↓
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDuplicate?.(); }}
          title="Duplicera"
          style={hoverBtnStyle}
        >
          <Copy size={13} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
          title="Radera"
          style={{ ...hoverBtnStyle, color: "#b91c1c" }}
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

const hoverBtnStyle = {
  width: 28,
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  color: "#57534e",
  fontSize: 14,
};

// Render the financialTable content block as a real <table>.
// Data is purely read from the block (already inserted by the prewritten
// drag-drop pipeline); there is no fetching here. Cells are not yet
// inline-editable — broker edits land via a future "Edit table" affordance.
function FinancialTableBlock({ block, section, onSelect }) {
  const headers = Array.isArray(block?.headers) ? block.headers : [];
  const rows = Array.isArray(block?.rows) ? block.rows : [];
  // Accept either {footers: [...]} (preferred) or legacy single {footer: {...}}.
  // Always end up with `footerRows` as an array we can map over.
  const footerRows = (() => {
    if (Array.isArray(block?.footers) && block.footers.length) return block.footers;
    if (block?.footer && typeof block.footer === "object") return [block.footer];
    return [];
  })();

  const fontFamily = section?.fontFamily || "Arial, Helvetica, sans-serif";
  const textColor = section?.textColor || "#0f172a";
  const fontSize = section?.fontSize || 14;

  if (rows.length === 0 && headers.length === 0) {
    return (
      <div
        className="imstudio-editor__financial-table imstudio-editor__financial-table--empty"
        onClick={onSelect}
        style={{ fontFamily, color: textColor, fontSize, padding: "12px 0" }}
      >
        Ingen finansiell data tillgänglig.
      </div>
    );
  }

  return (
    <div
      className="imstudio-editor__financial-table"
      onClick={onSelect}
      style={{ fontFamily, color: textColor }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          // Auto-scale font when many columns to keep the table inside A4
          // width without horizontal scroll. 9 kolumner är fortfarande
          // läsbart vid 14px; över det skalar vi ner ett par px.
          fontSize: `${headers.length > 8 ? Math.max(11, fontSize - 2) : fontSize}px`,
        }}
      >
        {headers.length > 0 && (
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: i === 0 ? "left" : "right",
                    padding: headers.length > 8 ? "6px 6px" : "8px 12px",
                    borderBottom: "2px solid #1c1917",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  style={{
                    textAlign: ci === 0 ? "left" : "right",
                    padding: headers.length > 8 ? "6px 6px" : "8px 12px",
                    borderBottom: "1px solid #e7e5e4",
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  {cell || "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footerRows.length > 0 && headers.length > 0 && (
          <tfoot>
            {footerRows.map((f, fi) => (
              <tr key={fi}>
                <td
                  colSpan={headers.length - 1}
                  style={{
                    textAlign: "left",
                    padding: fi === 0 ? "10px 12px 6px" : "4px 12px",
                    borderTop: fi === 0 ? "2px solid #1c1917" : "none",
                    fontWeight: 600,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {f.label}
                </td>
                <td
                  style={{
                    textAlign: "right",
                    padding: fi === 0 ? "10px 12px 6px" : "4px 12px",
                    borderTop: fi === 0 ? "2px solid #1c1917" : "none",
                    fontWeight: 600,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {f.value}
                </td>
              </tr>
            ))}
          </tfoot>
        )}
      </table>
    </div>
  );
}

// Ownership & board — two-column label/value table from DB, plus a separate
// shareholders sub-table with explicit share percentages above it.
function OwnershipTableBlock({ block, section, onSelect }) {
  const rows = Array.isArray(block?.rows) ? block.rows : [];
  const shareholders = Array.isArray(block?.shareholders) ? block.shareholders : [];
  const fontFamily = section?.fontFamily || "Arial, Helvetica, sans-serif";
  const textColor = section?.textColor || "#0f172a";
  const fontSize = section?.fontSize || 14;

  if (rows.length === 0 && shareholders.length === 0) {
    return (
      <div
        className="imstudio-editor__ownership-table imstudio-editor__ownership-table--empty"
        onClick={onSelect}
        style={{ fontFamily, color: textColor, fontSize, padding: "12px 0" }}
      >
        Ingen ägar- eller styrelsedata tillgänglig.
      </div>
    );
  }

  const fmtPct = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return `${n.toFixed(1).replace(".", ",")} %`;
  };

  return (
    <div
      className="imstudio-editor__ownership-table"
      onClick={onSelect}
      style={{ fontFamily, color: textColor }}
    >
      {shareholders.length > 0 && (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: `${fontSize}px`,
            marginBottom: rows.length > 0 ? 16 : 0,
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  textAlign: "left",
                  padding: "8px 12px",
                  borderBottom: "2px solid #1c1917",
                  fontWeight: 600,
                }}
              >
                Ägare
              </th>
              <th
                style={{
                  textAlign: "right",
                  padding: "8px 12px",
                  borderBottom: "2px solid #1c1917",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                Andel
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: "8px 12px",
                  borderBottom: "2px solid #1c1917",
                  fontWeight: 600,
                }}
              >
                Roll
              </th>
            </tr>
          </thead>
          <tbody>
            {shareholders.map((s, i) => (
              <tr key={i}>
                <td
                  style={{
                    padding: "8px 12px",
                    borderBottom: "1px solid #e7e5e4",
                    verticalAlign: "top",
                  }}
                >
                  {s.name || "—"}
                </td>
                <td
                  style={{
                    padding: "8px 12px",
                    borderBottom: "1px solid #e7e5e4",
                    textAlign: "right",
                    verticalAlign: "top",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {fmtPct(s.share_percent)}
                </td>
                <td
                  style={{
                    padding: "8px 12px",
                    borderBottom: "1px solid #e7e5e4",
                    verticalAlign: "top",
                  }}
                >
                  {s.role || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {rows.length > 0 && (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: `${fontSize}px`,
          }}
        >
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <th
                  scope="row"
                  style={{
                    width: "32%",
                    textAlign: "left",
                    padding: "8px 12px",
                    borderBottom: "1px solid #e7e5e4",
                    fontWeight: 600,
                    verticalAlign: "top",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.label}
                </th>
                <td
                  style={{
                    padding: "8px 12px",
                    borderBottom: "1px solid #e7e5e4",
                    verticalAlign: "top",
                  }}
                >
                  {r.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Revenue & margin chart — inline SVG, no charting library.
// Two series: revenue (bars, left axis) and margin % (line, right axis).
function RevenueChartBlock({ block, section, onSelect }) {
  const periods = Array.isArray(block?.periods) ? block.periods : [];
  const fontFamily = section?.fontFamily || "Arial, Helvetica, sans-serif";
  const textColor = section?.textColor || "#0f172a";

  if (periods.length === 0) {
    return (
      <div
        className="imstudio-editor__revenue-chart imstudio-editor__revenue-chart--empty"
        onClick={onSelect}
        style={{ fontFamily, color: textColor, padding: "12px 0", fontSize: 14 }}
      >
        Ingen finansiell data att rita.
      </div>
    );
  }

  const W = 720;
  const H = 280;
  const PAD = { top: 16, right: 56, bottom: 36, left: 56 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const maxRev = Math.max(...periods.map((p) => p.revenue || 0)) || 1;
  // Margin axis: clamp between 0 and (maxMargin * 1.2) to give breathing room.
  const maxMargin = Math.max(
    0,
    ...periods.map((p) => (p.marginPct !== null && p.marginPct !== undefined ? p.marginPct : 0)),
  );
  const marginMax = Math.max(maxMargin * 1.2, 5);

  const barGap = 0.25;
  const barWidth = (innerW / periods.length) * (1 - barGap);
  const xCenter = (i) =>
    PAD.left + (innerW / periods.length) * (i + 0.5);

  const yRev = (v) => PAD.top + innerH - (v / maxRev) * innerH;
  const yMargin = (v) => PAD.top + innerH - (v / marginMax) * innerH;

  const linePath = periods
    .filter((p) => p.marginPct !== null && p.marginPct !== undefined)
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xCenter(i)} ${yMargin(p.marginPct)}`)
    .join(" ");

  return (
    <div
      className="imstudio-editor__revenue-chart"
      onClick={onSelect}
      style={{ fontFamily, color: textColor }}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ display: "block", maxWidth: "100%", height: "auto" }}
        role="img"
        aria-label="Omsättning och marginal per år"
      >
        {/* Y-axis grid + labels (revenue, left) */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const v = maxRev * t;
          const y = yRev(v);
          return (
            <g key={`g-${i}`}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y}
                y2={y}
                stroke="#e7e5e4"
                strokeWidth={1}
              />
              <text
                x={PAD.left - 8}
                y={y + 4}
                textAnchor="end"
                fontSize={11}
                fill="#57534e"
              >
                {(v / 1_000_000).toFixed(0)}
              </text>
            </g>
          );
        })}

        {/* Right axis labels (margin %) */}
        {[0, 0.5, 1].map((t, i) => {
          const v = marginMax * t;
          const y = yMargin(v);
          return (
            <text
              key={`r-${i}`}
              x={W - PAD.right + 8}
              y={y + 4}
              textAnchor="start"
              fontSize={11}
              fill="#57534e"
            >
              {v.toFixed(0)}%
            </text>
          );
        })}

        {/* Revenue bars */}
        {periods.map((p, i) => {
          const x = xCenter(i) - barWidth / 2;
          const y = yRev(p.revenue);
          const h = PAD.top + innerH - y;
          return (
            <rect
              key={`b-${i}`}
              x={x}
              y={y}
              width={barWidth}
              height={Math.max(0, h)}
              fill="#1c1917"
              opacity={0.85}
            />
          );
        })}

        {/* Margin line + points */}
        {linePath && (
          <path d={linePath} fill="none" stroke="#ea580c" strokeWidth={2} />
        )}
        {periods
          .filter((p) => p.marginPct !== null && p.marginPct !== undefined)
          .map((p, i) => (
            <circle
              key={`p-${i}`}
              cx={xCenter(i)}
              cy={yMargin(p.marginPct)}
              r={3.5}
              fill="#ea580c"
            />
          ))}

        {/* X-axis labels */}
        {periods.map((p, i) => (
          <text
            key={`x-${i}`}
            x={xCenter(i)}
            y={H - PAD.bottom + 16}
            textAnchor="middle"
            fontSize={11}
            fill="#57534e"
          >
            {p.label}
          </text>
        ))}

        {/* Legend */}
        <g transform={`translate(${PAD.left}, ${H - 6})`}>
          <rect x={0} y={-8} width={10} height={10} fill="#1c1917" opacity={0.85} />
          <text x={16} y={1} fontSize={11} fill="#57534e">
            Omsättning (MSEK)
          </text>
          <line x1={140} x2={156} y1={-3} y2={-3} stroke="#ea580c" strokeWidth={2} />
          <circle cx={148} cy={-3} r={3} fill="#ea580c" />
          <text x={162} y={1} fontSize={11} fill="#57534e">
            Marginal (%)
          </text>
        </g>
      </svg>
    </div>
  );
}

function SectionPreview({ section, isSelected, basePage, onSelect, onChildChange }) {
  const previewClass = isSelected
    ? "imstudio-editor__preview-block imstudio-editor__preview-block--selected"
    : "imstudio-editor__preview-block";

  if (section.type === "hero") {
    const titleChildren = section.children.filter((child) => child.kind === "title");
    const textChildren = section.children.filter((child) => child.kind === "text");
    const buttonChild = section.children.find((child) => child.kind === "button");
    const imageChild = section.children.find((child) => child.kind === "image");

    return (
      <div className={previewClass} onClick={onSelect}>
        <section
          className="imstudio-editor__hero"
          style={{
            background: section.bg || "#f8fafc",
            padding: `${section.paddingY || 32}px ${section.paddingX || 32}px`,
            textAlign: section.textAlign || "left",
            fontFamily: section.fontFamily || "Arial, Helvetica, sans-serif",
          }}
        >
          <div className="imstudio-editor__hero-content">
            {titleChildren.map((child, index) => (
              <InlineEditableText
                key={child.id}
                as="div"
                value={child.text}
                placeholder={index === 0 ? "Title" : "Sub title"}
                onSelect={onSelect}
                onChange={(value) => onChildChange(child.id, { text: value })}
                className="imstudio-editor__hero-title"
                style={{
                  color: section.textColor || "#0f172a",
                  fontFamily: section.fontFamily || "Arial, Helvetica, sans-serif",
                  fontWeight: section.fontWeight || 600,
                  fontSize: `${(section.fontSize || 16) + 14 - index * 4}px`,
                }}
              />
            ))}

            {textChildren.map((child) => (
              <InlineEditableText
                key={child.id}
                as="p"
                value={child.text}
                placeholder="Text"
                onSelect={onSelect}
                onChange={(value) => onChildChange(child.id, { text: value })}
                className="imstudio-editor__hero-text"
                style={{
                  color: section.textColor || "#0f172a",
                  fontFamily: section.fontFamily || "Arial, Helvetica, sans-serif",
                  fontWeight: section.fontWeight || 400,
                  fontSize: `${section.fontSize || 16}px`,
                }}
              />
            ))}

            {buttonChild ? (
              <InlineEditableLink
                text={buttonChild.text}
                onSelect={onSelect}
                onTextChange={(value) => onChildChange(buttonChild.id, { text: value })}
                className="imstudio-editor__dark-button"
              />
            ) : null}
          </div>

          <div className="imstudio-editor__hero-media">
            {imageChild ? (
              <img
                src={imageChild.imageUrl}
                alt="hero"
                className="imstudio-editor__hero-image"
                crossOrigin="anonymous"
              />
            ) : null}
          </div>
        </section>
      </div>
    );
  }

  if (section.type === "textSection") {
    // Compute inner page height for mid-section pagination: full A4
    // minus section padding (top + bottom) minus page-card border.
    // When the broker streams a long section, PaginatedChildren walks
    // the children, measures their heights, and inserts a visible
    // "Sidbrytning" divider at each A4 boundary — the section then
    // visibly spans multiple pages instead of growing into one tall
    // card without any visual break.
    const _secPaddingY = section.paddingY || 32;
    const _innerPageHeight = Math.max(200, PAGE_HEIGHT_PX - 2 * _secPaddingY - 2);
    return (
      <div className={previewClass} onClick={onSelect}>
        <section
          className="imstudio-editor__text-section"
          style={{
            background: section.backgroundColor || "transparent",
            padding: `${_secPaddingY}px ${section.paddingX || 32}px`,
            textAlign: section.textAlign || "left",
            fontFamily: section.fontFamily || "Arial, Helvetica, sans-serif",
          }}
        >
          <PaginatedChildren innerPageHeight={_innerPageHeight} basePage={basePage || 1}>
          {section.children.map((child) => {
            if (child.kind === "title") {
              return (
                <InlineEditableText
                  key={child.id}
                  as="h3"
                  value={child.text}
                  placeholder="Heading"
                  onSelect={onSelect}
                  onChange={(value) => onChildChange(child.id, { text: value })}
                  className="imstudio-editor__text-heading"
                  style={{
                    color: section.textColor || "#0f172a",
                    fontSize: `${(section.fontSize || 16) + 8}px`,
                    fontWeight: Math.max(section.fontWeight || 400, 600),
                    fontFamily: section.fontFamily || "Arial, Helvetica, sans-serif",
                    margin: 0,
                  }}
                />
              );
            }

            if (child.kind === "text") {
              return (
                <InlineEditableText
                  key={child.id}
                  as="p"
                  value={child.text}
                  placeholder="Body text"
                  onSelect={onSelect}
                  onChange={(value) => onChildChange(child.id, { text: value })}
                  className="imstudio-editor__text-body"
                  style={{
                    color: section.textColor || "#0f172a",
                    fontSize: `${section.fontSize || 16}px`,
                    fontWeight: section.fontWeight || 400,
                    fontFamily: section.fontFamily || "Arial, Helvetica, sans-serif",
                    // Preserve paragraph breaks (\n\n) coming from the LLM.
                    // Without this, all whitespace collapses inside <p> and a
                    // 3-paragraph body renders as one wall of text.
                    whiteSpace: "pre-wrap",
                  }}
                />
              );
            }

            if (child.kind === "financialTable") {
              return (
                <FinancialTableBlock
                  key={child.id}
                  block={child}
                  section={section}
                  onSelect={onSelect}
                />
              );
            }

            if (child.kind === "ownershipTable") {
              return (
                <OwnershipTableBlock
                  key={child.id}
                  block={child}
                  section={section}
                  onSelect={onSelect}
                />
              );
            }

            if (child.kind === "revenueChart") {
              return (
                <RevenueChartBlock
                  key={child.id}
                  block={child}
                  section={section}
                  onSelect={onSelect}
                />
              );
            }

            return null;
          })}
          </PaginatedChildren>
        </section>
      </div>
    );
  }

  if (section.type === "twoColSection") {
    const child = section.children.find((item) => item.kind === "twoColText");
    return (
      <div className={previewClass} onClick={onSelect}>
        <section
          className="imstudio-editor__two-col"
          style={{
            background: section.backgroundColor || "transparent",
            padding: `${section.paddingY || 32}px ${section.paddingX || 32}px`,
            textAlign: section.textAlign || "left",
            fontFamily: section.fontFamily || "Arial, Helvetica, sans-serif",
          }}
        >
          <div className="imstudio-editor__two-col-box">
            <InlineEditableText
              as="h3"
              value={child?.leftTitle || ""}
              placeholder="Left title"
              onSelect={onSelect}
              onChange={(value) => onChildChange(child.id, { leftTitle: value })}
              className="imstudio-editor__two-col-heading"
              style={{
                color: section.textColor || "#0f172a",
                fontSize: `${(section.fontSize || 16) + 4}px`,
                fontWeight: Math.max(section.fontWeight || 400, 600),
                fontFamily: section.fontFamily || "Arial, Helvetica, sans-serif",
                margin: 0,
              }}
            />
            <InlineEditableText
              as="p"
              value={child?.leftText || ""}
              placeholder="Left text"
              onSelect={onSelect}
              onChange={(value) => onChildChange(child.id, { leftText: value })}
              className="imstudio-editor__two-col-text"
              style={{
                color: section.textColor || "#0f172a",
                fontSize: `${section.fontSize || 16}px`,
                fontWeight: section.fontWeight || 400,
                fontFamily: section.fontFamily || "Arial, Helvetica, sans-serif",
              }}
            />
          </div>

          <div className="imstudio-editor__two-col-box">
            <InlineEditableText
              as="h3"
              value={child?.rightTitle || ""}
              placeholder="Right title"
              onSelect={onSelect}
              onChange={(value) => onChildChange(child.id, { rightTitle: value })}
              className="imstudio-editor__two-col-heading"
              style={{
                color: section.textColor || "#0f172a",
                fontSize: `${(section.fontSize || 16) + 4}px`,
                fontWeight: Math.max(section.fontWeight || 400, 600),
                fontFamily: section.fontFamily || "Arial, Helvetica, sans-serif",
                margin: 0,
              }}
            />
            <InlineEditableText
              as="p"
              value={child?.rightText || ""}
              placeholder="Right text"
              onSelect={onSelect}
              onChange={(value) => onChildChange(child.id, { rightText: value })}
              className="imstudio-editor__two-col-text"
              style={{
                color: section.textColor || "#0f172a",
                fontSize: `${section.fontSize || 16}px`,
                fontWeight: section.fontWeight || 400,
                fontFamily: section.fontFamily || "Arial, Helvetica, sans-serif",
              }}
            />
          </div>
        </section>
      </div>
    );
  }

  if (section.type === "imageSection") {
    const imageChild = section.children.find((child) => child.kind === "image");
    return (
      <div className={previewClass} onClick={onSelect}>
        <section
          className="imstudio-editor__image-section"
          style={{
            background: section.backgroundColor || "transparent",
            padding: `${section.paddingY || 32}px ${section.paddingX || 32}px`,
          }}
        >
          {imageChild ? (
            <>
              <img
                src={imageChild.imageUrl}
                alt="content"
                className="imstudio-editor__content-image"
                crossOrigin="anonymous"
                // Keep images inside one A4 — PAGE_HEIGHT_PX minus the
                // section's top/bottom padding and a buffer for the
                // caption + page-card border. object-fit lets the browser
                // letterbox portrait images instead of overflowing.
                style={{
                  maxHeight: PAGE_HEIGHT_PX - 2 * (section.paddingY || 32) - 80,
                  objectFit: "contain",
                }}
              />
              <InlineEditableText
                as="div"
                value={imageChild.caption || ""}
                placeholder="Caption"
                onSelect={onSelect}
                onChange={(value) => onChildChange(imageChild.id, { caption: value })}
                className="imstudio-editor__image-caption"
              />
            </>
          ) : null}
        </section>
      </div>
    );
  }

  if (section.type === "ctaSection") {
    const titleChildren = section.children.filter((child) => child.kind === "title");
    const textChildren = section.children.filter((child) => child.kind === "text");
    const buttonChild = section.children.find((child) => child.kind === "button");

    return (
      <div className={previewClass} onClick={onSelect}>
        <section
          className="imstudio-editor__cta-section"
          style={{
            padding: `${section.paddingY || 32}px ${section.paddingX || 32}px`,
            textAlign: section.textAlign || "left",
            fontFamily: section.fontFamily || "Arial, Helvetica, sans-serif",
          }}
        >
          <div
            className="imstudio-editor__cta-box"
            style={{
              background: section.bg || "#111827",
              color: section.textColor || "#ffffff",
            }}
          >
            {titleChildren.map((child) => (
              <InlineEditableText
                key={child.id}
                as="h3"
                value={child.text}
                placeholder="Heading"
                onSelect={onSelect}
                onChange={(value) => onChildChange(child.id, { text: value })}
                className="imstudio-editor__cta-heading"
                style={{
                  fontSize: `${(section.fontSize || 16) + 8}px`,
                  fontWeight: Math.max(section.fontWeight || 400, 600),
                  fontFamily: section.fontFamily || "Arial, Helvetica, sans-serif",
                  margin: 0,
                }}
              />
            ))}

            {textChildren.map((child) => (
              <InlineEditableText
                key={child.id}
                as="p"
                value={child.text}
                placeholder="Body"
                onSelect={onSelect}
                onChange={(value) => onChildChange(child.id, { text: value })}
                className="imstudio-editor__cta-text"
                style={{
                  color: section.textColor || "#ffffff",
                  fontSize: `${section.fontSize || 16}px`,
                  fontWeight: section.fontWeight || 400,
                  fontFamily: section.fontFamily || "Arial, Helvetica, sans-serif",
                }}
              />
            ))}

            {buttonChild ? (
              <InlineEditableLink
                text={buttonChild.text}
                onSelect={onSelect}
                onTextChange={(value) => onChildChange(buttonChild.id, { text: value })}
                className="imstudio-editor__cta-button"
              />
            ) : null}
          </div>
        </section>
      </div>
    );
  }

  if (section.type === "coverSection") {
    // Helsida-cover: stort centrerat företagsnamn + subtitle, fyller en
    // A4-sida så efterföljande sektioner börjar på sida 2. min-height
    // matchar PAGE_HEIGHT_PX (= A4 vid 794px bredd ≈ 1123px).
    const titleChild = section.children?.find((c) => c.kind === "title");
    const textChild = section.children?.find((c) => c.kind === "text");
    return (
      <div className={previewClass} onClick={onSelect}>
        <section
          style={{
            background: section.backgroundColor || "#FAFAF9",
            color: section.textColor || "#1C1917",
            minHeight: PAGE_HEIGHT_PX,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 32,
            padding: "80px 48px",
            textAlign: "center",
          }}
        >
          {titleChild && (
            <InlineEditableText
              as="h1"
              value={titleChild.text}
              placeholder="[Bolagsnamn]"
              onSelect={onSelect}
              onChange={(v) => onChildChange(titleChild.id, { text: v })}
              style={{
                margin: 0,
                fontSize: 56,
                fontWeight: 700,
                color: "inherit",
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
                maxWidth: 640,
              }}
            />
          )}
          {textChild && (
            <InlineEditableText
              as="div"
              value={textChild.text}
              placeholder="INFORMATION MEMORANDUM"
              onSelect={onSelect}
              onChange={(v) => onChildChange(textChild.id, { text: v })}
              style={{
                margin: 0,
                fontSize: 13,
                fontWeight: 600,
                color: "inherit",
                opacity: 0.7,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            />
          )}
        </section>
      </div>
    );
  }

  if (section.type === "spacerSection") {
    const spacer = section.children?.find((c) => c.kind === "spacer");
    return (
      <div className={previewClass} onClick={onSelect}>
        <div style={{ height: spacer?.height ?? 32, background: "transparent" }} />
      </div>
    );
  }

  if (section.type === "imageCardSection") {
    const card = section.children?.find((c) => c.kind === "imageCard");
    if (!card) return null;
    return (
      <div className={previewClass} onClick={onSelect}>
        <section
          style={{
            background: section.backgroundColor || "#fff",
            padding: `${section.paddingY || 24}px ${section.paddingX || 32}px`,
          }}
        >
          <div style={{
            background: "#fff",
            border: "1px solid #e7e5e4",
            borderRadius: 12,
            overflow: "hidden",
            maxWidth: 720,
            margin: "0 auto",
          }}>
            <img
              src={card.imageUrl}
              alt={card.title || "card"}
              crossOrigin="anonymous"
              style={{
                width: "100%",
                display: "block",
                height: "auto",
                maxHeight: PAGE_HEIGHT_PX - 2 * (section.paddingY || 32) - 180,
                objectFit: "contain",
              }}
            />
            <div style={{ padding: "20px 24px" }}>
              <InlineEditableText
                as="h3"
                value={card.title}
                placeholder="Titel"
                onSelect={onSelect}
                onChange={(v) => onChildChange(card.id, { title: v })}
                style={{ margin: "0 0 8px 0", fontSize: 20, fontWeight: 700, color: "#1c1917" }}
              />
              <InlineEditableText
                as="p"
                value={card.text}
                placeholder="Beskrivning"
                onSelect={onSelect}
                onChange={(v) => onChildChange(card.id, { text: v })}
                style={{ margin: "0 0 16px 0", fontSize: 14, color: "#57534e", lineHeight: 1.5 }}
              />
              {card.buttonText && (
                <InlineEditableLink
                  text={card.buttonText}
                  onSelect={onSelect}
                  onTextChange={(v) => onChildChange(card.id, { buttonText: v })}
                  className="imstudio-editor__dark-button"
                />
              )}
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (section.type === "columnsSection") {
    return (
      <div className={previewClass} onClick={onSelect}>
        <section
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${(section.cells || []).length || 2}, 1fr)`,
            gap: 16,
            padding: `${section.paddingY || 24}px ${section.paddingX || 32}px`,
            background: section.backgroundColor || "transparent",
          }}
        >
          {(section.cells || []).map((cell) => (
            <div
              key={cell.id}
              style={{ minHeight: 80, padding: 12, border: "1px dashed transparent" }}
            >
              {(cell.children || []).map((child) => {
                if (child.kind === "title") {
                  return (
                    <InlineEditableText
                      key={child.id}
                      as="h3"
                      value={child.text}
                      placeholder="Rubrik"
                      onSelect={onSelect}
                      onChange={(v) => onChildChange(child.id, { text: v })}
                      style={{ margin: "0 0 8px 0", fontSize: 18, fontWeight: 600, color: "#1c1917" }}
                    />
                  );
                }
                if (child.kind === "text") {
                  return (
                    <InlineEditableText
                      key={child.id}
                      as="p"
                      value={child.text}
                      placeholder="Text"
                      onSelect={onSelect}
                      onChange={(v) => onChildChange(child.id, { text: v })}
                      style={{
                        margin: 0,
                        fontSize: 14,
                        color: "#44403c",
                        lineHeight: 1.5,
                        whiteSpace: "pre-wrap",
                      }}
                    />
                  );
                }
                if (child.kind === "image") {
                  return (
                    <img
                      key={child.id}
                      src={child.imageUrl}
                      alt=""
                      crossOrigin="anonymous"
                      style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: PAGE_HEIGHT_PX - 2 * (section.paddingY || 32) - 80,
                        objectFit: "contain",
                        borderRadius: 8,
                      }}
                    />
                  );
                }
                if (child.kind === "financialTable") {
                  return (
                    <FinancialTableBlock
                      key={child.id}
                      block={child}
                      section={section}
                      onSelect={onSelect}
                    />
                  );
                }
                if (child.kind === "ownershipTable") {
                  return (
                    <OwnershipTableBlock
                      key={child.id}
                      block={child}
                      section={section}
                      onSelect={onSelect}
                    />
                  );
                }
                if (child.kind === "revenueChart") {
                  return (
                    <RevenueChartBlock
                      key={child.id}
                      block={child}
                      section={section}
                      onSelect={onSelect}
                    />
                  );
                }
                return null;
              })}
            </div>
          ))}
        </section>
      </div>
    );
  }

  return (
    <div className={previewClass} onClick={onSelect}>
      <div
        className="imstudio-editor__divider-wrap"
        style={{
          background: section.backgroundColor || "transparent",
          padding: `${section.paddingY || 24}px ${section.paddingX || 32}px`,
        }}
      >
        <hr className="imstudio-editor__divider-line" />
      </div>
    </div>
  );
}
